import {
  APPROVAL_CHANNELS,
  APPROVAL_REQUIRES_APPROVER_MESSAGE,
  CARSARRIVE_DEFAULT_INVOICE_MAPPING,
  CARSARRIVE_INVOICE_BUCKETS,
  CARSARRIVE_SUBSCRIPTION,
  KNOWN_SCHEDULER_VALUES,
  SUPERDISPATCH_SUBSCRIPTION,
  TRANSITION_MODES,
  definition,
  schema,
  transitionPolicySchema
} from "../chunk-B45MFSSL.mjs";

// src/renderers/RendererRegistry.ts
var customRenderers = /* @__PURE__ */ new Map();
function registerCustomRenderer(key, renderer, mode = "full", layout, requires) {
  customRenderers.set(key, { renderer, mode, layout, requires });
}
function getCustomRenderer(key) {
  return customRenderers.get(key)?.renderer ?? null;
}
function hasCustomRenderer(key) {
  return customRenderers.has(key);
}
function getRendererMode(key) {
  return customRenderers.get(key)?.mode ?? null;
}
function isFullRenderer(key) {
  return customRenderers.get(key)?.mode === "full";
}
function isInputRenderer(key) {
  return customRenderers.get(key)?.mode === "input";
}
function getRendererLayout(key) {
  return customRenderers.get(key)?.layout ?? null;
}
function getRendererRequires(key) {
  return customRenderers.get(key)?.requires;
}

// src/renderers/SubFieldsRegistry.ts
var subFieldsMap = /* @__PURE__ */ new Map();
function registerSubFields(settingKey, labels) {
  subFieldsMap.set(settingKey, labels);
}
function getSubFields(settingKey) {
  return subFieldsMap.get(settingKey) ?? null;
}
function hasSubFields(settingKey) {
  return subFieldsMap.has(settingKey);
}

// src/definitions/reporting.renderer.tsx
import { useState, useEffect, useCallback } from "react";
import { Box, Collapse, Divider, FormControlLabel, IconButton, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import { SubOption, StyledSwitch } from "@hopdrive/sdk-ui";
import { jsx, jsxs } from "react/jsx-runtime";
var TAB_DEFS = [
  {
    key: "operations",
    label: "Operations Overview",
    groups: [
      {
        key: "metrics",
        label: "Metric Cards",
        modules: [
          { key: "total_moves", label: "Total Moves" },
          { key: "completed", label: "Completed" },
          { key: "pending", label: "Pending" },
          { key: "canceled", label: "Canceled" },
          { key: "on_time_pct", label: "On-Time Percentage" },
          { key: "avg_distance", label: "Average Distance" },
          { key: "active_drivers", label: "Active Drivers" },
          { key: "moves_per_driver", label: "Moves Per Driver" },
          { key: "repeat_customers", label: "Repeat Consumers" },
          { key: "missing_ref", label: "Missing Reference Numbers" },
          { key: "rideshares_utilized", label: "Rideshares Utilized" }
        ]
      },
      {
        key: "charts",
        label: "Charts",
        modules: [
          { key: "move_volume", label: "Move Volume Over Time" },
          { key: "status_distribution", label: "Status Distribution" },
          { key: "moves_by_weekday", label: "Moves by Weekday" },
          { key: "moves_by_hour", label: "Moves by Hour" },
          { key: "driver_activity", label: "Driver Activity" },
          { key: "distance_distribution", label: "Distance Distribution" }
        ]
      }
    ]
  },
  {
    key: "appointments",
    label: "Appointment Usage",
    groups: [
      {
        key: "metrics",
        label: "Metric Cards",
        modules: [
          { key: "est_utilization", label: "Estimated Utilization" },
          { key: "upcoming_appts", label: "Upcoming Appointments" },
          { key: "completed_appts", label: "Completed Appointments" },
          { key: "canceled_appts", label: "Canceled Appointments" },
          { key: "configured_drivers", label: "Configured Drivers" },
          { key: "avg_moves_per_driver", label: "Avg Moves Per Driver" },
          { key: "unused_slots", label: "Unused Estimated Slots" },
          { key: "avg_move_duration", label: "Avg Move Duration" }
        ]
      },
      {
        key: "charts",
        label: "Charts",
        modules: [
          { key: "daily_utilization", label: "Daily Estimated Utilization" },
          { key: "booked_vs_capacity", label: "Booked vs Estimated Capacity" },
          { key: "appt_volume_by_hour", label: "Appointment Volume by Hour" },
          { key: "driver_activity", label: "Driver Activity" }
        ]
      }
    ]
  },
  {
    key: "executive",
    label: "Executive KPIs",
    groups: [
      {
        key: "kpis",
        label: "KPI Gauges",
        modules: [
          { key: "trips_assigned", label: "Trips Assigned" },
          { key: "trips_completed", label: "Trips Completed" },
          { key: "trips_validated", label: "Trips Validated" }
        ]
      },
      {
        key: "charts",
        label: "Charts",
        modules: [
          { key: "kpi_trend", label: "KPI Trend Over Time" },
          { key: "validation_breakdown", label: "Validation Breakdown" }
        ]
      }
    ]
  }
];
function normalize(value) {
  const v = value || {};
  const tabs = {};
  for (const tab of TAB_DEFS) {
    const tv = v?.tabs?.[tab.key] || {};
    const nt = { enabled: tv.enabled !== false };
    for (const group of tab.groups) {
      const gv = tv[group.key] || {};
      const ng = {};
      for (const m of group.modules) ng[m.key] = gv[m.key] !== false;
      nt[group.key] = ng;
    }
    tabs[tab.key] = nt;
  }
  return { enabled: v.enabled === true, tabs };
}
function ReportingRenderer({ value, onChange, disabled }) {
  const [working, setWorking] = useState(() => normalize(value));
  const [expanded, setExpanded] = useState({ operations: true });
  useEffect(() => {
    setWorking(normalize(value));
  }, [value]);
  const commit = useCallback(
    (next) => {
      setWorking(next);
      onChange(next);
    },
    [onChange]
  );
  const setMaster = (on) => commit({ ...working, enabled: on });
  const setTabEnabled = (tabKey, on) => commit({
    ...working,
    tabs: { ...working.tabs, [tabKey]: { ...working.tabs[tabKey], enabled: on } }
  });
  const setModule = (tabKey, groupKey, moduleKey, on) => {
    const tab = working.tabs[tabKey];
    const group = { ...tab[groupKey], [moduleKey]: on };
    commit({
      ...working,
      tabs: { ...working.tabs, [tabKey]: { ...tab, [groupKey]: group } }
    });
  };
  const masterOff = !working.enabled;
  return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx(
      SubOption,
      {
        title: "Reporting Dashboard Enabled",
        description: "Master switch for the customer-facing reporting dashboard. When off, the dashboard is hidden entirely.",
        inline: true,
        children: /* @__PURE__ */ jsx(
          StyledSwitch,
          {
            checked: working.enabled,
            onChange: (e) => setMaster(e.target.checked),
            disabled
          }
        )
      }
    ),
    TAB_DEFS.map((tab) => {
      const tabState = working.tabs[tab.key];
      const tabOff = masterOff || !tabState.enabled;
      const isOpen = expanded[tab.key] ?? false;
      return /* @__PURE__ */ jsxs(
        Box,
        {
          sx: {
            border: (theme) => `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "8px",
            opacity: masterOff ? 0.6 : 1
          },
          children: [
            /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 2, p: 2 }, children: [
              /* @__PURE__ */ jsx(
                IconButton,
                {
                  size: "small",
                  onClick: () => setExpanded((s) => ({ ...s, [tab.key]: !isOpen })),
                  "aria-label": isOpen ? "Collapse" : "Expand",
                  children: isOpen ? /* @__PURE__ */ jsx(ExpandLessIcon, {}) : /* @__PURE__ */ jsx(ExpandMoreIcon, {})
                }
              ),
              /* @__PURE__ */ jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [
                /* @__PURE__ */ jsx(Typography, { fontSize: 14, fontWeight: 600, children: tab.label }),
                /* @__PURE__ */ jsx(Typography, { fontSize: 13, color: "text.secondary", children: "Show this tab and choose which modules appear on it." })
              ] }),
              /* @__PURE__ */ jsx(
                StyledSwitch,
                {
                  checked: tabState.enabled,
                  onChange: (e) => setTabEnabled(tab.key, e.target.checked),
                  disabled: disabled || masterOff
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(Collapse, { in: isOpen, unmountOnExit: true, children: [
              /* @__PURE__ */ jsx(Divider, {}),
              /* @__PURE__ */ jsx(Box, { sx: { p: 2, display: "flex", flexDirection: "column", gap: 2 }, children: tab.groups.map((group) => {
                const groupState = tabState[group.key];
                return /* @__PURE__ */ jsxs(Box, { children: [
                  /* @__PURE__ */ jsx(Typography, { fontSize: 13, fontWeight: 600, color: "text.secondary", sx: { mb: 1 }, children: group.label }),
                  /* @__PURE__ */ jsx(
                    Box,
                    {
                      sx: {
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        columnGap: 2
                      },
                      children: group.modules.map((m) => /* @__PURE__ */ jsx(
                        FormControlLabel,
                        {
                          sx: { m: 0, justifyContent: "space-between", ml: 0 },
                          labelPlacement: "start",
                          control: /* @__PURE__ */ jsx(
                            StyledSwitch,
                            {
                              checked: groupState[m.key],
                              onChange: (e) => setModule(tab.key, group.key, m.key, e.target.checked),
                              disabled: disabled || tabOff
                            }
                          ),
                          label: /* @__PURE__ */ jsx(Typography, { fontSize: 13, sx: { flex: 1 }, children: m.label })
                        },
                        m.key
                      ))
                    }
                  )
                ] }, group.key);
              }) })
            ] })
          ]
        },
        tab.key
      );
    })
  ] });
}

// src/definitions/dry-run-behavior.renderer.tsx
import { useState as useState2, useEffect as useEffect2 } from "react";
import { TextField, MenuItem, ListItemText } from "@mui/material";
import { jsx as jsx2 } from "react/jsx-runtime";
var OPTION_DESCRIPTIONS = {
  "default-off": "Allow dry run, but default to non-dry run in move outcome.",
  "default-on": "Allow dry run and default to dry run in move outcome.",
  "forced-off": "Do not allow activation of dry run in move outcome.",
  "forced-on": "Always force the outcome to use the dry run fee."
};
var DRY_RUN_OPTIONS = definition.schema.options.map((value) => ({
  value,
  label: value.split("-").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
  description: OPTION_DESCRIPTIONS[value] || ""
}));
function DryRunBehaviorRenderer({ value, onChange, disabled }) {
  const [selectedValue, setSelectedValue] = useState2(value || "");
  useEffect2(() => {
    setSelectedValue(value || "");
  }, [value]);
  const handleChange = (e) => {
    const newValue = e.target.value;
    setSelectedValue(newValue);
    onChange(newValue || null);
  };
  return /* @__PURE__ */ jsx2(
    TextField,
    {
      select: true,
      label: "Select Behavior",
      value: selectedValue,
      onChange: handleChange,
      variant: "outlined",
      size: "small",
      disabled,
      sx: { width: 300 },
      children: DRY_RUN_OPTIONS.map((option) => /* @__PURE__ */ jsx2(MenuItem, { value: option.value, children: /* @__PURE__ */ jsx2(
        ListItemText,
        {
          primary: option.label,
          secondary: option.description,
          secondaryTypographyProps: { variant: "caption" }
        }
      ) }, option.value))
    }
  );
}

// src/definitions/allowed-move-types.renderer.tsx
import { useState as useState3, useEffect as useEffect3, useCallback as useCallback2 } from "react";
import {
  Box as Box2,
  Typography as Typography2,
  FormGroup,
  FormControlLabel as FormControlLabel2,
  Checkbox,
  Button,
  Stack
} from "@mui/material";
import { SelectAll as SelectAllIcon, Deselect as DeselectIcon } from "@mui/icons-material";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var MOVE_TYPE_OPTIONS = [
  {
    value: "concierge",
    label: "Concierge",
    description: "Standard vehicle transport with driver pickup and delivery"
  },
  {
    value: "concierge-loaner",
    label: "Concierge Loaner",
    description: "Vehicle transport with a loaner vehicle provided to the customer"
  },
  {
    value: "one-way",
    label: "One-Way",
    description: "Single direction transport without return trip"
  },
  {
    value: "round-trip",
    label: "Round-Trip",
    description: "Transport with driver returning to origin location"
  }
];
function AllowedMoveTypesRenderer({ value, onChange, disabled }) {
  const allowedTypes = value || [];
  const [selectedTypes, setSelectedTypes] = useState3(allowedTypes);
  useEffect3(() => {
    setSelectedTypes(value || []);
  }, [value]);
  const updateSelectedTypes = useCallback2(
    (newTypes) => {
      setSelectedTypes(newTypes);
      onChange(newTypes);
    },
    [onChange]
  );
  const handleToggle = (moveType) => {
    const newTypes = selectedTypes.includes(moveType) ? selectedTypes.filter((t) => t !== moveType) : [...selectedTypes, moveType];
    updateSelectedTypes(newTypes);
  };
  const handleSelectAll = () => {
    updateSelectedTypes(MOVE_TYPE_OPTIONS.map((opt) => opt.value));
  };
  const handleDeselectAll = () => {
    updateSelectedTypes([]);
  };
  const allSelected = selectedTypes.length === MOVE_TYPE_OPTIONS.length;
  const noneSelected = selectedTypes.length === 0;
  return /* @__PURE__ */ jsxs2("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsxs2(Stack, { direction: "row", spacing: 1, children: [
      /* @__PURE__ */ jsx3(
        Button,
        {
          size: "small",
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx3(SelectAllIcon, {}),
          onClick: handleSelectAll,
          disabled: disabled || allSelected,
          children: "Select All"
        }
      ),
      /* @__PURE__ */ jsx3(
        Button,
        {
          size: "small",
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx3(DeselectIcon, {}),
          onClick: handleDeselectAll,
          disabled: disabled || noneSelected,
          children: "Deselect All"
        }
      )
    ] }),
    /* @__PURE__ */ jsx3(FormGroup, { children: MOVE_TYPE_OPTIONS.map((option) => /* @__PURE__ */ jsx3(
      Box2,
      {
        sx: {
          py: 1.5,
          px: 2,
          mb: 1,
          borderRadius: 1,
          border: "1px solid",
          borderColor: selectedTypes.includes(option.value) ? "primary.main" : "divider",
          backgroundColor: selectedTypes.includes(option.value) ? "action.selected" : "transparent",
          transition: "all 0.15s ease-in-out",
          "&:hover": {
            borderColor: disabled ? "divider" : "primary.light"
          }
        },
        children: /* @__PURE__ */ jsx3(
          FormControlLabel2,
          {
            control: /* @__PURE__ */ jsx3(
              Checkbox,
              {
                checked: selectedTypes.includes(option.value),
                onChange: () => handleToggle(option.value),
                disabled,
                color: "primary"
              }
            ),
            label: /* @__PURE__ */ jsxs2(Box2, { children: [
              /* @__PURE__ */ jsx3(Typography2, { variant: "subtitle2", fontWeight: 600, children: option.label }),
              /* @__PURE__ */ jsx3(Typography2, { variant: "body2", color: "text.secondary", children: option.description })
            ] }),
            sx: { m: 0, width: "100%", alignItems: "flex-start" }
          }
        )
      },
      option.value
    )) }),
    /* @__PURE__ */ jsxs2(Typography2, { variant: "body2", color: "text.secondary", children: [
      selectedTypes.length,
      " of ",
      MOVE_TYPE_OPTIONS.length,
      " move types selected"
    ] })
  ] });
}

// src/definitions/rc_config.renderer.tsx
import { useState as useState4, useEffect as useEffect4, useCallback as useCallback3 } from "react";
import {
  Box as Box3,
  TextField as TextField2,
  Typography as Typography3,
  Divider as Divider2,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon2,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Key as KeyIcon,
  Code as CodeIcon
} from "@mui/icons-material";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function RcConfigRenderer({ value, onChange, disabled }) {
  const rcConfig = value || {};
  const [advisorEmail, setAdvisorEmail] = useState4(rcConfig.advisor_email ?? "");
  const [advisorName, setAdvisorName] = useState4(rcConfig.advisor_name ?? "");
  const [advisorPhone, setAdvisorPhone] = useState4(rcConfig.advisor_phone ?? "");
  const [advisorOtpSecret, setAdvisorOtpSecret] = useState4(rcConfig.advisor_otp_secret ?? "");
  const [driverEmail, setDriverEmail] = useState4(rcConfig.driver_email ?? "");
  const [driverName, setDriverName] = useState4(rcConfig.driver_name ?? "");
  const [driverPhone, setDriverPhone] = useState4(rcConfig.driver_phone ?? "");
  const [rcDealerAdminConfigId, setRcDealerAdminConfigId] = useState4(
    rcConfig.rc_dealer_admin_config_id ?? 0
  );
  const [emailScrapeConfigJson, setEmailScrapeConfigJson] = useState4(
    JSON.stringify(rcConfig.email_scrape_config ?? {}, null, 2)
  );
  const [emailScrapeError, setEmailScrapeError] = useState4(null);
  useEffect4(() => {
    const next = value || {};
    setAdvisorEmail(next.advisor_email ?? "");
    setAdvisorName(next.advisor_name ?? "");
    setAdvisorPhone(next.advisor_phone ?? "");
    setAdvisorOtpSecret(next.advisor_otp_secret ?? "");
    setDriverEmail(next.driver_email ?? "");
    setDriverName(next.driver_name ?? "");
    setDriverPhone(next.driver_phone ?? "");
    setRcDealerAdminConfigId(next.rc_dealer_admin_config_id ?? 0);
    setEmailScrapeConfigJson(JSON.stringify(next.email_scrape_config ?? {}, null, 2));
    setEmailScrapeError(null);
  }, [value]);
  const updateField = useCallback3(
    (field, newValue) => {
      const currentValue = value || {};
      onChange({
        ...currentValue,
        [field]: newValue || void 0
      });
    },
    [onChange, value]
  );
  const createBlurHandler = (field, localValue, savedValue) => {
    return () => {
      if (localValue !== (savedValue ?? (typeof localValue === "number" ? 0 : ""))) {
        updateField(field, localValue || void 0);
      }
    };
  };
  const handleEmailScrapeConfigChange = (e) => {
    const newValue = e.target.value;
    setEmailScrapeConfigJson(newValue);
    try {
      JSON.parse(newValue);
      setEmailScrapeError(null);
    } catch {
      setEmailScrapeError("Invalid JSON format");
    }
  };
  const handleEmailScrapeConfigBlur = () => {
    try {
      const parsed = JSON.parse(emailScrapeConfigJson);
      const currentConfig = value?.email_scrape_config ?? {};
      if (JSON.stringify(parsed) !== JSON.stringify(currentConfig)) {
        updateField("email_scrape_config", parsed);
      }
      setEmailScrapeError(null);
    } catch {
      setEmailScrapeError("Invalid JSON format - changes not saved");
    }
  };
  return /* @__PURE__ */ jsxs3("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, fontWeight: 500, children: "Advisor Configuration" }),
    /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, color: "text.secondary", sx: { mt: -2 }, children: "Configure advisor credentials for automation scenarios." }),
    /* @__PURE__ */ jsxs3(Box3, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Advisor Name",
          value: advisorName,
          onChange: (e) => setAdvisorName(e.target.value),
          onBlur: createBlurHandler("advisor_name", advisorName, rcConfig.advisor_name),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(PersonIcon, { fontSize: "small", color: "disabled" }) })
          }
        }
      ),
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Advisor Email",
          type: "email",
          value: advisorEmail,
          onChange: (e) => setAdvisorEmail(e.target.value),
          onBlur: createBlurHandler("advisor_email", advisorEmail, rcConfig.advisor_email),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(EmailIcon, { fontSize: "small", color: "disabled" }) })
          }
        }
      ),
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Advisor Phone",
          value: advisorPhone,
          onChange: (e) => setAdvisorPhone(e.target.value),
          onBlur: createBlurHandler("advisor_phone", advisorPhone, rcConfig.advisor_phone),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(PhoneIcon, { fontSize: "small", color: "disabled" }) })
          }
        }
      ),
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Advisor OTP Secret",
          type: "password",
          value: advisorOtpSecret,
          onChange: (e) => setAdvisorOtpSecret(e.target.value),
          onBlur: createBlurHandler("advisor_otp_secret", advisorOtpSecret, rcConfig.advisor_otp_secret),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(KeyIcon, { fontSize: "small", color: "disabled" }) })
          },
          helperText: "OTP secret for advisor authentication in automation"
        }
      )
    ] }),
    /* @__PURE__ */ jsx4(Divider2, { sx: { my: 1 } }),
    /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, fontWeight: 500, children: "Driver Configuration" }),
    /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, color: "text.secondary", sx: { mt: -2 }, children: "Configure driver credentials for automation scenarios." }),
    /* @__PURE__ */ jsxs3(Box3, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Driver Name",
          value: driverName,
          onChange: (e) => setDriverName(e.target.value),
          onBlur: createBlurHandler("driver_name", driverName, rcConfig.driver_name),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(PersonIcon, { fontSize: "small", color: "disabled" }) })
          }
        }
      ),
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Driver Email",
          type: "email",
          value: driverEmail,
          onChange: (e) => setDriverEmail(e.target.value),
          onBlur: createBlurHandler("driver_email", driverEmail, rcConfig.driver_email),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(EmailIcon, { fontSize: "small", color: "disabled" }) })
          }
        }
      ),
      /* @__PURE__ */ jsx4(
        TextField2,
        {
          label: "Driver Phone",
          value: driverPhone,
          onChange: (e) => setDriverPhone(e.target.value),
          onBlur: createBlurHandler("driver_phone", driverPhone, rcConfig.driver_phone),
          disabled,
          size: "small",
          fullWidth: true,
          sx: { maxWidth: 400 },
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx4(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx4(PhoneIcon, { fontSize: "small", color: "disabled" }) })
          }
        }
      )
    ] }),
    /* @__PURE__ */ jsx4(Divider2, { sx: { my: 1 } }),
    /* @__PURE__ */ jsxs3(Accordion, { children: [
      /* @__PURE__ */ jsx4(AccordionSummary, { expandIcon: /* @__PURE__ */ jsx4(ExpandMoreIcon2, {}), children: /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, fontWeight: 500, children: "Advanced Configuration" }) }),
      /* @__PURE__ */ jsx4(AccordionDetails, { children: /* @__PURE__ */ jsxs3(Box3, { sx: { display: "flex", flexDirection: "column", gap: 3 }, children: [
        /* @__PURE__ */ jsx4(
          TextField2,
          {
            label: "RC Dealer Admin Config ID",
            type: "number",
            value: rcDealerAdminConfigId,
            onChange: (e) => setRcDealerAdminConfigId(parseInt(e.target.value, 10) || 0),
            onBlur: createBlurHandler(
              "rc_dealer_admin_config_id",
              rcDealerAdminConfigId,
              rcConfig.rc_dealer_admin_config_id
            ),
            disabled,
            size: "small",
            fullWidth: true,
            sx: { maxWidth: 200 },
            helperText: "Reference to RC dealer admin configuration record"
          }
        ),
        /* @__PURE__ */ jsxs3(Box3, { children: [
          /* @__PURE__ */ jsxs3(Box3, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 1 }, children: [
            /* @__PURE__ */ jsx4(CodeIcon, { fontSize: "small", color: "action" }),
            /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, fontWeight: 500, children: "Email Scrape Configuration" })
          ] }),
          /* @__PURE__ */ jsx4(Typography3, { fontSize: 14, color: "text.secondary", sx: { mb: 1.5 }, children: "Email scraping config for extracting temporary passwords in automation. Enter valid JSON." }),
          /* @__PURE__ */ jsx4(
            TextField2,
            {
              fullWidth: true,
              multiline: true,
              rows: 8,
              label: "Email Scrape Config (JSON)",
              placeholder: "{}",
              size: "small",
              variant: "outlined",
              value: emailScrapeConfigJson,
              onChange: handleEmailScrapeConfigChange,
              onBlur: handleEmailScrapeConfigBlur,
              disabled,
              error: !!emailScrapeError,
              helperText: emailScrapeError,
              InputProps: {
                sx: { fontFamily: "monospace", fontSize: "0.875rem" }
              }
            }
          )
        ] })
      ] }) })
    ] })
  ] });
}

// src/definitions/dms.renderer.tsx
import { useState as useState5, useEffect as useEffect5, useCallback as useCallback4 } from "react";
import {
  Box as Box4,
  TextField as TextField3,
  MenuItem as MenuItem2,
  Typography as Typography4,
  Accordion as Accordion2,
  AccordionSummary as AccordionSummary2,
  AccordionDetails as AccordionDetails2
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon3, Code as CodeIcon2 } from "@mui/icons-material";
import { SubOption as SubOption2 } from "@hopdrive/sdk-ui";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var DMS_SERVICE_OPTIONS = [
  { value: "", label: "None" },
  { value: "CDK", label: "CDK Global" },
  { value: "Reynolds", label: "Reynolds and Reynolds" },
  { value: "DealerTrack", label: "DealerTrack" },
  { value: "Dealerware", label: "Dealerware" },
  { value: "DealerSocket", label: "DealerSocket" },
  { value: "AutoMate", label: "AutoMate" },
  { value: "custom", label: "Custom (specify in props)" }
];
function DmsRenderer({ value, onChange, disabled }) {
  const dmsValue = value || {};
  const [service, setService] = useState5(dmsValue.service ?? "");
  const [propsJson, setPropsJson] = useState5(JSON.stringify(dmsValue.props ?? {}, null, 2));
  const [propsError, setPropsError] = useState5(null);
  useEffect5(() => {
    const next = value || {};
    setService(next.service ?? "");
    setPropsJson(JSON.stringify(next.props ?? {}, null, 2));
    setPropsError(null);
  }, [value]);
  const updateDms = useCallback4(
    (updates) => {
      const currentValue = value || {};
      onChange({
        service,
        props: currentValue.props ?? {},
        ...updates
      });
    },
    [service, onChange, value]
  );
  const handleServiceChange = (e) => {
    const newValue = e.target.value;
    setService(newValue);
    updateDms({ service: newValue });
  };
  const handlePropsChange = (e) => {
    const newValue = e.target.value;
    setPropsJson(newValue);
    try {
      JSON.parse(newValue);
      setPropsError(null);
    } catch {
      setPropsError("Invalid JSON format");
    }
  };
  const handlePropsBlur = () => {
    try {
      const parsedProps = JSON.parse(propsJson);
      const currentProps = value?.props ?? {};
      if (JSON.stringify(parsedProps) !== JSON.stringify(currentProps)) {
        updateDms({ props: parsedProps });
      }
      setPropsError(null);
    } catch {
      setPropsError("Invalid JSON format - changes not saved");
    }
  };
  return /* @__PURE__ */ jsxs4("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx5(
      SubOption2,
      {
        title: "DMS Service Provider",
        description: "Select the Dealer Management System provider for this customer. This enables automatic data synchronization with the dealership's DMS.",
        inline: true,
        children: /* @__PURE__ */ jsx5(
          TextField3,
          {
            select: true,
            label: "Service Provider",
            size: "small",
            variant: "outlined",
            value: service,
            onChange: handleServiceChange,
            disabled,
            sx: { width: 300 },
            children: DMS_SERVICE_OPTIONS.map((option) => /* @__PURE__ */ jsx5(MenuItem2, { value: option.value, children: option.label }, option.value))
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs4(Accordion2, { defaultExpanded: Object.keys(dmsValue.props ?? {}).length > 0, children: [
      /* @__PURE__ */ jsx5(AccordionSummary2, { expandIcon: /* @__PURE__ */ jsx5(ExpandMoreIcon3, {}), children: /* @__PURE__ */ jsxs4(Box4, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
        /* @__PURE__ */ jsx5(CodeIcon2, { fontSize: "small", color: "action" }),
        /* @__PURE__ */ jsx5(Typography4, { variant: "subtitle2", fontWeight: 600, children: "Additional Properties" })
      ] }) }),
      /* @__PURE__ */ jsxs4(AccordionDetails2, { children: [
        /* @__PURE__ */ jsx5(Typography4, { fontSize: 14, color: "text.secondary", sx: { mb: 1.5 }, children: "DMS-specific configuration properties. Structure varies by provider. Enter valid JSON." }),
        /* @__PURE__ */ jsx5(
          TextField3,
          {
            fullWidth: true,
            multiline: true,
            rows: 8,
            label: "Properties (JSON)",
            placeholder: "{}",
            size: "small",
            variant: "outlined",
            value: propsJson,
            onChange: handlePropsChange,
            onBlur: handlePropsBlur,
            disabled,
            error: !!propsError,
            helperText: propsError,
            InputProps: {
              sx: { fontFamily: "monospace", fontSize: "0.875rem" }
            }
          }
        )
      ] })
    ] })
  ] });
}

// src/definitions/private.renderer.tsx
import { Alert, Typography as Typography5 } from "@mui/material";
import { KeyValueEditor } from "@hopdrive/sdk-ui";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
function PrivateRenderer({ value, onChange, disabled }) {
  const privateValues = value || {};
  return /* @__PURE__ */ jsxs5("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx6(Alert, { severity: "info", children: /* @__PURE__ */ jsxs5(Typography5, { variant: "body2", children: [
      /* @__PURE__ */ jsx6("strong", { children: "Internal Configuration:" }),
      " This section contains private/internal configuration values. Use with caution - these settings may affect internal system behavior and data sharing."
    ] }) }),
    /* @__PURE__ */ jsx6(
      KeyValueEditor,
      {
        value: privateValues,
        onChange,
        disabled,
        knownFields: [],
        allowCustomFields: true,
        placeholder: "Enter value..."
      }
    )
  ] });
}

// src/definitions/customer-values.renderer.tsx
import { useMemo } from "react";
import { KeyValueEditor as KeyValueEditor2 } from "@hopdrive/sdk-ui";
import { jsx as jsx7 } from "react/jsx-runtime";
var ACRONYMS = /* @__PURE__ */ new Set(["GMA", "ID", "DMS", "AR", "RC", "API", "URL"]);
function toLabel(snake) {
  return snake.split("_").map((part) => {
    const upper = part.toUpperCase();
    if (ACRONYMS.has(upper)) return upper;
    return part.charAt(0).toUpperCase() + part.slice(1);
  }).join(" ");
}
function unwrapDescription(field) {
  let current = field;
  while (current?._def) {
    if (current._def.description) return current._def.description;
    if (!current._def.innerType) break;
    current = current._def.innerType;
  }
  return "";
}
function buildKnownFields() {
  const shape = schema.shape;
  if (!shape) return [];
  return Object.entries(shape).map(([key, field]) => ({
    key,
    label: toLabel(key),
    description: unwrapDescription(field)
  }));
}
function CustomerValuesRenderer({ value, onChange, disabled }) {
  const customerValues = value || {};
  const knownFields = useMemo(buildKnownFields, []);
  return /* @__PURE__ */ jsx7(
    KeyValueEditor2,
    {
      value: customerValues,
      onChange,
      disabled,
      knownFields,
      allowCustomFields: true,
      placeholder: "Enter value..."
    }
  );
}

// src/definitions/fuel.renderer.tsx
import { useState as useState6, useEffect as useEffect6, useCallback as useCallback5 } from "react";
import { TextField as TextField4, InputAdornment as InputAdornment2 } from "@mui/material";
import { AttachMoney as AttachMoneyIcon, Percent as PercentIcon } from "@mui/icons-material";
import { SubOption as SubOption3 } from "@hopdrive/sdk-ui";
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
function FuelRenderer({ value, onChange, disabled }) {
  const fuelValue = value || {};
  const initialMinPrice = fuelValue.min_fuel_price ?? fuelValue.min_fuel_spend ?? 0;
  const initialMaxPrice = fuelValue.max_fuel_price ?? fuelValue.max_fuel_spend ?? 0;
  const initialFuelLevel = fuelValue.fuel_level ?? 0;
  const [minFuelPrice, setMinFuelPrice] = useState6(initialMinPrice);
  const [maxFuelPrice, setMaxFuelPrice] = useState6(initialMaxPrice);
  const [fuelLevelDecimal, setFuelLevelDecimal] = useState6(initialFuelLevel);
  const [fuelLevelPercent, setFuelLevelPercent] = useState6(initialFuelLevel * 100);
  useEffect6(() => {
    const fuelValue2 = value || {};
    const minPrice = fuelValue2.min_fuel_price ?? fuelValue2.min_fuel_spend ?? 0;
    const maxPrice = fuelValue2.max_fuel_price ?? fuelValue2.max_fuel_spend ?? 0;
    const fuelLevel = fuelValue2.fuel_level ?? 0;
    setMinFuelPrice(minPrice);
    setMaxFuelPrice(maxPrice);
    setFuelLevelDecimal(fuelLevel);
    setFuelLevelPercent(fuelLevel * 100);
  }, [value]);
  const updateFuel = useCallback5(
    (updates) => {
      onChange({
        fuel_level: fuelLevelDecimal,
        min_fuel_price: minFuelPrice,
        max_fuel_price: maxFuelPrice,
        ...updates
      });
    },
    [fuelLevelDecimal, minFuelPrice, maxFuelPrice, onChange]
  );
  const handleMinFuelPriceChange = (e) => {
    const newValue = Number(e.target.value);
    setMinFuelPrice(newValue);
  };
  const handleMinFuelPriceBlur = () => {
    const savedValue = fuelValue.min_fuel_price ?? fuelValue.min_fuel_spend ?? 0;
    if (minFuelPrice !== savedValue) {
      updateFuel({ min_fuel_price: minFuelPrice });
    }
  };
  const handleMaxFuelPriceChange = (e) => {
    const newValue = Number(e.target.value);
    setMaxFuelPrice(newValue);
  };
  const handleMaxFuelPriceBlur = () => {
    const savedValue = fuelValue.max_fuel_price ?? fuelValue.max_fuel_spend ?? 0;
    if (maxFuelPrice !== savedValue) {
      updateFuel({ max_fuel_price: maxFuelPrice });
    }
  };
  const handleFuelLevelChange = (e) => {
    const percent = Number(e.target.value);
    const decimal = percent / 100;
    setFuelLevelPercent(percent);
    setFuelLevelDecimal(decimal);
  };
  const handleFuelLevelBlur = () => {
    if (fuelLevelDecimal !== (fuelValue.fuel_level ?? 0)) {
      updateFuel({ fuel_level: fuelLevelDecimal });
    }
  };
  return /* @__PURE__ */ jsxs6("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx8(
      SubOption3,
      {
        title: "Minimum Fuel Spend",
        description: "Minimum amount of fuel to purchase (in dollars). Fuel amounts below this value will not be authorized by our system.",
        inline: true,
        children: /* @__PURE__ */ jsx8(
          TextField4,
          {
            fullWidth: true,
            type: "number",
            label: "Min Fuel Spend",
            placeholder: "Enter a minimum fuel amount...",
            size: "small",
            variant: "outlined",
            value: minFuelPrice,
            onChange: handleMinFuelPriceChange,
            onBlur: handleMinFuelPriceBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx8(InputAdornment2, { position: "start", children: /* @__PURE__ */ jsx8(AttachMoneyIcon, { fontSize: "small", color: "disabled" }) })
            },
            inputProps: {
              step: 0.1,
              min: 0,
              max: 100
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx8(
      SubOption3,
      {
        title: "Maximum Fuel Spend",
        description: "The maximum amount of fuel to pump when refueling is required (in dollars).",
        inline: true,
        children: /* @__PURE__ */ jsx8(
          TextField4,
          {
            fullWidth: true,
            type: "number",
            label: "Max Fuel Spend",
            placeholder: "Enter a maximum fuel amount...",
            size: "small",
            variant: "outlined",
            value: maxFuelPrice,
            onChange: handleMaxFuelPriceChange,
            onBlur: handleMaxFuelPriceBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx8(InputAdornment2, { position: "start", children: /* @__PURE__ */ jsx8(AttachMoneyIcon, { fontSize: "small", color: "disabled" }) })
            },
            inputProps: {
              step: 0.1,
              min: 0,
              max: 100
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx8(
      SubOption3,
      {
        title: "Required Fuel Level",
        description: "The expected fuel level at vehicle delivery (in percent).",
        inline: true,
        children: /* @__PURE__ */ jsx8(
          TextField4,
          {
            fullWidth: true,
            type: "number",
            label: "Fuel Level",
            placeholder: "Enter a fuel level...",
            size: "small",
            variant: "outlined",
            value: fuelLevelPercent,
            onChange: handleFuelLevelChange,
            onBlur: handleFuelLevelBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx8(InputAdornment2, { position: "start", children: /* @__PURE__ */ jsx8(PercentIcon, { fontSize: "small", color: "disabled" }) })
            },
            inputProps: {
              step: 1,
              min: 0,
              max: 100
            }
          }
        )
      }
    )
  ] });
}

// src/definitions/appraisals.renderer.tsx
import { useState as useState7, useEffect as useEffect7, useCallback as useCallback6 } from "react";
import { TextField as TextField5, Typography as Typography6, Chip, Stack as Stack2, Paper } from "@mui/material";
import { Schedule as ScheduleIcon } from "@mui/icons-material";
import { SubOption as SubOption4 } from "@hopdrive/sdk-ui";
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var MOVE_TYPE_OPTIONS2 = [
  { value: "concierge", label: "Concierge" },
  { value: "operations", label: "Operations" }
];
function AppraisalsRenderer({ value, onChange, disabled }) {
  const appraisalsValue = value || {};
  const [allowedMoveTypes, setAllowedMoveTypes] = useState7(
    appraisalsValue.allowed_move_types ?? ["concierge", "operations"]
  );
  const [level, setLevel] = useState7(appraisalsValue.level ?? 0);
  useEffect7(() => {
    const av = value || {};
    setAllowedMoveTypes(av.allowed_move_types ?? ["concierge", "operations"]);
    setLevel(av.level ?? 0);
  }, [value]);
  const updateAppraisals = useCallback6(
    (updates) => {
      const currentValue = value || {};
      onChange({
        active: currentValue.active ?? false,
        active_timestamps: currentValue.active_timestamps ?? [],
        allowed_move_types: allowedMoveTypes,
        level,
        ...updates
      });
    },
    [allowedMoveTypes, level, onChange, value]
  );
  const handleMoveTypeToggle = (moveType) => {
    const newTypes = allowedMoveTypes.includes(moveType) ? allowedMoveTypes.filter((t) => t !== moveType) : [...allowedMoveTypes, moveType];
    setAllowedMoveTypes(newTypes);
    updateAppraisals({ allowed_move_types: newTypes });
  };
  const handleLevelChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setLevel(newValue);
  };
  const handleLevelBlur = () => {
    if (level !== (appraisalsValue.level ?? 0)) {
      updateAppraisals({ level });
    }
  };
  const formatTimestamp = (ts) => {
    const from = ts.from ? new Date(ts.from).toLocaleDateString() : "Start";
    const to = ts.to === "now" ? "Present" : ts.to ? new Date(ts.to).toLocaleDateString() : "End";
    return `${from} \u2192 ${to}`;
  };
  const activeTimestamps = appraisalsValue.active_timestamps ?? [];
  return /* @__PURE__ */ jsxs7("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx9(
      SubOption4,
      {
        title: "Allowed Move Types",
        description: "Select which move types support appraisals. Only these types will have appraisal functionality.",
        children: /* @__PURE__ */ jsx9(Stack2, { direction: "row", spacing: 1, children: MOVE_TYPE_OPTIONS2.map((option) => /* @__PURE__ */ jsx9(
          Chip,
          {
            label: option.label,
            onClick: () => !disabled && handleMoveTypeToggle(option.value),
            color: allowedMoveTypes.includes(option.value) ? "primary" : "default",
            variant: allowedMoveTypes.includes(option.value) ? "filled" : "outlined",
            disabled,
            sx: { cursor: disabled ? "not-allowed" : "pointer" }
          },
          option.value
        )) })
      }
    ),
    /* @__PURE__ */ jsx9(
      SubOption4,
      {
        inline: true,
        title: "Appraisal Level",
        description: "Appraisal level/tier (0 = default). Higher levels may unlock additional features based on the subscription tier.",
        children: /* @__PURE__ */ jsx9(
          TextField5,
          {
            type: "number",
            label: "Level",
            size: "small",
            variant: "outlined",
            value: level,
            onChange: handleLevelChange,
            onBlur: handleLevelBlur,
            disabled,
            sx: { width: 300 },
            inputProps: { step: 1, min: 0 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx9(
      SubOption4,
      {
        title: "Active Timeframes (deprecated)",
        description: "Windows recorded while the old activation switch decided who had Appraisals. Nothing reads them now; the contract's dates and the appraisal records do that job.",
        children: activeTimestamps.length > 0 ? /* @__PURE__ */ jsx9(Stack2, { spacing: 1, children: activeTimestamps.map((ts, index) => /* @__PURE__ */ jsxs7(
          Paper,
          {
            variant: "outlined",
            sx: {
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: ts.to === "now" ? "action.selected" : "transparent"
            },
            children: [
              /* @__PURE__ */ jsx9(ScheduleIcon, { fontSize: "small", color: "action" }),
              /* @__PURE__ */ jsxs7(Typography6, { variant: "body2", children: [
                formatTimestamp(ts),
                ts.to === "now" && /* @__PURE__ */ jsx9(Chip, { label: "Current", size: "small", color: "success", sx: { ml: 1 } })
              ] })
            ]
          },
          index
        )) }) : /* @__PURE__ */ jsx9(Typography6, { variant: "body2", color: "text.secondary", fontStyle: "italic", children: "No active timeframes configured" })
      }
    )
  ] });
}

// src/definitions/driver-pipeline.renderer.tsx
import { useState as useState8, useEffect as useEffect8, useCallback as useCallback7 } from "react";
import {
  TextField as TextField6,
  Typography as Typography7,
  InputAdornment as InputAdornment3,
  Divider as Divider3
} from "@mui/material";
import {
  People as PeopleIcon,
  HourglassEmpty as HourglassIcon,
  EventRepeat as EventRepeatIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import { StyledSwitch as StyledSwitch2, SubOption as SubOption5 } from "@hopdrive/sdk-ui";
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
function DriverPipelineRenderer({ value, onChange, disabled }) {
  const pipelineValue = value || {};
  const [funnelOpen, setFunnelOpen] = useState8(pipelineValue.funnel_open ?? true);
  const [activeDriverTarget, setActiveDriverTarget] = useState8(pipelineValue.active_driver_target ?? 20);
  const [waitlistTarget, setWaitlistTarget] = useState8(pipelineValue.waitlist_target ?? 25);
  const [backlogTarget, setBacklogTarget] = useState8(pipelineValue.backlog_target ?? 50);
  const [engagementFrequencyDays, setEngagementFrequencyDays] = useState8(
    pipelineValue.engagement_frequency_days ?? 7
  );
  const [maxMissedConfirmations, setMaxMissedConfirmations] = useState8(
    pipelineValue.max_missed_confirmations ?? 3
  );
  useEffect8(() => {
    const pipelineValue2 = value || {};
    setFunnelOpen(pipelineValue2.funnel_open ?? true);
    setActiveDriverTarget(pipelineValue2.active_driver_target ?? 20);
    setWaitlistTarget(pipelineValue2.waitlist_target ?? 25);
    setBacklogTarget(pipelineValue2.backlog_target ?? 50);
    setEngagementFrequencyDays(pipelineValue2.engagement_frequency_days ?? 7);
    setMaxMissedConfirmations(pipelineValue2.max_missed_confirmations ?? 3);
  }, [value]);
  const updatePipeline = useCallback7(
    (updates) => {
      onChange({
        funnel_open: funnelOpen,
        active_driver_target: activeDriverTarget,
        waitlist_target: waitlistTarget,
        backlog_target: backlogTarget,
        engagement_frequency_days: engagementFrequencyDays,
        max_missed_confirmations: maxMissedConfirmations,
        ...updates
      });
    },
    [
      funnelOpen,
      activeDriverTarget,
      waitlistTarget,
      backlogTarget,
      engagementFrequencyDays,
      maxMissedConfirmations,
      onChange
    ]
  );
  const handleFunnelOpenChange = (e) => {
    const newValue = e.target.checked;
    setFunnelOpen(newValue);
    updatePipeline({ funnel_open: newValue });
  };
  const handleActiveDriverTargetChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setActiveDriverTarget(newValue);
  };
  const handleActiveDriverTargetBlur = () => {
    if (activeDriverTarget !== (pipelineValue.active_driver_target ?? 20)) {
      updatePipeline({ active_driver_target: activeDriverTarget });
    }
  };
  const handleWaitlistTargetChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setWaitlistTarget(newValue);
  };
  const handleWaitlistTargetBlur = () => {
    if (waitlistTarget !== (pipelineValue.waitlist_target ?? 25)) {
      updatePipeline({ waitlist_target: waitlistTarget });
    }
  };
  const handleBacklogTargetChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setBacklogTarget(newValue);
  };
  const handleBacklogTargetBlur = () => {
    if (backlogTarget !== (pipelineValue.backlog_target ?? 50)) {
      updatePipeline({ backlog_target: backlogTarget });
    }
  };
  const handleEngagementFrequencyChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 1;
    setEngagementFrequencyDays(newValue);
  };
  const handleEngagementFrequencyBlur = () => {
    if (engagementFrequencyDays !== (pipelineValue.engagement_frequency_days ?? 7)) {
      updatePipeline({ engagement_frequency_days: engagementFrequencyDays });
    }
  };
  const handleMaxMissedConfirmationsChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setMaxMissedConfirmations(newValue);
  };
  const handleMaxMissedConfirmationsBlur = () => {
    if (maxMissedConfirmations !== (pipelineValue.max_missed_confirmations ?? 3)) {
      updatePipeline({ max_missed_confirmations: maxMissedConfirmations });
    }
  };
  return /* @__PURE__ */ jsxs8("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx10(
      SubOption5,
      {
        inline: true,
        title: "Recruitment Funnel",
        description: "Control whether the driver recruitment funnel is open for this region. When open, new drivers can be recruited and onboarded.",
        children: /* @__PURE__ */ jsx10(
          StyledSwitch2,
          {
            checked: funnelOpen,
            onChange: handleFunnelOpenChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx10(Divider3, { sx: { my: 1 } }),
    /* @__PURE__ */ jsx10(Typography7, { variant: "subtitle1", fontWeight: 600, children: "Driver Targets" }),
    /* @__PURE__ */ jsx10(
      SubOption5,
      {
        inline: true,
        title: "Active Driver Target",
        description: "Target number of active drivers for this region. Used to determine recruitment goals and capacity planning.",
        children: /* @__PURE__ */ jsx10(
          TextField6,
          {
            type: "number",
            label: "Active Driver Target",
            placeholder: "Enter target...",
            size: "small",
            variant: "outlined",
            value: activeDriverTarget,
            onChange: handleActiveDriverTargetChange,
            onBlur: handleActiveDriverTargetBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "start", children: /* @__PURE__ */ jsx10(PeopleIcon, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "end", children: "drivers" })
            },
            inputProps: {
              step: 1,
              min: 0
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx10(
      SubOption5,
      {
        inline: true,
        title: "Waitlist Target",
        description: "Target number of drivers on the waitlist for this region. Maintains a pipeline of ready drivers who can be activated when needed.",
        children: /* @__PURE__ */ jsx10(
          TextField6,
          {
            type: "number",
            label: "Waitlist Target",
            placeholder: "Enter target...",
            size: "small",
            variant: "outlined",
            value: waitlistTarget,
            onChange: handleWaitlistTargetChange,
            onBlur: handleWaitlistTargetBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "start", children: /* @__PURE__ */ jsx10(HourglassIcon, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "end", children: "drivers" })
            },
            inputProps: {
              step: 1,
              min: 0
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx10(
      SubOption5,
      {
        inline: true,
        title: "Backlog Target",
        description: "Maximum number of drivers allowed in the backlog. When reached, the recruitment funnel may be automatically closed.",
        children: /* @__PURE__ */ jsx10(
          TextField6,
          {
            type: "number",
            label: "Backlog Target",
            placeholder: "Enter target...",
            size: "small",
            variant: "outlined",
            value: backlogTarget,
            onChange: handleBacklogTargetChange,
            onBlur: handleBacklogTargetBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "start", children: /* @__PURE__ */ jsx10(PeopleIcon, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "end", children: "drivers" })
            },
            inputProps: {
              step: 1,
              min: 0
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx10(Divider3, { sx: { my: 1 } }),
    /* @__PURE__ */ jsx10(Typography7, { variant: "subtitle1", fontWeight: 600, children: "Engagement Settings" }),
    /* @__PURE__ */ jsx10(
      SubOption5,
      {
        inline: true,
        title: "Engagement Frequency",
        description: "How often (in days) to engage with drivers in the pipeline. Used for scheduling follow-up communications and check-ins.",
        children: /* @__PURE__ */ jsx10(
          TextField6,
          {
            type: "number",
            label: "Engagement Frequency",
            placeholder: "Enter frequency...",
            size: "small",
            variant: "outlined",
            value: engagementFrequencyDays,
            onChange: handleEngagementFrequencyChange,
            onBlur: handleEngagementFrequencyBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "start", children: /* @__PURE__ */ jsx10(EventRepeatIcon, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "end", children: "days" })
            },
            inputProps: {
              step: 1,
              min: 1
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx10(
      SubOption5,
      {
        inline: true,
        title: "Max Missed Confirmations",
        description: "Maximum number of missed confirmations allowed before removing a driver from the recruitment pipeline. Helps maintain an engaged and responsive driver pool.",
        children: /* @__PURE__ */ jsx10(
          TextField6,
          {
            type: "number",
            label: "Max Missed Confirmations",
            placeholder: "Enter maximum...",
            size: "small",
            variant: "outlined",
            value: maxMissedConfirmations,
            onChange: handleMaxMissedConfirmationsChange,
            onBlur: handleMaxMissedConfirmationsBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "start", children: /* @__PURE__ */ jsx10(WarningIcon, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx10(InputAdornment3, { position: "end", children: "misses" })
            },
            inputProps: {
              step: 1,
              min: 0
            }
          }
        )
      }
    )
  ] });
}

// src/definitions/fraud-audit.renderer.tsx
import { useState as useState9, useEffect as useEffect9, useCallback as useCallback8 } from "react";
import { TextField as TextField7, InputAdornment as InputAdornment4 } from "@mui/material";
import { Schedule as ScheduleIcon2, Route as RouteIcon } from "@mui/icons-material";
import { StyledSwitch as StyledSwitch3, SubOption as SubOption6 } from "@hopdrive/sdk-ui";
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
function FraudAuditRenderer({ value, onChange, disabled }) {
  const fraudValue = value || {};
  const [enabled, setEnabled] = useState9(fraudValue.enabled ?? false);
  const [timeTolerance, setTimeTolerance] = useState9(fraudValue.time_tolerance ?? 15);
  const [distanceTolerance, setDistanceTolerance] = useState9(fraudValue.distance_tolerance ?? 5);
  useEffect9(() => {
    const fraudValue2 = value || {};
    setEnabled(fraudValue2.enabled ?? false);
    setTimeTolerance(fraudValue2.time_tolerance ?? 15);
    setDistanceTolerance(fraudValue2.distance_tolerance ?? 5);
  }, [value]);
  const updateFraudAudit = useCallback8(
    (updates) => {
      onChange({
        enabled,
        time_tolerance: timeTolerance,
        distance_tolerance: distanceTolerance,
        ...updates
      });
    },
    [enabled, timeTolerance, distanceTolerance, onChange]
  );
  const handleEnabledChange = (e) => {
    const newValue = e.target.checked;
    setEnabled(newValue);
    updateFraudAudit({ enabled: newValue });
  };
  const handleTimeToleranceChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setTimeTolerance(newValue);
  };
  const handleTimeToleranceBlur = () => {
    if (timeTolerance !== (fraudValue.time_tolerance ?? 15)) {
      updateFraudAudit({ time_tolerance: timeTolerance });
    }
  };
  const handleDistanceToleranceChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setDistanceTolerance(newValue);
  };
  const handleDistanceToleranceBlur = () => {
    if (distanceTolerance !== (fraudValue.distance_tolerance ?? 5)) {
      updateFraudAudit({ distance_tolerance: distanceTolerance });
    }
  };
  return /* @__PURE__ */ jsxs9("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx11(
      SubOption6,
      {
        inline: true,
        title: "Fraud Audit Monitoring",
        description: "Enable fraud audit monitoring to detect potential fraud cases. When enabled, trips that fall outside the configured distance and time tolerances will be flagged for review.",
        children: /* @__PURE__ */ jsx11(
          StyledSwitch3,
          {
            checked: enabled,
            onChange: handleEnabledChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx11(
      SubOption6,
      {
        inline: true,
        title: "Time Tolerance",
        description: "Maximum allowed time deviation (in minutes) before flagging as potentially fraudulent behavior. Trips exceeding this tolerance will be marked for review.",
        children: /* @__PURE__ */ jsx11(
          TextField7,
          {
            type: "number",
            label: "Time Tolerance",
            placeholder: "Enter time tolerance...",
            size: "small",
            variant: "outlined",
            value: timeTolerance,
            onChange: handleTimeToleranceChange,
            onBlur: handleTimeToleranceBlur,
            disabled: disabled || !enabled,
            sx: {
              width: 300,
              opacity: enabled ? 1 : 0.5
            },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx11(InputAdornment4, { position: "start", children: /* @__PURE__ */ jsx11(ScheduleIcon2, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx11(InputAdornment4, { position: "end", children: "minutes" })
            },
            inputProps: {
              step: 1,
              min: 0,
              max: 120
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx11(
      SubOption6,
      {
        inline: true,
        title: "Distance Tolerance",
        description: "Maximum allowed distance deviation (in miles) before flagging as potentially fraudulent behavior. Trips exceeding this tolerance will be marked for review.",
        children: /* @__PURE__ */ jsx11(
          TextField7,
          {
            type: "number",
            label: "Distance Tolerance",
            placeholder: "Enter distance tolerance...",
            size: "small",
            variant: "outlined",
            value: distanceTolerance,
            onChange: handleDistanceToleranceChange,
            onBlur: handleDistanceToleranceBlur,
            disabled: disabled || !enabled,
            sx: {
              width: 300,
              opacity: enabled ? 1 : 0.5
            },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx11(InputAdornment4, { position: "start", children: /* @__PURE__ */ jsx11(RouteIcon, { fontSize: "small", color: "disabled" }) }),
              endAdornment: /* @__PURE__ */ jsx11(InputAdornment4, { position: "end", children: "miles" })
            },
            inputProps: {
              step: 1,
              min: 0,
              max: 50
            }
          }
        )
      }
    )
  ] });
}

// src/definitions/guestrides.renderer.tsx
import { Alert as Alert2, Checkbox as Checkbox2, FormControlLabel as FormControlLabel3, InputAdornment as InputAdornment5, MenuItem as MenuItem3, TextField as TextField8, Typography as Typography8 } from "@mui/material";
import { StyledSwitch as StyledSwitch4, SubOption as SubOption7 } from "@hopdrive/sdk-ui";

// src/renderers/host.tsx
import { createContext, useContext } from "react";
import { jsx as jsx12 } from "react/jsx-runtime";
var HostServicesContext = createContext(null);
function HostServicesProvider({ value, children }) {
  return /* @__PURE__ */ jsx12(HostServicesContext.Provider, { value, children });
}
function useHostServices() {
  const ctx = useContext(HostServicesContext);
  if (!ctx) {
    throw new Error(
      "[sdk-configs] useHostServices must be used within a HostServicesProvider. Wrap the settings renderer tree with <HostServicesProvider value={...}>."
    );
  }
  return ctx;
}
function useOptionalHostServices() {
  return useContext(HostServicesContext);
}
function hasRequiredPorts(services, requires) {
  if (!requires || requires.length === 0) return true;
  if (!services) return false;
  return requires.every((path) => {
    const parts = path.split(".");
    let cur = services;
    for (const part of parts) {
      if (cur == null || typeof cur !== "object") return false;
      cur = cur[part];
    }
    return cur != null;
  });
}

// src/definitions/guestrides.logic.ts
function mergeGuestridesValue(current, updates) {
  const next = { ...current ?? {} };
  for (const [key, value] of Object.entries(updates)) {
    if (value === void 0) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}
var NONE_ITEM = {
  id: "",
  title: "None (default resolution)",
  subtitle: "Use the standard access decision with no product algorithm."
};
function resolveAlgorithmSelector(enumOptions, currentKey) {
  const options = enumOptions ?? [];
  if (options.length === 0) {
    return { mode: "hidden" };
  }
  const items = [
    NONE_ITEM,
    ...options.map((option) => ({
      id: String(option.value),
      title: option.label,
      subtitle: option.description
    }))
  ];
  const known = new Set(items.map((item) => item.id));
  const selectedId = currentKey && known.has(currentKey) ? currentKey : "";
  return { mode: "group", items, selectedId };
}

// src/definitions/guestrides.renderer.tsx
import { jsx as jsx13, jsxs as jsxs10 } from "react/jsx-runtime";
var CHANNEL_LABELS = { sms: "Text message", email: "Email" };
var DEFAULT_CHANNELS = ["sms", "email"];
function GuestRidesRenderer({ value, onChange, disabled }) {
  const host = useOptionalHostServices();
  const rooftopUsers = host?.dataSources?.rooftopUsers ? host.dataSources.rooftopUsers(host.entity) : null;
  const current = value || {};
  const guestPortalEnabled = current.guest_portal_enabled ?? false;
  const maxTripMiles = current.max_trip_miles;
  const requireApproval = current.require_approval ?? false;
  const approverUserId = current.approver_user_id ?? null;
  const channels = current.approval_notification_channels ?? DEFAULT_CHANNELS;
  const commit = (updates) => onChange(mergeGuestridesValue(current, updates));
  const handleGuestPortalChange = (e) => {
    commit({ guest_portal_enabled: e.target.checked });
  };
  const handleMaxTripMilesChange = (e) => {
    const raw = e.target.value.trim();
    commit({ max_trip_miles: raw === "" ? void 0 : Number(raw) });
  };
  const handleRequireApprovalChange = (e) => {
    commit({ require_approval: e.target.checked });
  };
  const handleApproverChange = (e) => {
    const raw = e.target.value;
    commit({ approver_user_id: raw === "" ? null : Number(raw) });
  };
  const handleChannelChange = (channel) => (e) => {
    const next = e.target.checked ? [...channels, channel] : channels.filter((c) => c !== channel);
    commit({ approval_notification_channels: APPROVAL_CHANNELS.filter((c) => next.includes(c)) });
  };
  const missingApprover = requireApproval && approverUserId === null;
  return /* @__PURE__ */ jsxs10("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx13(
      SubOption7,
      {
        inline: true,
        title: "Allow Guest Portal Rideshare",
        description: "Allow guests to call their own ride through a self-service portal.",
        children: /* @__PURE__ */ jsx13(StyledSwitch4, { checked: guestPortalEnabled, onChange: handleGuestPortalChange, disabled })
      }
    ),
    /* @__PURE__ */ jsx13(
      SubOption7,
      {
        inline: true,
        title: "Max Trip Miles",
        description: "Maximum trip distance allowed for a guest ride. Leave blank to inherit.",
        children: /* @__PURE__ */ jsx13(
          TextField8,
          {
            type: "number",
            size: "small",
            value: maxTripMiles ?? "",
            onChange: handleMaxTripMilesChange,
            disabled,
            InputProps: { endAdornment: /* @__PURE__ */ jsx13(InputAdornment5, { position: "end", children: "mi" }) },
            sx: { width: 160 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx13(
      SubOption7,
      {
        inline: true,
        title: "Require Manager Approval",
        description: "Hold each guest ride until the approver below approves it. Nothing is booked with Uber or Lyft before then.",
        children: /* @__PURE__ */ jsx13(StyledSwitch4, { checked: requireApproval, onChange: handleRequireApprovalChange, disabled })
      }
    ),
    /* @__PURE__ */ jsx13(SubOption7, { inline: true, title: "Approver", description: "The person who approves or denies guest rides for this rooftop.", children: /* @__PURE__ */ jsx13(
      ApproverPicker,
      {
        approverUserId,
        users: rooftopUsers,
        disabled,
        onChange: handleApproverChange
      }
    ) }),
    missingApprover && /* @__PURE__ */ jsx13(Alert2, { severity: "warning", children: APPROVAL_REQUIRES_APPROVER_MESSAGE }),
    /* @__PURE__ */ jsx13(SubOption7, { inline: true, title: "Notify Approver By", description: "How the approver hears that a ride is waiting.", children: /* @__PURE__ */ jsx13("div", { style: { display: "flex", gap: "8px" }, children: APPROVAL_CHANNELS.map((channel) => /* @__PURE__ */ jsx13(
      FormControlLabel3,
      {
        label: CHANNEL_LABELS[channel],
        control: /* @__PURE__ */ jsx13(
          Checkbox2,
          {
            checked: channels.includes(channel),
            onChange: handleChannelChange(channel),
            disabled: disabled || channels.length === 1 && channels.includes(channel)
          }
        )
      },
      channel
    )) }) })
  ] });
}
function ApproverPicker({ approverUserId, users, disabled, onChange }) {
  if (!users) {
    return /* @__PURE__ */ jsx13(Typography8, { variant: "body2", sx: { color: "text.secondary" }, children: approverUserId === null ? "No approver named" : `User #${approverUserId}` });
  }
  const { options, loading } = users;
  const known = approverUserId === null || options.some((o) => Number(o.id) === approverUserId);
  return /* @__PURE__ */ jsxs10(
    TextField8,
    {
      select: true,
      size: "small",
      label: "Approver",
      value: approverUserId === null ? "" : String(approverUserId),
      onChange,
      disabled: disabled || loading,
      sx: { minWidth: 240 },
      children: [
        /* @__PURE__ */ jsx13(MenuItem3, { value: "", children: "No approver" }),
        !known && /* @__PURE__ */ jsxs10(MenuItem3, { value: String(approverUserId), children: [
          "User #",
          approverUserId
        ] }),
        options.map((option) => /* @__PURE__ */ jsxs10(MenuItem3, { value: String(option.id), children: [
          option.label,
          option.sublabel ? ` (${option.sublabel})` : ""
        ] }, option.id))
      ]
    }
  );
}

// src/definitions/guestrides-access.renderer.tsx
import { Box as Box5, TextField as TextField9, InputAdornment as InputAdornment6, Typography as Typography10, CircularProgress } from "@mui/material";
import { StyledSwitch as StyledSwitch5, SubOption as SubOption8, BigButtonGroup } from "@hopdrive/sdk-ui";

// src/renderers/shared/settingsBigButton.tsx
import { ButtonBase, Typography as Typography9, useTheme } from "@mui/material";
import { jsx as jsx14, jsxs as jsxs11 } from "react/jsx-runtime";
function createSettingsBigButton({ title, subtitle, icon: icon2 }) {
  return function SettingsBigButton({ selected, onClick, disabled }) {
    const theme = useTheme();
    return /* @__PURE__ */ jsxs11(
      ButtonBase,
      {
        onClick,
        disabled,
        sx: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          p: 2.5,
          border: selected ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          backgroundColor: selected ? theme.palette.primary.main : "#f0f0f0",
          color: selected ? "#fff" : theme.palette.text.primary,
          transition: "0.15s",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          minHeight: 100,
          "&:hover:not(:disabled)": {
            backgroundColor: selected ? theme.palette.primary.dark : "#e0e0e0"
          }
        },
        children: [
          icon2 && icon2,
          /* @__PURE__ */ jsx14(
            Typography9,
            {
              sx: {
                fontSize: subtitle ? 20 : 16,
                fontWeight: subtitle ? 600 : 500,
                lineHeight: 1.25,
                textAlign: "center",
                mt: icon2 ? 1 : 0
              },
              children: title
            }
          ),
          subtitle && /* @__PURE__ */ jsx14(
            Typography9,
            {
              sx: {
                fontSize: 13,
                fontWeight: 400,
                lineHeight: 1.4,
                textAlign: "center",
                mt: 0.5,
                opacity: selected ? 0.9 : 0.7
              },
              children: subtitle
            }
          )
        ]
      }
    );
  };
}

// src/definitions/guestrides-access.renderer.tsx
import { jsx as jsx15, jsxs as jsxs12 } from "react/jsx-runtime";
function GuestRidesAccessRenderer({
  value,
  onChange,
  disabled,
  enumOptions,
  enumLoading
}) {
  const current = value || {};
  const enabled = current.enabled ?? false;
  const marketable = current.marketable ?? true;
  const creditLimit = current.credit_limit;
  const commit = (updates) => onChange(mergeGuestridesValue(current, updates));
  const selector = resolveAlgorithmSelector(enumOptions, current.algorithm_key);
  const handleEnabledChange = (e) => {
    commit({ enabled: e.target.checked });
  };
  const handleMarketableChange = (e) => {
    commit({ marketable: e.target.checked });
  };
  const handleCreditLimitChange = (e) => {
    const raw = e.target.value.trim();
    commit({ credit_limit: raw === "" ? void 0 : Number(raw) });
  };
  return /* @__PURE__ */ jsxs12("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx15(
      SubOption8,
      {
        inline: true,
        title: "Guest Rides Enabled",
        description: "Enable or disable the guest ride feature. Guest rides give clients the ability to call rides for their guests or have their guests call their own ride.",
        children: /* @__PURE__ */ jsx15(StyledSwitch5, { checked: enabled, onChange: handleEnabledChange, disabled })
      }
    ),
    /* @__PURE__ */ jsx15(
      SubOption8,
      {
        inline: true,
        title: "Marketable",
        description: "Show Guest Rides marketing content to customers who are not entitled to it. On by default. Turning it off hides the product ENTIRELY at this scope for every customer it covers \u2014 including entitled, paying customers, whose navigation and access disappear.",
        children: /* @__PURE__ */ jsx15(StyledSwitch5, { checked: marketable, onChange: handleMarketableChange, disabled })
      }
    ),
    /* @__PURE__ */ jsx15(
      SubOption8,
      {
        inline: true,
        title: "Credit Limit",
        description: "Maximum outstanding Guest Rides balance before new rides are restricted. Requires the credit-limit algorithm below. Leave blank (or 0) for no credit gating.",
        children: /* @__PURE__ */ jsx15(
          TextField9,
          {
            type: "number",
            size: "small",
            value: creditLimit ?? "",
            onChange: handleCreditLimitChange,
            disabled,
            InputProps: { startAdornment: /* @__PURE__ */ jsx15(InputAdornment6, { position: "start", children: "$" }) },
            sx: { width: 160 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx15(
      AlgorithmSelector,
      {
        selector,
        enumLoading,
        disabled,
        onSelect: (id) => commit({ algorithm_key: id === "" ? void 0 : id })
      }
    )
  ] });
}
function AlgorithmSelector({
  selector,
  enumLoading,
  disabled,
  onSelect
}) {
  const AlgorithmReference = useOptionalHostServices()?.slots?.AlgorithmReference;
  if (enumLoading) {
    return /* @__PURE__ */ jsxs12(Box5, { sx: { display: "flex", alignItems: "center", gap: 1, py: 1 }, children: [
      /* @__PURE__ */ jsx15(CircularProgress, { size: 18 }),
      /* @__PURE__ */ jsx15(Typography10, { variant: "body2", color: "text.secondary", children: "Loading algorithms..." })
    ] });
  }
  if (selector.mode === "hidden") {
    return null;
  }
  const items = selector.items.map((item) => ({
    id: item.id,
    render: createSettingsBigButton({
      title: AlgorithmReference && item.id ? /* @__PURE__ */ jsx15(AlgorithmReference, { algorithmKey: item.id, selection: true }) : item.title,
      subtitle: item.subtitle
    })
  }));
  return /* @__PURE__ */ jsxs12(Box5, { children: [
    /* @__PURE__ */ jsx15(Typography10, { variant: "body2", sx: { fontWeight: 600, mb: 1 }, children: "Access Algorithm" }),
    /* @__PURE__ */ jsx15(
      BigButtonGroup,
      {
        items,
        selectedId: selector.selectedId,
        onSelectionChange: onSelect,
        columns: { xs: 6, sm: 6, md: 6 },
        disabled
      }
    )
  ] });
}

// src/definitions/mobility.renderer.tsx
import { Stack as Stack3, Typography as Typography11 } from "@mui/material";
import { StyledSwitch as StyledSwitch6 } from "@hopdrive/sdk-ui";
import { jsx as jsx16, jsxs as jsxs13 } from "react/jsx-runtime";
function MobilityRenderer({ settingKey, value, onChange, disabled }) {
  const current = value && typeof value === "object" ? value : {};
  const active = Boolean(current.active);
  const thirdParty = Boolean(current.third_party);
  return /* @__PURE__ */ jsxs13(Stack3, { spacing: 0.5, children: [
    /* @__PURE__ */ jsxs13(Stack3, { direction: "row", alignItems: "center", spacing: 1, children: [
      /* @__PURE__ */ jsx16(
        StyledSwitch6,
        {
          checked: active,
          onChange: (e) => (
            // Deactivating the feature also turns couriers off — a stale
            // third_party: true must not survive an active: false.
            onChange(
              e.target.checked ? { ...current, active: true } : { ...current, active: false, ..."third_party" in current ? { third_party: false } : {} }
            )
          ),
          disabled
        }
      ),
      /* @__PURE__ */ jsx16(Typography11, { variant: "body2", color: "text.secondary", children: active ? "Active" : "Inactive" })
    ] }),
    settingKey === "parts_delivery" ? /* @__PURE__ */ jsxs13(Stack3, { direction: "row", alignItems: "center", spacing: 1, children: [
      /* @__PURE__ */ jsx16(
        StyledSwitch6,
        {
          checked: thirdParty,
          onChange: (e) => onChange({ ...current, third_party: e.target.checked }),
          disabled: disabled || !active
        }
      ),
      /* @__PURE__ */ jsxs13(Typography11, { variant: "body2", color: "text.secondary", children: [
        "HopDrive Managed couriers (Uber Direct / DoorDash)",
        active ? "" : " \u2014 requires Active"
      ] })
    ] }) : null
  ] });
}

// src/definitions/routing.renderer.tsx
import { useState as useState10, useEffect as useEffect10, useCallback as useCallback9 } from "react";
import { TextField as TextField10, MenuItem as MenuItem4 } from "@mui/material";
import { StyledSwitch as StyledSwitch7 } from "@hopdrive/sdk-ui";
import { SubOption as SubOption9 } from "@hopdrive/sdk-ui";
import { jsx as jsx17, jsxs as jsxs14 } from "react/jsx-runtime";
function RoutingRenderer({ value, onChange, disabled }) {
  const routingValue = value || {};
  const [avoidTolls, setAvoidTolls] = useState10(routingValue.avoid_tolls ?? false);
  const [avoidHighways, setAvoidHighways] = useState10(routingValue.avoid_highways ?? false);
  const [avoidFerries, setAvoidFerries] = useState10(routingValue.avoid_ferries ?? false);
  const [routePreference, setRoutePreference] = useState10(
    routingValue.route_preference ?? "fastest"
  );
  const [noTollCharges, setNoTollCharges] = useState10(routingValue.no_toll_charges ?? false);
  useEffect10(() => {
    const routingValue2 = value || {};
    setAvoidTolls(routingValue2.avoid_tolls ?? false);
    setAvoidHighways(routingValue2.avoid_highways ?? false);
    setAvoidFerries(routingValue2.avoid_ferries ?? false);
    setRoutePreference(routingValue2.route_preference ?? "fastest");
    setNoTollCharges(routingValue2.no_toll_charges ?? false);
  }, [value]);
  const updateRouting = useCallback9(
    (updates) => {
      onChange({
        avoid_tolls: avoidTolls,
        avoid_highways: avoidHighways,
        avoid_ferries: avoidFerries,
        route_preference: routePreference,
        no_toll_charges: noTollCharges,
        ...updates
      });
    },
    [avoidTolls, avoidHighways, avoidFerries, routePreference, noTollCharges, onChange]
  );
  const handleAvoidTollsChange = (e) => {
    const newValue = e.target.checked;
    setAvoidTolls(newValue);
    updateRouting({ avoid_tolls: newValue });
  };
  const handleAvoidHighwaysChange = (e) => {
    const newValue = e.target.checked;
    setAvoidHighways(newValue);
    updateRouting({ avoid_highways: newValue });
  };
  const handleAvoidFerriesChange = (e) => {
    const newValue = e.target.checked;
    setAvoidFerries(newValue);
    updateRouting({ avoid_ferries: newValue });
  };
  const handleRoutePreferenceChange = (e) => {
    const newValue = e.target.value;
    setRoutePreference(newValue);
    updateRouting({ route_preference: newValue });
  };
  const handleNoTollChargesChange = (e) => {
    const newValue = e.target.checked;
    setNoTollCharges(newValue);
    updateRouting({ no_toll_charges: newValue });
  };
  return /* @__PURE__ */ jsxs14("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx17(
      SubOption9,
      {
        title: "Route Optimization",
        description: 'Choose how routes should be optimized. "Fastest" prioritizes travel time, while "Shortest" prioritizes distance.',
        children: /* @__PURE__ */ jsxs14(
          TextField10,
          {
            fullWidth: true,
            select: true,
            label: "Route Preference",
            size: "small",
            variant: "outlined",
            value: routePreference,
            onChange: handleRoutePreferenceChange,
            disabled,
            sx: { maxWidth: 400 },
            children: [
              /* @__PURE__ */ jsx17(MenuItem4, { value: "fastest", children: "Fastest (optimize for time)" }),
              /* @__PURE__ */ jsx17(MenuItem4, { value: "shortest", children: "Shortest (optimize for distance)" })
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx17(
      SubOption9,
      {
        inline: true,
        title: "Avoid Toll Roads",
        description: "When enabled, routes will avoid toll roads where possible. This may result in longer travel times or distances.",
        children: /* @__PURE__ */ jsx17(
          StyledSwitch7,
          {
            checked: avoidTolls,
            onChange: handleAvoidTollsChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx17(
      SubOption9,
      {
        inline: true,
        title: "Avoid Highways",
        description: "When enabled, routes will avoid highways where possible. Useful for vehicles with highway restrictions or customers preferring local roads.",
        children: /* @__PURE__ */ jsx17(
          StyledSwitch7,
          {
            checked: avoidHighways,
            onChange: handleAvoidHighwaysChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx17(
      SubOption9,
      {
        inline: true,
        title: "Avoid Ferries",
        description: "When enabled, routes will avoid ferries where possible. Useful for customers who cannot use ferry routes.",
        children: /* @__PURE__ */ jsx17(
          StyledSwitch7,
          {
            checked: avoidFerries,
            onChange: handleAvoidFerriesChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx17(
      SubOption9,
      {
        inline: true,
        title: "No Toll Charges",
        description: "When enabled, toll charges are excluded from the transport cost. Lane tolls are billed as $0 in AR. When disabled, tolls charge normally.",
        children: /* @__PURE__ */ jsx17(
          StyledSwitch7,
          {
            checked: noTollCharges,
            onChange: handleNoTollChargesChange,
            disabled
          }
        )
      }
    )
  ] });
}

// src/definitions/email-scrape-config.renderer.tsx
import { useState as useState11, useEffect as useEffect11, useCallback as useCallback10 } from "react";
import {
  Box as Box6,
  TextField as TextField11,
  Typography as Typography12,
  Accordion as Accordion3,
  AccordionSummary as AccordionSummary3,
  AccordionDetails as AccordionDetails3,
  IconButton as IconButton2,
  Button as Button2,
  Paper as Paper2,
  Stack as Stack4,
  Chip as Chip2,
  MenuItem as MenuItem5,
  Tooltip,
  Divider as Divider4
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon4,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from "@mui/icons-material";
import { SubOption as SubOption10 } from "@hopdrive/sdk-ui";
import { Fragment, jsx as jsx18, jsxs as jsxs15 } from "react/jsx-runtime";
var MAPPING_FIELDS = [
  { key: "name", label: "Customer Name", description: "Customer name from appointment email" },
  { key: "phone", label: "Phone Number", description: "Customer phone number" },
  { key: "address", label: "Address", description: "Customer address" },
  { key: "vehicle", label: "Vehicle", description: "Vehicle make/model/year" },
  { key: "vin", label: "VIN", description: "Vehicle identification number" },
  { key: "advisor", label: "Advisor", description: "Service advisor name" },
  { key: "services", label: "Services", description: "Requested services" },
  { key: "dealer_code", label: "Dealer Code", description: "Dealer code identifier" },
  { key: "appointment_time", label: "Appointment Time", description: "Scheduled appointment date/time" },
  { key: "appointment_type", label: "Appointment Type", description: "Type of service appointment" },
  { key: "arrangement", label: "Arrangement", description: "Transportation arrangement details" },
  { key: "confirmation_code", label: "Confirmation Code", description: "Appointment confirmation code" }
];
var PLUGINS = [
  { value: "trim", label: "Trim", description: "Removes leading/trailing whitespace" },
  { value: "getMetaContent", label: "Get Meta Content", description: "Extracts content from HTML meta tag" },
  {
    value: "matchThenNextElement",
    label: "Match Then Next",
    description: "Finds matching text, returns next element"
  },
  { value: "extractBetween", label: "Extract Between", description: "Extracts text between two markers" }
];
function EmailScrapeConfigRenderer({ value, onChange, disabled }) {
  const config = value || {};
  const [name, setName] = useState11(config.name ?? "");
  const [version, setVersion] = useState11(config.version ?? "");
  const [configKey, setConfigKey] = useState11(config.key ?? "");
  const [expandedMapping, setExpandedMapping] = useState11(false);
  useEffect11(() => {
    const cfg = value || {};
    setName(cfg.name ?? "");
    setVersion(cfg.version ?? "");
    setConfigKey(cfg.key ?? "");
  }, [value]);
  const updateConfig = useCallback10(
    (updates) => {
      const current = value || {};
      onChange({
        ...current,
        ...updates
      });
    },
    [onChange, value]
  );
  const handleNameBlur = () => {
    if (name !== (config.name ?? "")) {
      updateConfig({ name: name || void 0 });
    }
  };
  const handleVersionBlur = () => {
    if (version !== (config.version ?? "")) {
      updateConfig({ version: version || void 0 });
    }
  };
  const handleKeyBlur = () => {
    if (configKey !== (config.key ?? "")) {
      updateConfig({ key: configKey || void 0 });
    }
  };
  const updateMapping = useCallback10(
    (fieldKey, mapping) => {
      const currentMappings = config.mappings || {};
      const newMappings = { ...currentMappings };
      if (mapping) {
        newMappings[fieldKey] = mapping;
      } else {
        delete newMappings[fieldKey];
      }
      const hasAnyMapping = Object.keys(newMappings).length > 0;
      updateConfig({ mappings: hasAnyMapping ? newMappings : void 0 });
    },
    [config.mappings, updateConfig]
  );
  const configuredMappings = MAPPING_FIELDS.filter(
    (field) => config.mappings?.[field.key]
  );
  const unconfiguredMappings = MAPPING_FIELDS.filter(
    (field) => !config.mappings?.[field.key]
  );
  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpandedMapping(isExpanded ? panel : false);
  };
  return /* @__PURE__ */ jsxs15("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx18(
      SubOption10,
      {
        title: "Basic Configuration",
        description: "General settings for this email scraping configuration.",
        children: /* @__PURE__ */ jsxs15(Stack4, { spacing: 2, sx: { maxWidth: 400 }, children: [
          /* @__PURE__ */ jsx18(
            TextField11,
            {
              label: "Configuration Name",
              value: name,
              onChange: (e) => setName(e.target.value),
              onBlur: handleNameBlur,
              disabled,
              size: "small",
              placeholder: "e.g., CDK Appointment Email",
              helperText: "Identifier for this scraping configuration"
            }
          ),
          /* @__PURE__ */ jsx18(
            TextField11,
            {
              label: "Version",
              value: version,
              onChange: (e) => setVersion(e.target.value),
              onBlur: handleVersionBlur,
              disabled,
              size: "small",
              placeholder: "e.g., 1.0",
              helperText: "Schema version for compatibility tracking"
            }
          ),
          /* @__PURE__ */ jsx18(
            TextField11,
            {
              label: "Key",
              value: configKey,
              onChange: (e) => setConfigKey(e.target.value),
              onBlur: handleKeyBlur,
              disabled,
              size: "small",
              placeholder: "e.g., cdk_v1",
              helperText: "Unique key identifier",
              InputProps: {
                sx: { fontFamily: "monospace" }
              }
            }
          )
        ] })
      }
    ),
    /* @__PURE__ */ jsx18(Divider4, {}),
    /* @__PURE__ */ jsx18(
      SubOption10,
      {
        title: "Field Mappings",
        description: "Configure how each field is extracted from appointment emails. Each mapping defines a CSS selector and optional processing pipeline.",
        children: /* @__PURE__ */ jsxs15(Fragment, { children: [
          configuredMappings.length > 0 && /* @__PURE__ */ jsx18(Stack4, { spacing: 1, sx: { mb: 2 }, children: configuredMappings.map((field) => /* @__PURE__ */ jsx18(
            MappingAccordion,
            {
              fieldKey: field.key,
              label: field.label,
              description: field.description,
              mapping: config.mappings?.[field.key],
              onChange: (mapping) => updateMapping(field.key, mapping),
              onDelete: () => updateMapping(field.key, void 0),
              disabled,
              expanded: expandedMapping === field.key,
              onExpandChange: handleAccordionChange(field.key)
            },
            field.key
          )) }),
          unconfiguredMappings.length > 0 && /* @__PURE__ */ jsxs15(Box6, { children: [
            /* @__PURE__ */ jsx18(Typography12, { variant: "subtitle2", color: "text.secondary", sx: { mb: 1 }, children: "Add Field Mapping" }),
            /* @__PURE__ */ jsx18(Box6, { sx: { display: "flex", flexWrap: "wrap", gap: 1 }, children: unconfiguredMappings.map((field) => /* @__PURE__ */ jsx18(
              Chip2,
              {
                label: field.label,
                onClick: () => {
                  updateMapping(field.key, { selector: "", pipeline: [] });
                  setExpandedMapping(field.key);
                },
                icon: /* @__PURE__ */ jsx18(AddIcon, {}),
                variant: "outlined",
                size: "small",
                disabled,
                sx: { cursor: "pointer" }
              },
              field.key
            )) })
          ] }),
          configuredMappings.length === 0 && /* @__PURE__ */ jsx18(Paper2, { variant: "outlined", sx: { p: 3, textAlign: "center" }, children: /* @__PURE__ */ jsx18(Typography12, { variant: "body2", color: "text.secondary", children: "No field mappings configured. Click a field above to add a mapping." }) })
        ] })
      }
    )
  ] });
}
function MappingAccordion({
  fieldKey,
  label,
  description,
  mapping,
  onChange,
  onDelete,
  disabled,
  expanded,
  onExpandChange
}) {
  const [selector, setSelector] = useState11(mapping?.selector ?? "");
  useEffect11(() => {
    setSelector(mapping?.selector ?? "");
  }, [mapping?.selector]);
  const handleSelectorBlur = () => {
    if (selector !== (mapping?.selector ?? "")) {
      onChange({ ...mapping, selector: selector || void 0 });
    }
  };
  const updatePipeline = (pipeline) => {
    onChange({ ...mapping, pipeline: pipeline.length > 0 ? pipeline : void 0 });
  };
  const addPipelineStep = () => {
    const currentPipeline = mapping?.pipeline || [];
    const newStep = {
      ordinal: currentPipeline.length,
      plugin: "trim",
      params: {}
    };
    updatePipeline([...currentPipeline, newStep]);
  };
  const updatePipelineStep = (index, step) => {
    const currentPipeline = [...mapping?.pipeline || []];
    currentPipeline[index] = step;
    updatePipeline(currentPipeline);
  };
  const deletePipelineStep = (index) => {
    const currentPipeline = [...mapping?.pipeline || []];
    currentPipeline.splice(index, 1);
    currentPipeline.forEach((step, i) => {
      step.ordinal = i;
    });
    updatePipeline(currentPipeline);
  };
  return /* @__PURE__ */ jsxs15(Accordion3, { expanded, onChange: onExpandChange, children: [
    /* @__PURE__ */ jsx18(AccordionSummary3, { expandIcon: /* @__PURE__ */ jsx18(ExpandMoreIcon4, {}), children: /* @__PURE__ */ jsxs15(Box6, { sx: { display: "flex", alignItems: "center", gap: 1, width: "100%", pr: 2 }, children: [
      /* @__PURE__ */ jsx18(Typography12, { variant: "subtitle2", fontWeight: 600, children: label }),
      /* @__PURE__ */ jsx18(Typography12, { variant: "body2", color: "text.secondary", sx: { flex: 1 }, children: mapping?.selector ? `selector: ${mapping.selector}` : "(not configured)" }),
      mapping?.pipeline && mapping.pipeline.length > 0 && /* @__PURE__ */ jsx18(Chip2, { label: `${mapping.pipeline.length} step(s)`, size: "small" })
    ] }) }),
    /* @__PURE__ */ jsx18(AccordionDetails3, { children: /* @__PURE__ */ jsxs15(Box6, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
      /* @__PURE__ */ jsx18(Typography12, { variant: "body2", color: "text.secondary", children: description }),
      /* @__PURE__ */ jsx18(
        TextField11,
        {
          label: "CSS Selector",
          value: selector,
          onChange: (e) => setSelector(e.target.value),
          onBlur: handleSelectorBlur,
          disabled,
          size: "small",
          fullWidth: true,
          placeholder: 'e.g., .customer-name, #phone-field, div[data-field="address"]',
          helperText: "CSS selector to locate the element containing this field value",
          InputProps: {
            sx: { fontFamily: "monospace", fontSize: "0.875rem" }
          }
        }
      ),
      /* @__PURE__ */ jsxs15(Box6, { children: [
        /* @__PURE__ */ jsx18(Typography12, { variant: "subtitle2", gutterBottom: true, children: "Processing Pipeline" }),
        /* @__PURE__ */ jsx18(Typography12, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "Optional steps to extract and transform the value after selection." }),
        /* @__PURE__ */ jsx18(Stack4, { spacing: 1.5, children: (mapping?.pipeline || []).map((step, index) => /* @__PURE__ */ jsx18(
          PipelineStepEditor,
          {
            step,
            index,
            onChange: (updated) => updatePipelineStep(index, updated),
            onDelete: () => deletePipelineStep(index),
            disabled
          },
          index
        )) }),
        /* @__PURE__ */ jsx18(
          Button2,
          {
            startIcon: /* @__PURE__ */ jsx18(AddIcon, {}),
            onClick: addPipelineStep,
            disabled,
            size: "small",
            sx: { mt: 1 },
            children: "Add Pipeline Step"
          }
        )
      ] }),
      /* @__PURE__ */ jsx18(Box6, { sx: { display: "flex", justifyContent: "flex-end", pt: 1 }, children: /* @__PURE__ */ jsx18(
        Button2,
        {
          color: "error",
          startIcon: /* @__PURE__ */ jsx18(DeleteIcon, {}),
          onClick: onDelete,
          disabled,
          size: "small",
          children: "Remove Mapping"
        }
      ) })
    ] }) })
  ] });
}
function PipelineStepEditor({ step, index, onChange, onDelete, disabled }) {
  const [paramsJson, setParamsJson] = useState11(JSON.stringify(step.params || {}, null, 2));
  const [paramsError, setParamsError] = useState11(null);
  useEffect11(() => {
    setParamsJson(JSON.stringify(step.params || {}, null, 2));
    setParamsError(null);
  }, [step.params]);
  const handlePluginChange = (e) => {
    onChange({
      ...step,
      plugin: e.target.value
    });
  };
  const handleParamsChange = (e) => {
    const newValue = e.target.value;
    setParamsJson(newValue);
    try {
      JSON.parse(newValue);
      setParamsError(null);
    } catch {
      setParamsError("Invalid JSON");
    }
  };
  const handleParamsBlur = () => {
    try {
      const parsed = JSON.parse(paramsJson);
      if (JSON.stringify(parsed) !== JSON.stringify(step.params || {})) {
        onChange({
          ...step,
          params: Object.keys(parsed).length > 0 ? parsed : void 0
        });
      }
      setParamsError(null);
    } catch {
      setParamsError("Invalid JSON - changes not saved");
    }
  };
  const selectedPlugin = PLUGINS.find((p) => p.value === step.plugin);
  return /* @__PURE__ */ jsx18(Paper2, { variant: "outlined", sx: { p: 2 }, children: /* @__PURE__ */ jsxs15(Box6, { sx: { display: "flex", alignItems: "flex-start", gap: 2 }, children: [
    /* @__PURE__ */ jsxs15(Box6, { sx: { display: "flex", alignItems: "center", gap: 0.5, pt: 1 }, children: [
      /* @__PURE__ */ jsx18(DragIcon, { fontSize: "small", color: "disabled" }),
      /* @__PURE__ */ jsxs15(Typography12, { variant: "body2", color: "text.secondary", fontWeight: 600, children: [
        index + 1,
        "."
      ] })
    ] }),
    /* @__PURE__ */ jsxs15(Box6, { sx: { flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }, children: [
      /* @__PURE__ */ jsx18(
        TextField11,
        {
          select: true,
          label: "Plugin",
          value: step.plugin || "",
          onChange: handlePluginChange,
          disabled,
          size: "small",
          sx: { maxWidth: 250 },
          children: PLUGINS.map((plugin) => /* @__PURE__ */ jsx18(MenuItem5, { value: plugin.value, children: plugin.label }, plugin.value))
        }
      ),
      selectedPlugin && /* @__PURE__ */ jsx18(Typography12, { variant: "body2", color: "text.secondary", children: selectedPlugin.description }),
      /* @__PURE__ */ jsx18(
        TextField11,
        {
          label: "Parameters (JSON)",
          value: paramsJson,
          onChange: handleParamsChange,
          onBlur: handleParamsBlur,
          disabled,
          size: "small",
          multiline: true,
          rows: 2,
          error: !!paramsError,
          helperText: paramsError || "Plugin-specific parameters as key-value pairs",
          InputProps: {
            sx: { fontFamily: "monospace", fontSize: "0.8rem" }
          }
        }
      )
    ] }),
    /* @__PURE__ */ jsx18(Tooltip, { title: "Delete step", children: /* @__PURE__ */ jsx18(IconButton2, { onClick: onDelete, disabled, size: "small", color: "error", children: /* @__PURE__ */ jsx18(DeleteIcon, { fontSize: "small" }) }) })
  ] }) });
}

// src/definitions/preferred-ride-partner.renderer.tsx
import { ButtonBase as ButtonBase2, Typography as Typography13, useTheme as useTheme2 } from "@mui/material";
import { BigButtonGroup as BigButtonGroup2 } from "@hopdrive/sdk-ui";
import { jsx as jsx19 } from "react/jsx-runtime";
var UberLogo = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTAgODYuODYiPjxnIGlkPSJMYXllcl8yIiBkYXRhLW5hbWU9IkxheWVyIDIiPjxnIGlkPSJ1YmVyIj48cGF0aCBmaWxsPSJjdXJyZW50Q29sb3IiIGQ9Ik03Ny4yNCw4NS4zNVYuMTFIODkuMzhWMzAuODZjOC43Ny03Ljk1LDE4LjUxLTEwLjY5LDI5LjU0LTguMjNBMzEsMzEsMCwwLDEsMTM3LjgzLDM1LDMyLjQ3LDMyLjQ3LDAsMCwxLDg5LjQsNzcuOTJ2Ny40M0g3Ny4yNFptMzMuNDItNTIuNDhjLTExLjQxLS40NS0yMS41Myw5LjMtMjEuNDgsMjEuNDNhMjEuMzUsMjEuMzUsMCwxLDAsNDIuNjkuMDZDMTMyLDQyLjI5LDEyMS44MSwzMi40NywxMTAuNjYsMzIuODdaIi8+PHBhdGggZmlsbD0iY3VycmVudENvbG9yIiBkPSJNLjE1LjA5SDEyLjg3VjIuNDdxMCwyNS4yOSwwLDUwLjU4YzAsNi4xNCwxLjc5LDExLjcxLDYuMTMsMTYuMjMsMTAuNDEsMTAuODQsMjkuNTIsNi41NiwzNC4xNi03Ljc0YTMwLjE3LDMwLjE3LDAsMCwwLDEuNDgtOWMuMTQtMTYuNjYuMDctMzMuMzMuMDctNTBWMGM0LjA3LDAsOCwwLDExLjkzLDAsLjI4LDAsLjcuNS43OS44M2E3LjQ3LDcuNDcsMCwwLDEsLjA2LDEuNzFWODIuODZjMCwyLjQ5LDAsMi40OS0yLjUsMi40OS0yLjY4LDAtNS4zNy0uMDktOCwwLTEuNjMuMDgtMi4zNC0uMzMtMi4xOC0yLjFzMC0zLjQyLDAtNS40N2MtMS43MiwxLjM0LTMuMTUsMi41Ni00LjcsMy42MS04LDUuNDEtMTYuOTIsNi42NC0yNi4xNyw0LjM1QzEzLjMxLDgzLjE0LDUuODUsNzYuNTUsMi4yNyw2Ni4xMkEzOC41NywzOC41NywwLDAsMSwuMTQsNTQuMzRDLS4wOSwzNi43MiwwLDE5LjA5LDAsMS40NiwwLDEuMDkuMS43MS4xNS4wOVoiLz48cGF0aCBmaWxsPSJjdXJyZW50Q29sb3IiIGQ9Ik0yMTIuMjksNTguMjlIMTYxLjQyYzEuNjgsNy45LDUuODIsMTMuNTcsMTMuMTUsMTYuMTksMTAsMy41OCwxOC41LjY5LDI1LjQyLTcuNjNsOC44Miw2LjQ4YTMyLjcsMzIuNywwLDAsMS0xMiwxMC4wN2MtMTIuNjYsNi4zMy0yOS40NCwzLjM3LTM5LjE4LTYuODlDMTQxLjI3LDU5LjI0LDE0OSwzMCwxNzEuOCwyMy4yMWMxOC01LjMzLDM2LjI4LDUuMTcsMzkuNTUsMjQuNDNDMjExLjkzLDUxLjA1LDIxMiw1NC41NiwyMTIuMjksNTguMjlabS0xMy05LjlhMTguNzQsMTguNzQsMCwwLDAtMTkuMTMtMTUuNjhjLTkuMjkuNDItMTguNDUsOC40Mi0xOC4xMiwxNS42OFoiLz48cGF0aCBmaWxsPSJjdXJyZW50Q29sb3IiIGQ9Ik0yMzEuMTcsODUuMjRIMjE4LjkzVjIzLjE2aDEydjYuNjdsLjQyLjI5YzQuNTgtNi42NSwxMS4xNC04LDE4LjY0LTcuNFYzMy44OWMtMi40My4xNS00Ljg0LjE1LTcuMjEuNDctNS40Mi43Mi04Ljg4LDQtMTAuNTEsOWEyNC43LDI0LjcsMCwwLDAtMS4wNiw3LjMyYy0uMTEsMTAuNzItLjA1LDIxLjQ0LS4wNSwzMi4xN1oiLz48L2c+PC9nPjwvc3ZnPg==";
var LyftLogo = "data:image/svg+xml;base64,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";
var BunnyIcon = "data:image/svg+xml;base64,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";
var PARTNERS = [
  { value: "", label: "AUTO" },
  {
    value: "lyft",
    label: "Lyft",
    logo: LyftLogo,
    logoFilter: "invert(18%) sepia(92%) saturate(4954%) hue-rotate(316deg) brightness(91%) contrast(108%)",
    logoFilterSelected: "brightness(0) invert(1)"
  },
  {
    value: "uber",
    label: "Uber",
    logo: UberLogo,
    logoFilter: "brightness(0) saturate(100%)",
    logoFilterSelected: "brightness(0) invert(1)"
  },
  {
    value: "shuttle",
    label: "HopDrive",
    logo: BunnyIcon,
    logoFilter: "invert(42%) sepia(92%) saturate(2945%) hue-rotate(346deg) brightness(102%) contrast(101%)",
    logoFilterSelected: "brightness(0) invert(1)",
    disabled: true
  }
];
function createPartnerButton(partner) {
  return function PartnerButton({ selected, onClick, disabled: btnDisabled }) {
    const theme = useTheme2();
    const isDisabled = btnDisabled || partner.disabled;
    return /* @__PURE__ */ jsx19(
      ButtonBase2,
      {
        onClick,
        disabled: isDisabled,
        sx: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          p: 3,
          border: selected ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          backgroundColor: selected ? theme.palette.primary.main : "#f0f0f0",
          color: selected ? "#fff" : theme.palette.text.primary,
          transition: "0.15s",
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          minHeight: 120,
          "&:hover:not(:disabled)": {
            backgroundColor: selected ? theme.palette.primary.dark : "#e0e0e0"
          }
        },
        children: partner.logo ? /* @__PURE__ */ jsx19(
          "img",
          {
            src: partner.logo,
            alt: partner.label,
            style: {
              width: 60,
              height: 60,
              objectFit: "contain",
              filter: selected ? partner.logoFilterSelected : partner.logoFilter
            }
          }
        ) : /* @__PURE__ */ jsx19(Typography13, { sx: { fontSize: 32, fontWeight: 700, letterSpacing: 2 }, children: partner.label })
      }
    );
  };
}
function PreferredRidePartnerRenderer({ value, onChange, disabled }) {
  const items = PARTNERS.map((partner) => ({
    id: partner.value,
    render: createPartnerButton(partner)
  }));
  return /* @__PURE__ */ jsx19(
    BigButtonGroup2,
    {
      items,
      selectedId: value ?? "",
      onSelectionChange: (id) => onChange(id),
      columns: { xs: 3, sm: 3, md: 3 },
      disabled
    }
  );
}

// src/definitions/ride-provider-algorithm.renderer.tsx
import { Box as Box7, Typography as Typography14, CircularProgress as CircularProgress2 } from "@mui/material";
import { BigButtonGroup as BigButtonGroup3 } from "@hopdrive/sdk-ui";
import { jsx as jsx20, jsxs as jsxs16 } from "react/jsx-runtime";
function RideProviderAlgorithmRenderer({
  value,
  onChange,
  disabled,
  enumOptions,
  enumLoading
}) {
  const AlgorithmReference = useOptionalHostServices()?.slots?.AlgorithmReference;
  if (enumLoading) {
    return /* @__PURE__ */ jsxs16(Box7, { sx: { display: "flex", alignItems: "center", gap: 1, py: 2 }, children: [
      /* @__PURE__ */ jsx20(CircularProgress2, { size: 20 }),
      /* @__PURE__ */ jsx20(Typography14, { variant: "body2", color: "text.secondary", children: "Loading algorithms..." })
    ] });
  }
  if (!enumOptions || enumOptions.length === 0) {
    return /* @__PURE__ */ jsx20(Typography14, { variant: "body2", color: "text.secondary", children: "No algorithms available" });
  }
  const items = enumOptions.map((option) => ({
    id: String(option.value),
    render: createSettingsBigButton({
      title: AlgorithmReference ? /* @__PURE__ */ jsx20(AlgorithmReference, { algorithmKey: String(option.value), selection: true }) : option.label,
      subtitle: option.description
    })
  }));
  return /* @__PURE__ */ jsx20(
    BigButtonGroup3,
    {
      items,
      selectedId: value || "",
      onSelectionChange: (id) => onChange(id),
      columns: { xs: 6, sm: 6, md: 6 },
      disabled
    }
  );
}

// src/definitions/payer.renderer.tsx
import { useState as useState12, useEffect as useEffect12 } from "react";
import {
  Box as Box8,
  TextField as TextField12,
  MenuItem as MenuItem6,
  Typography as Typography15,
  CircularProgress as CircularProgress3,
  InputAdornment as InputAdornment7,
  Alert as Alert3
} from "@mui/material";
import { Payment as PaymentIcon } from "@mui/icons-material";
import { jsx as jsx21, jsxs as jsxs17 } from "react/jsx-runtime";
function PayerRenderer({ value, onChange, disabled }) {
  const payerId = value || 0;
  const [selectedId, setSelectedId] = useState12(payerId);
  const { options: customers, loading, error } = useHostServices().dataSources.payers();
  useEffect12(() => {
    setSelectedId(value || 0);
  }, [value]);
  const handleChange = (e) => {
    const newId = Number(e.target.value) || 0;
    setSelectedId(newId);
    onChange(newId || null);
  };
  const selectedCustomer = customers.find((c) => c.id === selectedId);
  const showMissingWarning = selectedId > 0 && !loading && customers.length > 0 && !selectedCustomer;
  if (error) {
    return /* @__PURE__ */ jsxs17(Alert3, { severity: "error", children: [
      "Failed to load customers: ",
      error instanceof Error ? error.message : String(error)
    ] });
  }
  if (loading) {
    return /* @__PURE__ */ jsxs17(Box8, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
      /* @__PURE__ */ jsx21(CircularProgress3, { size: 20 }),
      /* @__PURE__ */ jsx21(Typography15, { variant: "body2", color: "text.secondary", children: "Loading..." })
    ] });
  }
  return /* @__PURE__ */ jsxs17("div", { children: [
    /* @__PURE__ */ jsxs17(
      TextField12,
      {
        select: true,
        label: "Select Payer",
        value: selectedId || "",
        onChange: handleChange,
        variant: "outlined",
        size: "small",
        disabled,
        sx: { width: 300 },
        InputProps: {
          startAdornment: /* @__PURE__ */ jsx21(InputAdornment7, { position: "start", children: /* @__PURE__ */ jsx21(PaymentIcon, { fontSize: "small", color: "disabled" }) })
        },
        children: [
          /* @__PURE__ */ jsx21(MenuItem6, { value: "", children: /* @__PURE__ */ jsx21("em", { children: "None (use entity default)" }) }),
          customers.map((customer) => /* @__PURE__ */ jsxs17(MenuItem6, { value: customer.id, children: [
            customer.label,
            " (ID: ",
            customer.id,
            ")"
          ] }, customer.id))
        ]
      }
    ),
    showMissingWarning && /* @__PURE__ */ jsx21(Alert3, { severity: "warning", sx: { mt: 1 }, children: /* @__PURE__ */ jsxs17(Typography15, { variant: "body2", children: [
      "Payer ID ",
      selectedId,
      " not found \u2014 may be deactivated."
    ] }) }),
    selectedCustomer && /* @__PURE__ */ jsxs17(Typography15, { variant: "body2", color: "text.secondary", sx: { mt: 0.5 }, children: [
      "Selected: ",
      /* @__PURE__ */ jsx21("strong", { children: selectedCustomer.label })
    ] })
  ] });
}

// src/definitions/scheduler.renderer.tsx
import { useMemo as useMemo2 } from "react";
import {
  Autocomplete,
  TextField as TextField13,
  Box as Box9,
  Typography as Typography16,
  CircularProgress as CircularProgress4
} from "@mui/material";
import { Fragment as Fragment2, jsx as jsx22, jsxs as jsxs18 } from "react/jsx-runtime";
import { createElement } from "react";
function buildOptions(distinctFromDb) {
  const seenLower = /* @__PURE__ */ new Map();
  for (const v of KNOWN_SCHEDULER_VALUES) {
    seenLower.set(v.toLowerCase(), v);
  }
  for (const v of distinctFromDb) {
    const key = v.toLowerCase();
    if (!seenLower.has(key)) {
      seenLower.set(key, v);
    }
  }
  return Array.from(seenLower.values()).sort(
    (a, b) => a.localeCompare(b, void 0, { sensitivity: "base" })
  );
}
function SchedulerRenderer({
  value,
  onChange,
  disabled
}) {
  const current = value?.type ?? "";
  const { options: dbValues, loading } = useHostServices().dataSources.schedulerValues();
  const options = useMemo2(() => buildOptions(dbValues), [dbValues]);
  const handleChange = (_event, newValue) => {
    const next = (newValue ?? "").trim();
    onChange({ type: next });
  };
  return /* @__PURE__ */ jsx22(Box9, { sx: { width: 300 }, children: /* @__PURE__ */ jsx22(
    Autocomplete,
    {
      freeSolo: true,
      autoSelect: true,
      disabled,
      loading,
      options,
      value: current || null,
      onChange: handleChange,
      renderInput: (params) => /* @__PURE__ */ jsx22(
        TextField13,
        {
          ...params,
          label: "Scheduler",
          placeholder: loading ? "Loading\u2026" : "Select or type a vendor",
          size: "small",
          variant: "outlined",
          InputProps: {
            ...params.InputProps,
            endAdornment: /* @__PURE__ */ jsxs18(Fragment2, { children: [
              loading ? /* @__PURE__ */ jsx22(CircularProgress4, { color: "inherit", size: 16 }) : null,
              params.InputProps.endAdornment
            ] })
          }
        }
      ),
      renderOption: (props, option) => /* @__PURE__ */ createElement("li", { ...props, key: option }, /* @__PURE__ */ jsxs18(Box9, { sx: { display: "flex", flexDirection: "column" }, children: [
        /* @__PURE__ */ jsx22(Typography16, { variant: "body2", children: option }),
        KNOWN_SCHEDULER_VALUES.includes(option) ? /* @__PURE__ */ jsx22(Typography16, { variant: "caption", color: "text.secondary", children: "Known vendor" }) : null
      ] })),
      noOptionsText: "No known schedulers yet \u2014 type to add a new one"
    }
  ) });
}

// src/definitions/superdispatch-settings.renderer.tsx
import { useState as useState13, useEffect as useEffect13, useCallback as useCallback11 } from "react";
import { Alert as Alert4, Box as Box10, TextField as TextField14, Typography as Typography17, Divider as Divider5, FormControlLabel as FormControlLabel4 } from "@mui/material";
import { StyledSwitch as StyledSwitch8, SubOption as SubOption11, BigButtonGroup as BigButtonGroup4 } from "@hopdrive/sdk-ui";
import { jsx as jsx23, jsxs as jsxs19 } from "react/jsx-runtime";
var AR_OPTIONS = [
  {
    value: "default",
    label: "Leave both prices alone",
    detail: "HopDrive bills its own price, their order keeps theirs, and any difference stays a difference."
  },
  {
    value: "override_hopdrive_price",
    label: "Use the shipper's price",
    detail: "HopDrive's AR price is replaced by the price on their Super Dispatch order, so billing follows what they entered."
  },
  {
    value: "correct_transport_cost",
    label: "Correct their total",
    detail: "HopDrive bills its own price and posts a signed adjustment line so the shipper's total matches."
  }
];
var CANCEL_OPTIONS = [
  {
    value: "notify_only",
    label: "Post to Slack, leave the order alone",
    detail: "Their order is untouched and the team decides whether to cancel it or reschedule."
  },
  {
    value: "auto_cancel",
    label: "Cancel it in Super Dispatch",
    detail: "The order is cancelled automatically, with HopDrive's reason mapped to theirs. No human in the loop."
  }
];
var CODES = [
  { code: "fuel", becomes: "Fuel" },
  { code: "toll", becomes: "Toll" },
  { code: "tow", becomes: "Pulling fee" },
  { code: "delay", becomes: "Late fee" },
  { code: "partial", becomes: "Dry run fee" },
  { code: "roadside", becomes: 'Other, labelled "roadside"' },
  { code: "surge", becomes: 'Other, labelled "surge"' },
  { code: "insurance", becomes: 'Other, labelled "insurance"' },
  { code: "platform", becomes: 'Other, labelled "platform"' }
];
function SuperdispatchSettingsRenderer({ value, onChange, disabled }) {
  const settings = value || {};
  const host = useOptionalHostServices();
  const atOrganization = host?.entity?.level === "organization";
  const [slackChannel, setSlackChannel] = useState13(settings.slackChannel ?? "");
  const [invoiceEmail, setInvoiceEmail] = useState13(settings.invoiceEmail ?? "");
  useEffect13(() => {
    const next = value || {};
    setSlackChannel(next.slackChannel ?? "");
    setInvoiceEmail(next.invoiceEmail ?? "");
  }, [value]);
  const commit = useCallback11(
    (updates) => {
      onChange({ ...settings, ...updates });
    },
    [settings, onChange]
  );
  const toggle = (field) => (e) => {
    commit({ [field]: e.target.checked });
  };
  const activeCodes = settings.allowedAccessorialCodes ?? [];
  const needsInvoiceEmail = activeCodes.length > 0 || settings.arBehavior === "correct_transport_cost";
  const toggleCode = (code) => {
    const next = activeCodes.includes(code) ? activeCodes.filter((c) => c !== code) : [...activeCodes, code];
    commit({ allowedAccessorialCodes: next });
  };
  const codeRows = [
    ...CODES,
    ...activeCodes.filter((c) => !CODES.some((k) => k.code === c)).map((code) => ({ code, becomes: 'Other, labelled "' + code + '"' }))
  ];
  return /* @__PURE__ */ jsxs19(Box10, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    atOrganization && /* @__PURE__ */ jsxs19(Alert4, { severity: "info", variant: "outlined", children: [
      "These settings apply to every customer in this organization, and a customer only needs its own row to diverge from them. ",
      /* @__PURE__ */ jsx23("strong", { children: "Webhook subscriptions are the exception" }),
      " \u2014 they live on the customer, one row per rooftop, and there is no organization-level equivalent. Set ",
      /* @__PURE__ */ jsx23("strong", { children: "Super Dispatch Subscription" }),
      " on each rooftop that should receive events, or nothing below will ever reach it."
    ] }),
    /* @__PURE__ */ jsx23(Typography17, { variant: "subtitle2", color: "text.secondary", children: "Photos" }),
    /* @__PURE__ */ jsx23(
      SubOption11,
      {
        inline: true,
        title: "Send Inspection Photos",
        description: "Sends HopDrive inspection photos to the Super Dispatch vehicle \u2014 the four corner shots the standard workflows map. This delays the shipper's pickup and delivery notification, because Super Dispatch closes each inspection on the status change so the photos must go first (usually seconds, up to ten minutes on a bad connection).",
        children: /* @__PURE__ */ jsx23(StyledSwitch8, { checked: settings.syncVehiclePhotos ?? false, onChange: toggle("syncVehiclePhotos"), disabled })
      }
    ),
    /* @__PURE__ */ jsx23(
      SubOption11,
      {
        inline: true,
        title: "Send Damage Photos",
        description: "Sends damage-report photos with a damage record attached to each. Independent of inspection photos. Best-effort: any photo still uploading when the status change goes through is skipped, because Super Dispatch will not accept it afterwards.",
        children: /* @__PURE__ */ jsx23(StyledSwitch8, { checked: settings.syncDamagePhotos ?? false, onChange: toggle("syncDamagePhotos"), disabled })
      }
    ),
    /* @__PURE__ */ jsx23(Divider5, {}),
    /* @__PURE__ */ jsx23(Typography17, { variant: "subtitle2", color: "text.secondary", children: "Pricing" }),
    /* @__PURE__ */ jsxs19(Box10, { children: [
      /* @__PURE__ */ jsx23(Typography17, { variant: "body2", sx: { fontWeight: 500 }, children: "AR Behaviour" }),
      /* @__PURE__ */ jsx23(Typography17, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "How HopDrive's price and the shipper's order price are reconciled." }),
      /* @__PURE__ */ jsx23(
        BigButtonGroup4,
        {
          items: AR_OPTIONS.map((opt) => ({
            id: opt.value,
            render: createSettingsBigButton({ title: opt.label, subtitle: opt.detail })
          })),
          selectedId: settings.arBehavior ?? "default",
          onSelectionChange: (id) => commit({ arBehavior: id }),
          columns: { xs: 12, sm: 4, md: 4 },
          disabled
        }
      )
    ] }),
    /* @__PURE__ */ jsx23(Divider5, {}),
    /* @__PURE__ */ jsx23(Typography17, { variant: "subtitle2", color: "text.secondary", children: "Cancellations" }),
    /* @__PURE__ */ jsxs19(Box10, { children: [
      /* @__PURE__ */ jsx23(Typography17, { variant: "body2", sx: { fontWeight: 500 }, children: "Cancel Behaviour" }),
      /* @__PURE__ */ jsx23(Typography17, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "What happens to the shipper's order when HopDrive cancels a move." }),
      /* @__PURE__ */ jsx23(
        BigButtonGroup4,
        {
          items: CANCEL_OPTIONS.map((opt) => ({
            id: opt.value,
            render: createSettingsBigButton({ title: opt.label, subtitle: opt.detail })
          })),
          selectedId: settings.cancelBehavior ?? "notify_only",
          onSelectionChange: (id) => commit({ cancelBehavior: id }),
          columns: { xs: 12, sm: 6, md: 6 },
          disabled
        }
      )
    ] }),
    /* @__PURE__ */ jsx23(Divider5, {}),
    /* @__PURE__ */ jsx23(Typography17, { variant: "subtitle2", color: "text.secondary", children: "Charges on the shipper's order" }),
    /* @__PURE__ */ jsxs19(Box10, { children: [
      /* @__PURE__ */ jsx23(Typography17, { variant: "body2", sx: { fontWeight: 500 }, children: "Accessorial Charges Synced" }),
      /* @__PURE__ */ jsx23(Typography17, { variant: "body2", color: "text.secondary", sx: { mb: 1.5 }, children: "Which HopDrive accessorial charges are posted to the Super Dispatch order once the move settles. The label under each is what the shipper sees on their order. All off means no charges are sent." }),
      /* @__PURE__ */ jsxs19(Typography17, { variant: "body2", color: "text.secondary", sx: { mb: 1.5 }, children: [
        "Turning any of these on also ",
        /* @__PURE__ */ jsx23("strong", { children: "submits the Super Dispatch invoice for each load" }),
        " \u2014 an expense is not shown to the shipper until the load is invoiced, so charges without an invoice reach nobody. Every settled load is invoiced, including loads with no charges, so their board is never half invoiced."
      ] }),
      /* @__PURE__ */ jsx23(
        Box10,
        {
          sx: {
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 3
          },
          children: codeRows.map(({ code, becomes }) => /* @__PURE__ */ jsx23(
            FormControlLabel4,
            {
              sx: { m: 0, justifyContent: "space-between", ml: 0 },
              labelPlacement: "start",
              control: /* @__PURE__ */ jsx23(
                StyledSwitch8,
                {
                  checked: activeCodes.includes(code),
                  onChange: () => toggleCode(code),
                  disabled
                }
              ),
              label: /* @__PURE__ */ jsxs19(Box10, { sx: { flex: 1, py: 0.5 }, children: [
                /* @__PURE__ */ jsx23(Typography17, { fontSize: 13, sx: { textTransform: "capitalize" }, children: code }),
                /* @__PURE__ */ jsx23(Typography17, { fontSize: 11, color: "text.secondary", children: becomes })
              ] })
            },
            code
          ))
        }
      )
    ] }),
    /* @__PURE__ */ jsx23(
      SubOption11,
      {
        inline: true,
        title: "Invoice Email",
        description: "Where Super Dispatch sends its copy of the invoice. Required once any charge above is on, because the invoice is what makes those charges visible and it has to be addressed somewhere. With no address set nothing is submitted, the charges stay invisible, and a Slack alert is raised instead of guessing at a recipient. Not validated against the shipper, so this is ours to choose.",
        children: /* @__PURE__ */ jsx23(
          TextField14,
          {
            value: invoiceEmail,
            onChange: (e) => setInvoiceEmail(e.target.value),
            onBlur: () => invoiceEmail !== (settings.invoiceEmail ?? "") && commit({ invoiceEmail }),
            disabled,
            size: "small",
            placeholder: "billing@hopdrive.com",
            error: needsInvoiceEmail && !settings.invoiceEmail?.trim(),
            helperText: needsInvoiceEmail && !settings.invoiceEmail?.trim() ? "Required \u2014 charges are synced but nothing can be invoiced" : void 0,
            sx: { width: 240 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx23(Divider5, {}),
    /* @__PURE__ */ jsx23(Typography17, { variant: "subtitle2", color: "text.secondary", children: "Alerting" }),
    /* @__PURE__ */ jsx23(
      SubOption11,
      {
        inline: true,
        title: "Slack Channel",
        description: "Where this organization's Super Dispatch failure alerts go, without the leading #. The alert bot must be invited to the channel or the messages will never post. Leave blank for the integration default.",
        children: /* @__PURE__ */ jsx23(
          TextField14,
          {
            value: slackChannel,
            onChange: (e) => setSlackChannel(e.target.value),
            onBlur: () => slackChannel !== (settings.slackChannel ?? "") && commit({ slackChannel }),
            disabled,
            size: "small",
            placeholder: "merchants-fleet",
            sx: { width: 240 }
          }
        )
      }
    )
  ] });
}

// src/definitions/superdispatch-routing.renderer.tsx
import { useState as useState14, useEffect as useEffect14, useCallback as useCallback12 } from "react";
import { Box as Box11, TextField as TextField15, Typography as Typography18, Divider as Divider6, Alert as Alert5 } from "@mui/material";
import { StyledSwitch as StyledSwitch9, SubOption as SubOption12 } from "@hopdrive/sdk-ui";
import { jsx as jsx24, jsxs as jsxs20 } from "react/jsx-runtime";
function SuperdispatchRoutingRenderer({ value, onChange, disabled }) {
  const routing = value || {};
  const [prefix, setPrefix] = useState14(routing.prefix ?? "");
  useEffect14(() => {
    setPrefix((value || {}).prefix ?? "");
  }, [value]);
  const commit = useCallback12(
    (updates) => {
      onChange({ ...routing, ...updates });
    },
    [routing, onChange]
  );
  const toggle = (field) => (e) => {
    commit({ [field]: e.target.checked });
  };
  return /* @__PURE__ */ jsxs20(Box11, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    /* @__PURE__ */ jsx24(
      SubOption12,
      {
        inline: true,
        title: "Super Dispatch Enabled",
        description: "Master switch for this organization. When off, HopDrive stops accepting their Super Dispatch offers, so no new moves are created. Moves already in flight keep syncing.",
        children: /* @__PURE__ */ jsx24(StyledSwitch9, { checked: routing.enabled ?? false, onChange: toggle("enabled"), disabled })
      }
    ),
    /* @__PURE__ */ jsx24(Divider6, {}),
    /* @__PURE__ */ jsx24(
      SubOption12,
      {
        inline: true,
        title: "Order Number Prefix",
        description: "Super Dispatch order numbers starting with this belong to this organization \u2014 TS for Merchants Fleet, HC for Hertz. The longest match wins, so HCL beats HC. An order matching no organization falls back to a default set in the integration's code.",
        children: /* @__PURE__ */ jsx24(
          TextField15,
          {
            value: prefix,
            onChange: (e) => setPrefix(e.target.value.toUpperCase().trim()),
            onBlur: () => prefix !== (routing.prefix ?? "") && commit({ prefix }),
            disabled,
            size: "small",
            placeholder: "TS",
            sx: { width: 140 }
          }
        )
      }
    ),
    !prefix && /* @__PURE__ */ jsx24(Alert5, { severity: "warning", children: "Without a prefix, no Super Dispatch order will route to this organization." }),
    /* @__PURE__ */ jsx24(Divider6, {}),
    /* @__PURE__ */ jsx24(Typography18, { variant: "subtitle2", color: "text.secondary", children: "Offers" }),
    /* @__PURE__ */ jsx24(
      SubOption12,
      {
        inline: true,
        title: "Auto-Accept Offers",
        description: "Accept load offers matching this prefix automatically instead of leaving them for a human. Never applies to an order that only reached this organization through the code-level fallback \u2014 auto-accept always requires an explicit prefix match.",
        children: /* @__PURE__ */ jsx24(StyledSwitch9, { checked: routing.autoAcceptOffer ?? false, onChange: toggle("autoAcceptOffer"), disabled })
      }
    ),
    /* @__PURE__ */ jsx24(
      SubOption12,
      {
        inline: true,
        title: "Accept Placeholder-Vehicle Offers",
        description: 'Accept offers whose vehicle details are still placeholders (make and model literally "make" and "model"). With this off, such an offer is NOT accepted now and is NOT accepted later either \u2014 HopDrive never revisits it once the real vehicle details arrive. It waits for a human to accept it by hand.',
        children: /* @__PURE__ */ jsx24(StyledSwitch9, { checked: routing.ghostMoveOffer ?? false, onChange: toggle("ghostMoveOffer"), disabled })
      }
    )
  ] });
}

// src/definitions/superdispatch-subscription.renderer.tsx
import { Box as Box13, Typography as Typography20, Alert as Alert7 } from "@mui/material";

// src/renderers/shared/subscriptionPanel.tsx
import { useState as useState15, useCallback as useCallback13 } from "react";
import { Box as Box12, Typography as Typography19, Chip as Chip3, Alert as Alert6, Button as Button3, CircularProgress as CircularProgress5, Tooltip as Tooltip2 } from "@mui/material";
import { Fragment as Fragment3, jsx as jsx25, jsxs as jsxs21 } from "react/jsx-runtime";
var subscribedEvents = (row) => Object.entries(row.eventTypes ?? {}).filter(([, on]) => on).map(([key]) => key);
var isLive = (row) => row.active === 1 && row.status === "enabled";
var verdictFor = (ours, expectedEvents, integrationLabel) => {
  if (!ours) {
    return {
      severity: "info",
      headline: "Not subscribed",
      detail: `${integrationLabel} is told nothing about this customer's moves \u2014 no status changes, photos or cancellations.`
    };
  }
  if (!isLive(ours)) {
    return {
      severity: "info",
      headline: "Subscription disabled",
      detail: `Row ${ours.id} exists but is switched off, so nothing is delivered.`
    };
  }
  if (!ours.hasSecret) {
    return {
      severity: "error",
      headline: "Subscribed, but it cannot deliver",
      detail: "Deliveries are signed before they are sent, so a row with no secret fails every attempt and then gives up silently. Re-apply to generate one."
    };
  }
  const have = new Set(subscribedEvents(ours));
  const missing = expectedEvents.filter((e) => !have.has(e));
  const extra = [...have].filter((e) => !expectedEvents.includes(e));
  if (missing.length || extra.length) {
    return {
      severity: "warning",
      headline: "Subscribed, but not to the right events",
      detail: [
        missing.length ? `Missing: ${missing.join(", ")} \u2014 those events never arrive.` : "",
        extra.length ? `Extra: ${extra.join(", ")} \u2014 delivered but ignored.` : ""
      ].filter(Boolean).join(" ")
    };
  }
  return {
    severity: "success",
    headline: "Subscribed",
    detail: `Row ${ours.id}, ${expectedEvents.length} events, signed.`
  };
};
function SubscriptionPanel({
  integrationLabel,
  targetUrl,
  expectedEvents,
  rows,
  loading,
  disabled,
  onApply,
  onDisable
}) {
  const [busy, setBusy] = useState15(null);
  const ours = rows.find((r) => r.targetUrl === targetUrl && isLive(r)) ?? rows.find((r) => r.targetUrl === targetUrl);
  const others = rows.filter((r) => r.targetUrl !== targetUrl);
  const verdict = verdictFor(ours, expectedEvents, integrationLabel);
  const live = !!ours && isLive(ours);
  const run = async (which, action) => {
    setBusy(which);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };
  return /* @__PURE__ */ jsxs21(Box12, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: [
    /* @__PURE__ */ jsxs21(Box12, { children: [
      /* @__PURE__ */ jsx25(Typography19, { variant: "body2", sx: { fontWeight: 500 }, children: "Webhook Subscription" }),
      /* @__PURE__ */ jsxs21(Typography19, { variant: "body2", color: "text.secondary", children: [
        "Read live from the platform, not from the settings above \u2014 this is what will actually be delivered. Re-apply rebuilds it to match what ",
        integrationLabel,
        " needs, and never touches subscriptions pointing anywhere else."
      ] })
    ] }),
    loading ? /* @__PURE__ */ jsxs21(Box12, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
      /* @__PURE__ */ jsx25(CircularProgress5, { size: 14 }),
      /* @__PURE__ */ jsx25(Typography19, { variant: "body2", color: "text.secondary", children: "Checking subscriptions\u2026" })
    ] }) : /* @__PURE__ */ jsxs21(Fragment3, { children: [
      /* @__PURE__ */ jsxs21(Alert6, { severity: verdict.severity, children: [
        /* @__PURE__ */ jsx25("strong", { children: verdict.headline }),
        verdict.detail ? ` \u2014 ${verdict.detail}` : ""
      ] }),
      ours && /* @__PURE__ */ jsxs21(Box12, { sx: { pl: 0.5, display: "flex", flexDirection: "column", gap: 0.75 }, children: [
        /* @__PURE__ */ jsx25(Typography19, { fontSize: 12, color: "text.secondary", sx: { wordBreak: "break-all" }, children: ours.targetUrl }),
        /* @__PURE__ */ jsxs21(Box12, { sx: { display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }, children: [
          subscribedEvents(ours).map((event) => /* @__PURE__ */ jsx25(
            Chip3,
            {
              label: event.replace(/_/g, " "),
              size: "small",
              variant: "outlined",
              color: expectedEvents.includes(event) ? "default" : "warning"
            },
            event
          )),
          expectedEvents.filter((e) => !subscribedEvents(ours).includes(e)).map((event) => /* @__PURE__ */ jsx25(Tooltip2, { title: "Required by the integration but not subscribed", children: /* @__PURE__ */ jsx25(Chip3, { label: event.replace(/_/g, " "), size: "small", variant: "outlined", color: "error" }) }, event))
        ] })
      ] }),
      /* @__PURE__ */ jsxs21(Box12, { sx: { display: "flex", gap: 1 }, children: [
        /* @__PURE__ */ jsx25(
          Button3,
          {
            size: "small",
            variant: live ? "outlined" : "contained",
            onClick: () => run("apply", onApply),
            disabled: disabled || busy !== null,
            children: busy === "apply" ? "Working\u2026" : live ? "Re-apply" : "Enable subscription"
          }
        ),
        live && /* @__PURE__ */ jsx25(
          Button3,
          {
            size: "small",
            variant: "outlined",
            color: "warning",
            onClick: () => run("disable", onDisable),
            disabled: disabled || busy !== null,
            children: busy === "disable" ? "Working\u2026" : "Disable subscription"
          }
        )
      ] }),
      others.length > 0 && /* @__PURE__ */ jsxs21(Box12, { sx: { mt: 0.5 }, children: [
        /* @__PURE__ */ jsxs21(Typography19, { fontSize: 12, color: "text.secondary", children: [
          "This customer also has ",
          others.length,
          " subscription",
          others.length > 1 ? "s" : "",
          " to other endpoints. Left untouched:"
        ] }),
        others.map((row) => /* @__PURE__ */ jsxs21(Typography19, { fontSize: 11, color: "text.disabled", sx: { wordBreak: "break-all" }, children: [
          isLive(row) ? "" : "(disabled) ",
          row.targetUrl
        ] }, row.id))
      ] })
    ] })
  ] });
}
function SubscriptionSection({
  subscriptions,
  customerId,
  integrationLabel,
  targetUrl,
  expectedEvents,
  disabled
}) {
  const { rows, loading, refetch } = subscriptions.forCustomer(customerId);
  const onApply = useCallback13(async () => {
    await subscriptions.apply({ customerId, targetUrl, events: [...expectedEvents] });
    refetch();
  }, [subscriptions, customerId, targetUrl, expectedEvents, refetch]);
  const onDisable = useCallback13(async () => {
    await subscriptions.disable({ customerId, targetUrl });
    refetch();
  }, [subscriptions, customerId, targetUrl, refetch]);
  return /* @__PURE__ */ jsx25(
    SubscriptionPanel,
    {
      customerId,
      integrationLabel,
      targetUrl,
      expectedEvents,
      rows,
      loading,
      disabled,
      onApply,
      onDisable
    }
  );
}

// src/definitions/superdispatch-subscription.renderer.tsx
import { jsx as jsx26, jsxs as jsxs22 } from "react/jsx-runtime";
function SuperdispatchSubscriptionRenderer({ disabled }) {
  const host = useOptionalHostServices();
  const customerId = host?.entity.level === "customer" ? host?.entity.entityId ?? null : null;
  const subscriptionsPort = host?.subscriptions;
  const targetUrl = host?.appEnv === "prod" ? SUPERDISPATCH_SUBSCRIPTION.targetUrl.prod : SUPERDISPATCH_SUBSCRIPTION.targetUrl.test;
  if (!subscriptionsPort || !customerId) {
    return /* @__PURE__ */ jsx26(Alert7, { severity: "info", children: "Subscriptions are managed per customer. Select a customer to see and change what Super Dispatch receives." });
  }
  return /* @__PURE__ */ jsxs22(Box13, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    /* @__PURE__ */ jsx26(Alert7, { severity: "info", icon: false, children: /* @__PURE__ */ jsxs22(Typography20, { variant: "body2", children: [
      "Changes here take effect ",
      /* @__PURE__ */ jsx26("strong", { children: "immediately" }),
      " \u2014 there is nothing to save. Removing the subscription is what makes a rooftop go quiet: Super Dispatch Sync controls the charges and photos we send, but pickup, delivery and cancellation updates are reported regardless while a subscription exists."
    ] }) }),
    /* @__PURE__ */ jsx26(
      SubscriptionSection,
      {
        subscriptions: subscriptionsPort,
        customerId,
        integrationLabel: "Super Dispatch",
        targetUrl,
        expectedEvents: SUPERDISPATCH_SUBSCRIPTION.events,
        disabled
      }
    )
  ] });
}

// src/definitions/carsarrive-routing.renderer.tsx
import { useCallback as useCallback14 } from "react";
import { Box as Box14, Alert as Alert8 } from "@mui/material";
import { StyledSwitch as StyledSwitch10, SubOption as SubOption13 } from "@hopdrive/sdk-ui";
import { jsx as jsx27, jsxs as jsxs23 } from "react/jsx-runtime";
function CarsarriveRoutingRenderer({ value, onChange, disabled }) {
  const routing = value || {};
  const commit = useCallback14(
    (updates) => {
      onChange({ ...routing, ...updates });
    },
    [routing, onChange]
  );
  const enabled = routing.enabled ?? false;
  return /* @__PURE__ */ jsxs23(Box14, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    /* @__PURE__ */ jsx27(
      SubOption13,
      {
        inline: true,
        title: "CarsArrive Enabled",
        description: "Master switch for this organization. When off, an assigned CarsArrive load is rejected back to them rather than becoming a move. Moves already in flight keep reporting their status, photos and invoice. Quoting is unaffected \u2014 a quote request is still priced, because refusing to price one looks like an outage rather than a decision.",
        children: /* @__PURE__ */ jsx27(
          StyledSwitch10,
          {
            checked: enabled,
            onChange: (e) => commit({ enabled: e.target.checked }),
            disabled
          }
        )
      }
    ),
    !enabled && /* @__PURE__ */ jsx27(Alert8, { severity: "warning", children: "No new CarsArrive load will be accepted for this organization. Individual rooftops cannot override this." })
  ] });
}

// src/definitions/carsarrive-settings.renderer.tsx
import { useState as useState16, useEffect as useEffect15, useCallback as useCallback15 } from "react";
import { Box as Box15, TextField as TextField16, Typography as Typography21, Divider as Divider7, MenuItem as MenuItem7, Select, Alert as Alert9 } from "@mui/material";
import { StyledSwitch as StyledSwitch11, SubOption as SubOption14, BigButtonGroup as BigButtonGroup5 } from "@hopdrive/sdk-ui";
import { jsx as jsx28, jsxs as jsxs24 } from "react/jsx-runtime";
var CANCEL_OPTIONS2 = [
  {
    value: "auto_cancel",
    label: "Cancel it in CarsArrive",
    detail: "The load is rejected back to CarsArrive automatically as soon as the move is cancelled."
  },
  {
    value: "notify_only",
    label: "Post to Slack, leave the load alone",
    detail: "CarsArrive keeps believing the vehicle is being moved until somebody acts on the message."
  }
];
var BUCKET_LABELS = {
  standardTransport: "Standard transport",
  tolls: "Tolls",
  fuel: "Fuel",
  other: "Other"
};
var UNMAPPED = "__unmapped__";
var KNOWN_NAMES = [
  "transport",
  "toll",
  "fuel",
  "delay",
  "surge",
  "tow",
  "roadside",
  "partial",
  "insurance",
  "platform"
];
var DEFAULT_MAPPING = {
  standardTransport: [...CARSARRIVE_DEFAULT_INVOICE_MAPPING.standardTransport],
  tolls: [...CARSARRIVE_DEFAULT_INVOICE_MAPPING.tolls],
  fuel: [...CARSARRIVE_DEFAULT_INVOICE_MAPPING.fuel],
  other: [...CARSARRIVE_DEFAULT_INVOICE_MAPPING.other]
};
var bucketByName = (mapping) => {
  const out = {};
  for (const bucket of CARSARRIVE_INVOICE_BUCKETS) {
    for (const name of mapping[bucket] ?? []) {
      const key = name.trim().toLowerCase();
      if (key && !(key in out)) out[key] = bucket;
    }
  }
  return out;
};
function CarsarriveSettingsRenderer({ value, onChange, disabled }) {
  const settings = value || {};
  const [slackChannel, setSlackChannel] = useState16(settings.slackChannel ?? "");
  useEffect15(() => {
    const next = value || {};
    setSlackChannel(next.slackChannel ?? "");
  }, [value]);
  const commit = useCallback15(
    (updates) => {
      onChange({ ...settings, ...updates });
    },
    [settings, onChange]
  );
  const mapping = settings.invoiceMapping ?? DEFAULT_MAPPING;
  const assigned = bucketByName(mapping);
  const rows = [...KNOWN_NAMES, ...Object.keys(assigned).filter((n) => !KNOWN_NAMES.includes(n))];
  const setBucket = (name, bucket) => {
    const next = {};
    for (const b of CARSARRIVE_INVOICE_BUCKETS) {
      next[b] = (mapping[b] ?? []).filter((n) => n.trim().toLowerCase() !== name);
    }
    if (bucket !== UNMAPPED) next[bucket] = [...next[bucket] ?? [], name];
    commit({ invoiceMapping: next });
  };
  const unmappedCount = rows.filter((n) => !assigned[n]).length;
  return /* @__PURE__ */ jsxs24(Box15, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    /* @__PURE__ */ jsx28(Typography21, { variant: "subtitle2", color: "text.secondary", children: "Photos" }),
    /* @__PURE__ */ jsx28(
      SubOption14,
      {
        inline: true,
        title: "Send Inspection Photos",
        description: "Waits for the move's inspection photos before reporting pickup and delivery, so the photos actually reach the load. This delays the notification CarsArrive receives \u2014 usually seconds, up to ten minutes on a bad connection. With this off the callback goes out immediately and carries only the photos that happened to have finished uploading, which on a fast move is usually none.",
        children: /* @__PURE__ */ jsx28(
          StyledSwitch11,
          {
            checked: settings.syncVehiclePhotos ?? false,
            onChange: (e) => commit({ syncVehiclePhotos: e.target.checked }),
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx28(Divider7, {}),
    /* @__PURE__ */ jsx28(Typography21, { variant: "subtitle2", color: "text.secondary", children: "Cancellations" }),
    /* @__PURE__ */ jsxs24(Box15, { children: [
      /* @__PURE__ */ jsx28(Typography21, { variant: "body2", sx: { fontWeight: 500 }, children: "Cancel Behaviour" }),
      /* @__PURE__ */ jsx28(Typography21, { variant: "body2", color: "text.secondary", sx: { mb: 1 }, children: "What happens to the CarsArrive load when HopDrive cancels a move." }),
      /* @__PURE__ */ jsx28(
        BigButtonGroup5,
        {
          items: CANCEL_OPTIONS2.map((opt) => ({
            id: opt.value,
            render: createSettingsBigButton({ title: opt.label, subtitle: opt.detail })
          })),
          selectedId: settings.cancelBehavior ?? "auto_cancel",
          onSelectionChange: (id) => commit({ cancelBehavior: id }),
          columns: { xs: 12, sm: 6, md: 6 },
          disabled
        }
      )
    ] }),
    /* @__PURE__ */ jsx28(Divider7, {}),
    /* @__PURE__ */ jsx28(Typography21, { variant: "subtitle2", color: "text.secondary", children: "Invoice" }),
    /* @__PURE__ */ jsxs24(Box15, { children: [
      /* @__PURE__ */ jsx28(Typography21, { variant: "body2", sx: { fontWeight: 500 }, children: "Charge Mapping" }),
      /* @__PURE__ */ jsx28(Typography21, { variant: "body2", color: "text.secondary", sx: { mb: 1.5 }, children: "CarsArrive's invoice has four buckets, so every HopDrive charge has to land in one of them. Matched case-insensitively against the accounts-receivable line names on the move." }),
      /* @__PURE__ */ jsx28(Box15, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 3, rowGap: 1 }, children: rows.map((name) => /* @__PURE__ */ jsxs24(Box15, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [
        /* @__PURE__ */ jsx28(Typography21, { fontSize: 13, sx: { textTransform: "capitalize" }, children: name }),
        /* @__PURE__ */ jsxs24(
          Select,
          {
            value: assigned[name] ?? UNMAPPED,
            onChange: (e) => setBucket(name, e.target.value),
            disabled,
            size: "small",
            sx: { width: 190 },
            children: [
              CARSARRIVE_INVOICE_BUCKETS.map((bucket) => /* @__PURE__ */ jsx28(MenuItem7, { value: bucket, children: BUCKET_LABELS[bucket] }, bucket)),
              /* @__PURE__ */ jsx28(MenuItem7, { value: UNMAPPED, children: "Not invoiced" })
            ]
          }
        )
      ] }, name)) }),
      unmappedCount > 0 && /* @__PURE__ */ jsxs24(Alert9, { severity: "info", sx: { mt: 1.5 }, children: [
        unmappedCount,
        " charge ",
        unmappedCount === 1 ? "type is" : "types are",
        " not invoiced to CarsArrive. HopDrive still bills ",
        unmappedCount === 1 ? "it" : "them",
        " \u2014 ",
        unmappedCount === 1 ? "it" : "they",
        " just will not appear on the customer's invoice, so this is a silent gap rather than an error if it was not deliberate."
      ] })
    ] }),
    /* @__PURE__ */ jsx28(Divider7, {}),
    /* @__PURE__ */ jsx28(Typography21, { variant: "subtitle2", color: "text.secondary", children: "Alerting" }),
    /* @__PURE__ */ jsx28(
      SubOption14,
      {
        inline: true,
        title: "Slack Channel",
        description: "Where this customer's CarsArrive failure alerts go, without the leading #. The alert bot must be invited to the channel or the messages will never post. Leave blank for the integration default.",
        children: /* @__PURE__ */ jsx28(
          TextField16,
          {
            value: slackChannel,
            onChange: (e) => setSlackChannel(e.target.value),
            onBlur: () => slackChannel !== (settings.slackChannel ?? "") && commit({ slackChannel }),
            disabled,
            size: "small",
            placeholder: "carsarrive-alerts",
            sx: { width: 240 }
          }
        )
      }
    )
  ] });
}

// src/definitions/carsarrive-connection.renderer.tsx
import { Box as Box16, Typography as Typography22, Alert as Alert10, AlertTitle } from "@mui/material";
import { Fragment as Fragment4, jsx as jsx29, jsxs as jsxs25 } from "react/jsx-runtime";
function CarsarriveConnectionRenderer({ disabled }) {
  const host = useOptionalHostServices();
  const customerId = host?.entity.level === "customer" ? host?.entity.entityId ?? null : null;
  const subscriptionsPort = host?.subscriptions;
  const targetUrl = host?.appEnv === "prod" ? CARSARRIVE_SUBSCRIPTION.targetUrl.prod : CARSARRIVE_SUBSCRIPTION.targetUrl.test;
  return /* @__PURE__ */ jsxs25(Box16, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: [
    /* @__PURE__ */ jsxs25(Alert10, { severity: "warning", children: [
      /* @__PURE__ */ jsx29(AlertTitle, { children: "CarsArrive has to add this customer to the API key" }),
      /* @__PURE__ */ jsx29(Typography22, { variant: "body2", sx: { mb: 1 }, children: "HopDrive authenticates to CarsArrive with a single API key, and CarsArrive scopes that key to the customers it is allowed to act for. A rooftop that has not been added to it is not reachable, no matter what is configured here." }),
      /* @__PURE__ */ jsxs25(Typography22, { variant: "body2", sx: { mb: 1 }, children: [
        /* @__PURE__ */ jsx29("strong", { children: "This is a manual request to CarsArrive and cannot be done from HopDrive." }),
        " Send them the rooftop's details and confirm they have added it before treating this customer as live."
      ] }),
      /* @__PURE__ */ jsx29(Typography22, { variant: "body2", children: "If it is skipped, the failure is immediate and total: their load is rejected at creation and no move is ever made. That is a different symptom from a missing subscription below, where moves are created and driven normally and only the updates back to CarsArrive go missing." })
    ] }),
    !subscriptionsPort || !customerId ? /* @__PURE__ */ jsx29(Alert10, { severity: "info", children: "Subscriptions are managed per customer. Select a customer to see and change what CarsArrive receives." }) : /* @__PURE__ */ jsxs25(Fragment4, { children: [
      /* @__PURE__ */ jsx29(Alert10, { severity: "info", icon: false, children: /* @__PURE__ */ jsxs25(Typography22, { variant: "body2", children: [
        "Changes below take effect ",
        /* @__PURE__ */ jsx29("strong", { children: "immediately" }),
        " \u2014 there is nothing to save. Removing the subscription does not stop CarsArrive sending loads and does not stop HopDrive driving them; it only stops HopDrive reporting back. To stop taking new work, switch the organization off under CarsArrive routing."
      ] }) }),
      /* @__PURE__ */ jsx29(
        SubscriptionSection,
        {
          subscriptions: subscriptionsPort,
          customerId,
          integrationLabel: "CarsArrive",
          targetUrl,
          expectedEvents: CARSARRIVE_SUBSCRIPTION.events,
          disabled
        }
      )
    ] })
  ] });
}

// src/definitions/hopdrive-contacts.renderer.tsx
import { useState as useState17, useEffect as useEffect16, useCallback as useCallback16 } from "react";
import {
  Box as Box17,
  TextField as TextField17,
  MenuItem as MenuItem8,
  Typography as Typography23,
  Card,
  CardContent,
  CardActions,
  Button as Button4,
  CircularProgress as CircularProgress6
} from "@mui/material";
import { SubOption as SubOption15 } from "@hopdrive/sdk-ui";
import { jsx as jsx30, jsxs as jsxs26 } from "react/jsx-runtime";
function HopdriveContactsRenderer({ value, onChange, disabled }) {
  const { options: adminOptions, loading } = useHostServices().dataSources.admins();
  const [admins, setAdmins] = useState17([]);
  const [marketManager, setMarketManager] = useState17(null);
  const [accountManager, setAccountManager] = useState17(null);
  const [salesRep, setSalesRep] = useState17(null);
  const [arRep, setArRep] = useState17(null);
  useEffect16(() => {
    if (adminOptions.length > 0) {
      const sortedAdmins = [...adminOptions].sort((a, b) => {
        const labelA = (a.sublabel || a.label)?.toLowerCase() || "";
        const labelB = (b.sublabel || b.label)?.toLowerCase() || "";
        return labelA.localeCompare(labelB);
      });
      setAdmins(sortedAdmins);
    }
  }, [adminOptions]);
  useEffect16(() => {
    const cv = value || {};
    if (admins.length > 0) {
      setMarketManager(admins.find((a) => a.id === cv.market_manager) || null);
      setAccountManager(admins.find((a) => a.id === cv.account_manager) || null);
      setSalesRep(admins.find((a) => a.id === cv.sales_rep) || null);
      setArRep(admins.find((a) => a.id === cv.ar_rep) || null);
    }
  }, [admins, value]);
  const updateContacts = useCallback16(
    (updates) => {
      onChange({
        market_manager: Number(marketManager?.id) || 0,
        account_manager: Number(accountManager?.id) || 0,
        sales_rep: Number(salesRep?.id) || 0,
        ar_rep: Number(arRep?.id) || 0,
        ...updates
      });
    },
    [marketManager, accountManager, salesRep, arRep, onChange]
  );
  const handleChange = (field, setter) => (e) => {
    const admin = admins.find((a) => a.id === Number(e.target.value)) || null;
    setter(admin);
    updateContacts({ [field]: Number(admin?.id) || 0 });
  };
  const ContactCard = ({ admin }) => /* @__PURE__ */ jsxs26(Card, { variant: "outlined", sx: { mt: 2 }, children: [
    /* @__PURE__ */ jsxs26(CardContent, { sx: { pb: 0 }, children: [
      /* @__PURE__ */ jsx30(Typography23, { variant: "caption", color: "text.secondary", gutterBottom: true, children: "Contact info as it will appear to customers:" }),
      /* @__PURE__ */ jsxs26(Box17, { sx: { mt: 1.5 }, children: [
        /* @__PURE__ */ jsxs26(Typography23, { variant: "body2", children: [
          /* @__PURE__ */ jsx30(Box17, { component: "span", fontWeight: 600, children: "NAME:" }),
          " ",
          admin.label || "\u2013"
        ] }),
        /* @__PURE__ */ jsxs26(Typography23, { variant: "body2", children: [
          /* @__PURE__ */ jsx30(Box17, { component: "span", fontWeight: 600, children: "EMAIL:" }),
          " ",
          admin.sublabel || "\u2013"
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx30(CardActions, { children: /* @__PURE__ */ jsx30(Button4, { size: "small", color: "error", onClick: () => {
      window.location.href = `/users/${admin.id}`;
    }, children: "Update User Info" }) })
  ] });
  if (loading) {
    return /* @__PURE__ */ jsxs26(Box17, { sx: { display: "flex", alignItems: "center", gap: 1, py: 2 }, children: [
      /* @__PURE__ */ jsx30(CircularProgress6, { size: 20 }),
      /* @__PURE__ */ jsx30(Typography23, { variant: "body2", color: "text.secondary", children: "Loading admin users..." })
    ] });
  }
  const selectProps = (label, selectedAdmin, onChangeHandler) => ({
    select: true,
    label,
    value: selectedAdmin?.id || "",
    onChange: onChangeHandler,
    variant: "outlined",
    size: "small",
    disabled,
    sx: { width: 300 }
  });
  const CONTACTS = [
    {
      field: "account_manager",
      title: "Account Manager",
      description: "The account manager assigned to this customer.",
      label: "Select Account Manager",
      admin: accountManager,
      setter: setAccountManager
    },
    {
      field: "market_manager",
      title: "Market Manager",
      description: "The market manager assigned to the customer's region.",
      label: "Select Market Manager",
      admin: marketManager,
      setter: setMarketManager
    },
    {
      field: "sales_rep",
      title: "Sales Representative",
      description: "The customer's sales representative.",
      label: "Select Sales Representative",
      admin: salesRep,
      setter: setSalesRep
    },
    {
      field: "ar_rep",
      title: "AR Representative",
      description: "The customer's accounts receivable contact.",
      label: "Select AR Representative",
      admin: arRep,
      setter: setArRep
    }
  ];
  return /* @__PURE__ */ jsx30("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: CONTACTS.map(({ field, title, description, label, admin, setter }) => /* @__PURE__ */ jsx30(SubOption15, { inline: true, title, description, children: /* @__PURE__ */ jsxs26("div", { children: [
    /* @__PURE__ */ jsxs26(TextField17, { ...selectProps(label, admin, handleChange(field, setter)), children: [
      /* @__PURE__ */ jsx30(MenuItem8, { value: "", children: "None" }),
      admins.map((a) => /* @__PURE__ */ jsx30(MenuItem8, { value: a.id, children: a.sublabel || a.label }, a.id))
    ] }),
    admin && /* @__PURE__ */ jsx30(ContactCard, { admin })
  ] }) }, field)) });
}

// src/definitions/appointment-management.renderer.tsx
import { useState as useState18, useEffect as useEffect17, useCallback as useCallback17 } from "react";
import {
  Box as Box18,
  TextField as TextField18,
  MenuItem as MenuItem9,
  Typography as Typography24,
  CircularProgress as CircularProgress7,
  InputAdornment as InputAdornment8
} from "@mui/material";
import {
  People as PeopleIcon2,
  Schedule as ScheduleIcon3,
  Timer as TimerIcon
} from "@mui/icons-material";
import {
  StyledSwitch as StyledSwitch12,
  SubOption as SubOption16,
  BigButtonGroup as BigButtonGroup6
} from "@hopdrive/sdk-ui";
import { jsx as jsx31, jsxs as jsxs27 } from "react/jsx-runtime";
function AppointmentManagementRenderer({ value, onChange, disabled }) {
  const appointmentValue = value || {};
  const hostServices = useHostServices();
  const { options: algorithms, loading } = hostServices.dataSources.apptAlgorithms();
  const AlgorithmReference = hostServices.slots?.AlgorithmReference;
  const [enabled, setEnabled] = useState18(appointmentValue.enabled ?? false);
  const [fullPlanningEnabled, setFullPlanningEnabled] = useState18(
    appointmentValue.full_planning_enabled ?? false
  );
  const [strategy, setStrategy] = useState18(appointmentValue.strategy ?? "max_first_driver");
  const [driverCount, setDriverCount] = useState18(appointmentValue.driver_count ?? 0);
  const [moveBuffer, setMoveBuffer] = useState18(appointmentValue.move_buffer_minutes ?? 0);
  const [enforcementMode, setEnforcementMode] = useState18(
    appointmentValue.enforcement_mode ?? "suggestion"
  );
  const [offsetMax, setOffsetMax] = useState18(appointmentValue.offset_max_minutes ?? 0);
  const [offsetIncrement, setOffsetIncrement] = useState18(
    appointmentValue.offset_increment_minutes ?? 15
  );
  useEffect17(() => {
    const appointmentValue2 = value || {};
    setEnabled(appointmentValue2.enabled ?? false);
    setFullPlanningEnabled(appointmentValue2.full_planning_enabled ?? false);
    setStrategy(appointmentValue2.strategy ?? "max_first_driver");
    setDriverCount(appointmentValue2.driver_count ?? 0);
    setMoveBuffer(appointmentValue2.move_buffer_minutes ?? 0);
    setEnforcementMode(appointmentValue2.enforcement_mode ?? "suggestion");
    setOffsetMax(appointmentValue2.offset_max_minutes ?? 0);
    setOffsetIncrement(appointmentValue2.offset_increment_minutes ?? 15);
  }, [value]);
  const updateAppointmentManagement = useCallback17(
    (updates) => {
      onChange({
        enabled,
        full_planning_enabled: fullPlanningEnabled,
        strategy,
        driver_count: driverCount,
        move_buffer_minutes: moveBuffer,
        enforcement_mode: enforcementMode,
        offset_max_minutes: offsetMax,
        offset_increment_minutes: offsetIncrement,
        ...updates
      });
    },
    [enabled, fullPlanningEnabled, strategy, driverCount, moveBuffer, enforcementMode, offsetMax, offsetIncrement, onChange]
  );
  const handleEnabledChange = (e) => {
    const newValue = e.target.checked;
    setEnabled(newValue);
    if (!newValue && fullPlanningEnabled) {
      setFullPlanningEnabled(false);
      updateAppointmentManagement({ enabled: newValue, full_planning_enabled: false });
    } else {
      updateAppointmentManagement({ enabled: newValue });
    }
  };
  const handleFullPlanningEnabledChange = (e) => {
    const newValue = e.target.checked;
    setFullPlanningEnabled(newValue);
    updateAppointmentManagement({ full_planning_enabled: newValue });
  };
  const handleStrategyChange = (newStrategy) => {
    setStrategy(newStrategy);
    updateAppointmentManagement({ strategy: newStrategy });
  };
  const handleDriverCountChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setDriverCount(newValue);
  };
  const handleDriverCountBlur = () => {
    if (driverCount !== (appointmentValue.driver_count ?? 0)) {
      updateAppointmentManagement({ driver_count: driverCount });
    }
  };
  const handleMoveBufferChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setMoveBuffer(newValue);
  };
  const handleMoveBufferBlur = () => {
    if (moveBuffer !== (appointmentValue.move_buffer_minutes ?? 0)) {
      updateAppointmentManagement({ move_buffer_minutes: moveBuffer });
    }
  };
  const handleEnforcementModeChange = (e) => {
    const newValue = e.target.value;
    setEnforcementMode(newValue);
    updateAppointmentManagement({ enforcement_mode: newValue });
  };
  const handleOffsetMaxChange = (e) => {
    const newValue = parseInt(e.target.value, 10) || 0;
    setOffsetMax(newValue);
  };
  const handleOffsetMaxBlur = () => {
    if (offsetMax !== (appointmentValue.offset_max_minutes ?? 0)) {
      updateAppointmentManagement({ offset_max_minutes: offsetMax });
    }
  };
  const handleOffsetIncrementChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    setOffsetIncrement(newValue);
    updateAppointmentManagement({ offset_increment_minutes: newValue });
  };
  return /* @__PURE__ */ jsxs27("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Appointment Management Enabled",
        description: "Enable or disable appointment management features for this client. When enabled, users can view the timeline in Portal and manage driver schedules.",
        children: /* @__PURE__ */ jsx31(StyledSwitch12, { checked: enabled, onChange: handleEnabledChange, disabled })
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Full Planning Enabled",
        description: "Enable full planning and time suggestions for appointment management. Note: Appointment Management must be enabled first.",
        children: /* @__PURE__ */ jsx31(
          StyledSwitch12,
          {
            checked: fullPlanningEnabled,
            onChange: handleFullPlanningEnabledChange,
            disabled: disabled || !enabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        title: "Appointment Management Strategy Algorithm",
        description: "Select the algorithm to use for appointment scheduling. Each algorithm has different scheduling criteria and driver utilization logic.",
        children: loading ? /* @__PURE__ */ jsxs27(Box18, { sx: { display: "flex", alignItems: "center", gap: 1, py: 2 }, children: [
          /* @__PURE__ */ jsx31(CircularProgress7, { size: 20 }),
          /* @__PURE__ */ jsx31(Typography24, { variant: "body2", color: "text.secondary", children: "Loading algorithms..." })
        ] }) : /* @__PURE__ */ jsx31(
          BigButtonGroup6,
          {
            items: algorithms.map((algo) => ({
              id: String(algo.id),
              render: createSettingsBigButton({
                title: AlgorithmReference ? /* @__PURE__ */ jsx31(AlgorithmReference, { algorithmKey: String(algo.id), selection: true }) : algo.label,
                subtitle: algo.sublabel
              })
            })),
            selectedId: strategy,
            onSelectionChange: (id) => handleStrategyChange(String(id)),
            columns: { xs: 6, sm: 6, md: 6 },
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Driver Count",
        description: "For Gig customers only: Expected number of drivers typically available for this rooftop. Used by the algorithm to calculate capacity and suggest appropriate appointment times. For SaaS customers, the system reads from their actual drivers.",
        children: /* @__PURE__ */ jsx31(
          TextField18,
          {
            type: "number",
            label: "Driver Count",
            placeholder: "Enter driver count...",
            size: "small",
            variant: "outlined",
            value: driverCount,
            onChange: handleDriverCountChange,
            onBlur: handleDriverCountBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx31(InputAdornment8, { position: "start", children: /* @__PURE__ */ jsx31(PeopleIcon2, { fontSize: "small", color: "disabled" }) })
            },
            inputProps: {
              step: 1,
              min: 1
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Move Buffer",
        description: "Minimum time gap (in minutes) required between consecutive moves. Prevents back-to-back scheduling that doesn't account for traffic delays, customer interaction time, and vehicle inspection/handoff. Example: 40 minutes ensures drivers have breathing room between appointments.",
        children: /* @__PURE__ */ jsx31(
          TextField18,
          {
            type: "number",
            label: "Move Buffer (minutes)",
            placeholder: "Enter buffer time...",
            size: "small",
            variant: "outlined",
            value: moveBuffer,
            onChange: handleMoveBufferChange,
            onBlur: handleMoveBufferBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx31(InputAdornment8, { position: "start", children: /* @__PURE__ */ jsx31(ScheduleIcon3, { fontSize: "small", color: "disabled" }) })
            },
            inputProps: {
              step: 5,
              min: 0
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Enforcement Mode",
        description: 'Controls whether suggested times are recommendations or hard constraints. "Suggestion" displays suggested times but allows dealer to override. "Forced" only allows suggested times to be selectable, preventing submission of non-compliant appointment times.',
        children: /* @__PURE__ */ jsxs27(
          TextField18,
          {
            select: true,
            label: "Enforcement Mode",
            size: "small",
            variant: "outlined",
            value: enforcementMode,
            onChange: handleEnforcementModeChange,
            disabled,
            sx: { width: 300 },
            children: [
              /* @__PURE__ */ jsx31(MenuItem9, { value: "suggestion", children: "Suggestion" }),
              /* @__PURE__ */ jsx31(MenuItem9, { value: "forced", children: "Forced" })
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Allowable Appointment Offset Max",
        description: "Maximum time deviation (in minutes) a dealer can adjust from a suggested appointment time. Controls how far dealers can shift appointments from algorithm-suggested slots. Example: 30-minute offset with 8:00 AM suggested slot allows 7:30 AM (30 minutes earlier), 8:00 AM (suggested time), or 8:30 AM (30 minutes later).",
        children: /* @__PURE__ */ jsx31(
          TextField18,
          {
            type: "number",
            label: "Offset Max (minutes)",
            placeholder: "Enter maximum offset...",
            size: "small",
            variant: "outlined",
            value: offsetMax,
            onChange: handleOffsetMaxChange,
            onBlur: handleOffsetMaxBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx31(InputAdornment8, { position: "start", children: /* @__PURE__ */ jsx31(TimerIcon, { fontSize: "small", color: "disabled" }) })
            },
            inputProps: {
              step: 5,
              min: 0
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx31(
      SubOption16,
      {
        inline: true,
        title: "Allowable Appointment Offset Increment",
        description: "Defines the time increment (in minutes) for allowable offset adjustments. Prevents arbitrary times like 8:12 AM or 8:37 AM that complicate scheduling. Works in conjunction with Offset Max to control available override options. Example: 15-minute increments allow 8:00, 8:15, 8:30, 8:45, etc.",
        children: /* @__PURE__ */ jsxs27(
          TextField18,
          {
            select: true,
            label: "Offset Increment (minutes)",
            size: "small",
            variant: "outlined",
            value: offsetIncrement,
            onChange: handleOffsetIncrementChange,
            disabled,
            sx: { width: 300 },
            children: [
              /* @__PURE__ */ jsx31(MenuItem9, { value: 5, children: "5 minutes" }),
              /* @__PURE__ */ jsx31(MenuItem9, { value: 10, children: "10 minutes" }),
              /* @__PURE__ */ jsx31(MenuItem9, { value: 15, children: "15 minutes" })
            ]
          }
        )
      }
    )
  ] });
}

// src/definitions/workflow-id.renderer.tsx
import { useState as useState19, useEffect as useEffect18 } from "react";
import {
  Box as Box19,
  TextField as TextField19,
  MenuItem as MenuItem10,
  Typography as Typography25,
  CircularProgress as CircularProgress8,
  InputAdornment as InputAdornment9,
  Alert as Alert11
} from "@mui/material";
import { AccountTree as WorkflowIcon } from "@mui/icons-material";
import { Fragment as Fragment5, jsx as jsx32, jsxs as jsxs28 } from "react/jsx-runtime";
var WORKFLOWSET_KEYS = ["default_workflow_set_id"];
var WORKFLOW_KEYS = ["pickup_workflow_id", "delivery_workflow_id"];
var HIDDEN_WORKFLOW_SETTINGS = [
  "default_pickup_workflow_id",
  "default_delivery_workflow_id"
];
var SETTING_CONFIG = {
  default_workflow_set_id: {
    label: "Default Workflow Set",
    description: "Select the default workflow set to use for this entity. This will automatically set the pickup and delivery workflows based on the selected workflow set."
  },
  pickup_workflow_id: {
    label: "Pickup Workflow",
    description: "Select the workflow to use for pickup operations. This overrides the workflow set default."
  },
  delivery_workflow_id: {
    label: "Delivery Workflow",
    description: "Select the workflow to use for delivery operations. This overrides the workflow set default."
  }
};
function WorkflowIdRenderer({ value, onChange, disabled, settingKey }) {
  const workflowId = value || 0;
  const [selectedId, setSelectedId] = useState19(workflowId);
  const { entity, dataSources } = useHostServices();
  const isWorkflowSet = WORKFLOWSET_KEYS.includes(settingKey);
  const isWorkflow = WORKFLOW_KEYS.includes(settingKey);
  const config = SETTING_CONFIG[settingKey] || {
    label: "Workflow",
    description: "Select a workflow."
  };
  const { options: workflows, loading: workflowLoading } = dataSources.workflows(entity);
  const { options: workflowsets, loading: workflowSetLoading } = dataSources.workflowSets(entity);
  const loading = isWorkflowSet ? workflowSetLoading : workflowLoading;
  const items = isWorkflowSet ? workflowsets : workflows;
  useEffect18(() => {
    setSelectedId(value || 0);
  }, [value]);
  const handleChange = (e) => {
    const newId = Number(e.target.value) || 0;
    setSelectedId(newId);
    onChange(newId || null);
  };
  const selectedItem = items.find((item) => item.id === selectedId);
  const showMissingWarning = selectedId > 0 && !loading && items.length > 0 && !selectedItem;
  if (HIDDEN_WORKFLOW_SETTINGS.includes(settingKey)) {
    return null;
  }
  if (!isWorkflowSet && !isWorkflow) {
    return /* @__PURE__ */ jsxs28(Alert11, { severity: "warning", children: [
      "Unknown workflow setting key: ",
      settingKey,
      ". This renderer only supports workflow and workflowset ID settings."
    ] });
  }
  return /* @__PURE__ */ jsxs28(Fragment5, { children: [
    loading ? /* @__PURE__ */ jsxs28(Box19, { sx: { display: "flex", alignItems: "center", gap: 1, py: 1 }, children: [
      /* @__PURE__ */ jsx32(CircularProgress8, { size: 20 }),
      /* @__PURE__ */ jsxs28(Typography25, { variant: "body2", color: "text.secondary", children: [
        "Loading ",
        isWorkflowSet ? "workflow sets" : "workflows",
        "..."
      ] })
    ] }) : /* @__PURE__ */ jsxs28(
      TextField19,
      {
        select: true,
        fullWidth: true,
        label: `Select ${isWorkflowSet ? "Workflow Set" : "Workflow"}`,
        value: selectedId || "",
        onChange: handleChange,
        variant: "outlined",
        size: "small",
        disabled,
        sx: { maxWidth: 500 },
        InputProps: {
          startAdornment: /* @__PURE__ */ jsx32(InputAdornment9, { position: "start", children: /* @__PURE__ */ jsx32(WorkflowIcon, { fontSize: "small", color: "disabled" }) })
        },
        children: [
          /* @__PURE__ */ jsx32(MenuItem10, { value: "", children: /* @__PURE__ */ jsx32("em", { children: "None" }) }),
          items.map((item) => /* @__PURE__ */ jsxs28(MenuItem10, { value: item.id, children: [
            item.label,
            " (ID: ",
            item.id,
            ")"
          ] }, item.id))
        ]
      }
    ),
    showMissingWarning && /* @__PURE__ */ jsx32(Alert11, { severity: "warning", sx: { mt: 1 }, children: /* @__PURE__ */ jsxs28(Typography25, { variant: "body2", children: [
      "The currently selected ",
      isWorkflowSet ? "workflow set" : "workflow",
      " (ID: ",
      selectedId,
      ") was not found. It may have been deleted or you may not have access to it."
    ] }) }),
    config.description && /* @__PURE__ */ jsx32(Typography25, { variant: "body2", color: "text.secondary", sx: { mt: 1, fontStyle: "italic" }, children: config.description })
  ] });
}

// src/definitions/branding.renderer.tsx
import { useState as useState20, useRef, useEffect as useEffect19, useCallback as useCallback18 } from "react";
import {
  Box as Box20,
  TextField as TextField20,
  Typography as Typography26,
  Paper as Paper3,
  IconButton as IconButton3,
  Tooltip as Tooltip3,
  alpha,
  InputAdornment as InputAdornment10
} from "@mui/material";
import { useTheme as useTheme3 } from "@mui/material/styles";
import {
  ColorLens as ColorLensIcon,
  Label as LabelIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
  Clear as ClearIcon
} from "@mui/icons-material";
import { StyledSwitch as StyledSwitch13, SubOption as SubOption17, PhoneNumberField, parsePhoneNumber } from "@hopdrive/sdk-ui";
import { jsx as jsx33, jsxs as jsxs29 } from "react/jsx-runtime";
function BrandingRenderer({ value, onChange, disabled }) {
  const theme = useTheme3();
  const { entity, uploadAsset } = useHostServices();
  const { level, entityId } = entity;
  const brandingValue = value || {};
  const [active, setActive] = useState20(brandingValue.active ?? false);
  const [displayName, setDisplayName] = useState20(brandingValue.name ?? "");
  const [primaryColor, setPrimaryColor] = useState20(brandingValue.primary_color ?? "");
  const [secondaryColor, setSecondaryColor] = useState20(brandingValue.secondary_color ?? "");
  const [backdropColor, setBackdropColor] = useState20(brandingValue.backdrop_color ?? "");
  const [logoUrl, setLogoUrl] = useState20(brandingValue.logo_url ?? "");
  const [customerSupportPhone, setCustomerSupportPhone] = useState20(brandingValue.customer_support_phone_number ?? "");
  const [dropzoneDragover, setDropzoneDragover] = useState20(false);
  const [uploadError, setUploadError] = useState20(null);
  const [uploadingLogo, setUploadingLogo] = useState20(false);
  const fileInputRef = useRef(null);
  useEffect19(() => {
    const brandingValue2 = value || {};
    setActive(brandingValue2.active ?? false);
    setDisplayName(brandingValue2.name ?? "");
    setPrimaryColor(brandingValue2.primary_color ?? "");
    setSecondaryColor(brandingValue2.secondary_color ?? "");
    setBackdropColor(brandingValue2.backdrop_color ?? "");
    setLogoUrl(brandingValue2.logo_url ?? "");
    setCustomerSupportPhone(brandingValue2.customer_support_phone_number ?? "");
  }, [value]);
  const updateBranding = useCallback18(
    (updates) => {
      onChange({
        active,
        name: displayName,
        primary_color: primaryColor || null,
        secondary_color: secondaryColor || null,
        backdrop_color: backdropColor || null,
        logo_url: logoUrl,
        // PhoneNumberField only hands back clean numbers, so this only changes a number that was
        // stored before the field existed. Clean it if it parses, otherwise leave it alone.
        customer_support_phone_number: parsePhoneNumber(customerSupportPhone).digits ?? customerSupportPhone,
        ...updates
      });
    },
    [active, displayName, primaryColor, secondaryColor, backdropColor, logoUrl, customerSupportPhone, onChange]
  );
  const handleActiveChange = (e) => {
    const newValue = e.target.checked;
    setActive(newValue);
    updateBranding({ active: newValue });
  };
  const handleDisplayNameChange = (e) => {
    setDisplayName(e.target.value);
  };
  const handleDisplayNameBlur = () => {
    if (displayName !== (brandingValue.name ?? "")) {
      updateBranding({ name: displayName });
    }
  };
  const handlePrimaryColorChange = (e) => {
    setPrimaryColor(e.target.value);
  };
  const handlePrimaryColorBlur = () => {
    const newValue = primaryColor || null;
    if (newValue !== (brandingValue.primary_color ?? null)) {
      updateBranding({ primary_color: newValue });
    }
  };
  const handlePrimaryColorClear = () => {
    setPrimaryColor("");
    updateBranding({ primary_color: null });
  };
  const handleSecondaryColorChange = (e) => {
    setSecondaryColor(e.target.value);
  };
  const handleSecondaryColorBlur = () => {
    const newValue = secondaryColor || null;
    if (newValue !== (brandingValue.secondary_color ?? null)) {
      updateBranding({ secondary_color: newValue });
    }
  };
  const handleSecondaryColorClear = () => {
    setSecondaryColor("");
    updateBranding({ secondary_color: null });
  };
  const handleBackdropColorChange = (e) => {
    setBackdropColor(e.target.value);
  };
  const handleBackdropColorBlur = () => {
    const newValue = backdropColor || null;
    if (newValue !== (brandingValue.backdrop_color ?? null)) {
      updateBranding({ backdrop_color: newValue });
    }
  };
  const handleBackdropColorClear = () => {
    setBackdropColor("");
    updateBranding({ backdrop_color: null });
  };
  const handleCustomerSupportPhoneChange = (phone) => {
    setCustomerSupportPhone(phone);
    updateBranding({ customer_support_phone_number: phone });
  };
  const handleFileClick = (e) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      handleLogoUpload(file);
    }
  };
  const handleFileDrop = (e) => {
    e.preventDefault();
    setDropzoneDragover(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleLogoUpload(file);
    }
  };
  const handleFileDragOver = (e) => {
    e.preventDefault();
    setDropzoneDragover(true);
  };
  const handleFileDragLeave = (e) => {
    e.preventDefault();
    setDropzoneDragover(false);
  };
  const handleLogoUpload = async (file) => {
    setUploadError(null);
    if (file.size > 5e5) {
      setUploadError("File size must be less than 0.5MB/500KB");
      return;
    }
    if (file.type !== "image/png" && file.type !== "image/svg+xml") {
      setUploadError("Only PNG and SVG files are supported");
      return;
    }
    setUploadingLogo(true);
    try {
      const keyPrefix = `branding/${level}/${entityId}`;
      const { url } = await uploadAsset(file, { keyPrefix });
      setLogoUrl(url);
      updateBranding({ logo_url: url });
    } catch (err) {
      console.error("Failed to upload logo:", err);
      setUploadError("Failed to upload logo. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };
  const handleLogoClear = () => {
    setLogoUrl("");
    updateBranding({ logo_url: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const resolvedPrimaryColor = primaryColor || theme.palette.primary.main;
  const resolvedSecondaryColor = secondaryColor || theme.palette.secondary.main;
  const resolvedBackdropColor = backdropColor || theme.palette.utility?.main;
  return /* @__PURE__ */ jsxs29("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx33(
      SubOption17,
      {
        inline: true,
        title: "Activation",
        description: "Activate/Deactivate the branding feature for this client. Branded clients will be visible to their customers not as HopDrive, but as the client's own brand.",
        children: /* @__PURE__ */ jsx33(StyledSwitch13, { checked: active, onChange: handleActiveChange, disabled })
      }
    ),
    /* @__PURE__ */ jsx33(
      SubOption17,
      {
        inline: true,
        title: "Display Name",
        description: "Name to be displayed to the client's customers.",
        children: /* @__PURE__ */ jsx33(
          TextField20,
          {
            placeholder: "Enter a display name...",
            size: "small",
            variant: "outlined",
            value: displayName,
            onChange: handleDisplayNameChange,
            onBlur: handleDisplayNameBlur,
            disabled,
            sx: { width: 300 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx33(InputAdornment10, { position: "start", children: /* @__PURE__ */ jsx33(LabelIcon, { fontSize: "small", color: "disabled" }) })
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx33(
      SubOption17,
      {
        inline: true,
        title: "Primary Color",
        description: `Primary color is the main color used for branding the client.${!primaryColor ? " Currently using theme default." : ""}`,
        children: /* @__PURE__ */ jsxs29(Box20, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
          /* @__PURE__ */ jsx33(
            TextField20,
            {
              type: "color",
              size: "small",
              variant: "outlined",
              value: primaryColor || theme.palette.primary.main,
              onChange: handlePrimaryColorChange,
              onBlur: handlePrimaryColorBlur,
              disabled,
              sx: { width: 200 },
              InputProps: {
                startAdornment: /* @__PURE__ */ jsx33(InputAdornment10, { position: "start", children: /* @__PURE__ */ jsx33(ColorLensIcon, { fontSize: "small", color: "disabled" }) })
              }
            }
          ),
          primaryColor && /* @__PURE__ */ jsx33(Tooltip3, { title: "Clear to use theme default", children: /* @__PURE__ */ jsx33(IconButton3, { size: "small", onClick: handlePrimaryColorClear, disabled, children: /* @__PURE__ */ jsx33(ClearIcon, { fontSize: "small" }) }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx33(
      SubOption17,
      {
        inline: true,
        title: "Secondary Color",
        description: `Secondary color is the accent color used for branding the client.${!secondaryColor ? " Currently using theme default." : ""}`,
        children: /* @__PURE__ */ jsxs29(Box20, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
          /* @__PURE__ */ jsx33(
            TextField20,
            {
              type: "color",
              size: "small",
              variant: "outlined",
              value: secondaryColor || theme.palette.secondary.main,
              onChange: handleSecondaryColorChange,
              onBlur: handleSecondaryColorBlur,
              disabled,
              sx: { width: 200 },
              InputProps: {
                startAdornment: /* @__PURE__ */ jsx33(InputAdornment10, { position: "start", children: /* @__PURE__ */ jsx33(ColorLensIcon, { fontSize: "small", color: "disabled" }) })
              }
            }
          ),
          secondaryColor && /* @__PURE__ */ jsx33(Tooltip3, { title: "Clear to use theme default", children: /* @__PURE__ */ jsx33(IconButton3, { size: "small", onClick: handleSecondaryColorClear, disabled, children: /* @__PURE__ */ jsx33(ClearIcon, { fontSize: "small" }) }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx33(
      SubOption17,
      {
        inline: true,
        title: "Backdrop Color",
        description: `Backdrop color is used as the background behind the logo.${!backdropColor ? " Currently using theme default." : ""}`,
        children: /* @__PURE__ */ jsxs29(Box20, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
          /* @__PURE__ */ jsx33(
            TextField20,
            {
              type: "color",
              size: "small",
              variant: "outlined",
              value: backdropColor || theme.palette.utility?.main,
              onChange: handleBackdropColorChange,
              onBlur: handleBackdropColorBlur,
              disabled,
              sx: { width: 200 },
              InputProps: {
                startAdornment: /* @__PURE__ */ jsx33(InputAdornment10, { position: "start", children: /* @__PURE__ */ jsx33(ColorLensIcon, { fontSize: "small", color: "disabled" }) })
              }
            }
          ),
          backdropColor && /* @__PURE__ */ jsx33(Tooltip3, { title: "Clear to use theme default", children: /* @__PURE__ */ jsx33(IconButton3, { size: "small", onClick: handleBackdropColorClear, disabled, children: /* @__PURE__ */ jsx33(ClearIcon, { fontSize: "small" }) }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs29(
      SubOption17,
      {
        title: "Logo",
        description: "The logo is a link to a PNG or SVG file. Max file size 0.5MB/500KB. Min height 40px.",
        children: [
          !logoUrl ? /* @__PURE__ */ jsxs29(
            Paper3,
            {
              variant: "outlined",
              onDrop: handleFileDrop,
              onDragOver: handleFileDragOver,
              onDragLeave: handleFileDragLeave,
              onClick: () => !disabled && !uploadingLogo && fileInputRef.current?.click(),
              sx: {
                width: 256,
                height: 128,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled || uploadingLogo ? "not-allowed" : "pointer",
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: uploadError ? "error.main" : dropzoneDragover ? "primary.main" : "divider",
                backgroundColor: dropzoneDragover ? alpha(theme.palette.primary.main, 0.04) : "background.paper",
                opacity: disabled ? 0.5 : 1,
                transition: "all 0.15s ease-in-out",
                "&:hover": disabled || uploadingLogo ? {} : {
                  borderColor: "primary.main",
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              },
              children: [
                /* @__PURE__ */ jsx33(CloudUploadIcon, { sx: { fontSize: 32, color: "text.secondary", mb: 1 } }),
                /* @__PURE__ */ jsx33(Typography26, { fontSize: 14, color: "text.secondary", textAlign: "center", children: uploadingLogo ? "Uploading..." : "Click or drag PNG/SVG file here" }),
                /* @__PURE__ */ jsx33(Typography26, { variant: "caption", color: "text.disabled", sx: { mt: 1 }, children: "Max 0.5MB/500KB \u2022 Min height 40px" }),
                /* @__PURE__ */ jsx33(
                  "input",
                  {
                    ref: fileInputRef,
                    type: "file",
                    accept: "image/png, image/svg+xml",
                    onChange: handleFileClick,
                    disabled: disabled || uploadingLogo,
                    style: { display: "none" }
                  }
                )
              ]
            }
          ) : /* @__PURE__ */ jsxs29(Box20, { sx: { display: "flex", alignItems: "flex-start", gap: 1 }, children: [
            /* @__PURE__ */ jsx33(
              Paper3,
              {
                variant: "outlined",
                sx: {
                  p: 1,
                  width: 260,
                  backgroundColor: alpha(theme.palette.grey[300], 0.3),
                  backgroundImage: "linear-gradient(45deg, #d0d0d0 25%, transparent 25%), linear-gradient(-45deg, #d0d0d0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d0d0d0 75%), linear-gradient(-45deg, transparent 75%, #d0d0d0 75%)",
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px"
                },
                children: /* @__PURE__ */ jsx33(
                  "img",
                  {
                    src: logoUrl,
                    alt: "Brand logo",
                    style: {
                      display: "block",
                      maxWidth: "100%",
                      maxHeight: 144,
                      margin: "auto"
                    }
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx33(Tooltip3, { title: "Remove logo", placement: "top", children: /* @__PURE__ */ jsx33(IconButton3, { size: "small", onClick: handleLogoClear, disabled, sx: { mt: 0.5 }, children: /* @__PURE__ */ jsx33(CancelIcon, {}) }) })
          ] }),
          uploadError && /* @__PURE__ */ jsx33(Typography26, { fontSize: 14, color: "error", sx: { mt: 1 }, children: uploadError })
        ]
      }
    ),
    /* @__PURE__ */ jsx33(
      SubOption17,
      {
        inline: true,
        title: "Customer Support Phone Number",
        description: "Hopdrive Lite only. This number will be displayed in concierge SMS communications to consumers.",
        children: /* @__PURE__ */ jsx33(
          PhoneNumberField,
          {
            persistAs: "digits",
            size: "small",
            variant: "outlined",
            value: customerSupportPhone,
            onChange: handleCustomerSupportPhoneChange,
            disabled,
            sx: { width: 300 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs29(Box20, { sx: { mt: 2 }, children: [
      /* @__PURE__ */ jsx33(Typography26, { gutterBottom: true, fontSize: 14, fontWeight: 600, color: "text.disabled", textAlign: "center", children: "BRANDING PREVIEW" }),
      /* @__PURE__ */ jsxs29(Paper3, { variant: "outlined", sx: { mt: 2, borderRadius: 2, overflow: "hidden" }, children: [
        /* @__PURE__ */ jsx33(
          Box20,
          {
            sx: {
              height: 64,
              backgroundColor: resolvedBackdropColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1.5
            },
            children: logoUrl && /* @__PURE__ */ jsx33(
              "img",
              {
                src: logoUrl,
                alt: "Brand logo preview",
                style: {
                  maxHeight: 40,
                  maxWidth: "100%"
                }
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs29(Box20, { sx: { p: 3 }, children: [
          /* @__PURE__ */ jsxs29(Box20, { sx: { display: "flex", gap: 2, mb: 2 }, children: [
            /* @__PURE__ */ jsx33(
              Box20,
              {
                sx: {
                  flex: 1,
                  height: 60,
                  borderRadius: 1,
                  backgroundColor: theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                },
                children: /* @__PURE__ */ jsx33(Typography26, { fontSize: 14, sx: { color: "white", fontWeight: 500 }, children: "Default Color" })
              }
            ),
            /* @__PURE__ */ jsx33(
              Box20,
              {
                sx: {
                  flex: 1,
                  height: 60,
                  borderRadius: 1,
                  backgroundColor: resolvedPrimaryColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                },
                children: /* @__PURE__ */ jsx33(Typography26, { fontSize: 14, sx: { color: "white", fontWeight: 500 }, children: "Primary Color" })
              }
            ),
            /* @__PURE__ */ jsx33(
              Box20,
              {
                sx: {
                  flex: 1,
                  height: 60,
                  borderRadius: 1,
                  backgroundColor: resolvedSecondaryColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                },
                children: /* @__PURE__ */ jsx33(Typography26, { fontSize: 14, sx: { color: "white", fontWeight: 500 }, children: "Secondary Color" })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs29(Box20, { sx: { display: "flex", gap: 2 }, children: [
            /* @__PURE__ */ jsx33(
              Typography26,
              {
                variant: "body1",
                sx: { flex: 1, textAlign: "center", color: theme.palette.text.primary, fontWeight: 600 },
                children: "Default Text"
              }
            ),
            /* @__PURE__ */ jsx33(
              Typography26,
              {
                variant: "body1",
                sx: { flex: 1, textAlign: "center", color: resolvedPrimaryColor, fontWeight: 600 },
                children: "Primary Text"
              }
            ),
            /* @__PURE__ */ jsx33(
              Typography26,
              {
                variant: "body1",
                sx: { flex: 1, textAlign: "center", color: resolvedSecondaryColor, fontWeight: 600 },
                children: "Secondary Text"
              }
            )
          ] })
        ] })
      ] })
    ] })
  ] });
}

// src/definitions/service-radius.renderer.tsx
import { useState as useState21, useEffect as useEffect20, useCallback as useCallback19 } from "react";
import { TextField as TextField21, Link } from "@mui/material";
import { useTheme as useTheme4 } from "@mui/material/styles";
import { StyledSwitch as StyledSwitch14, SubOption as SubOption18 } from "@hopdrive/sdk-ui";
import { Fragment as Fragment6, jsx as jsx34, jsxs as jsxs30 } from "react/jsx-runtime";
function ServiceRadiusRenderer({ value, onChange, disabled }) {
  const radiusValue = value || {};
  const theme = useTheme4();
  const { entity, dataSources, slots } = useHostServices();
  const Map2 = slots?.Map;
  const [serviceRadius, setServiceRadius] = useState21(radiusValue.serviceable_radius_miles ?? 0);
  const [checkByPickup, setCheckByPickup] = useState21(
    radiusValue.serviceable_radius_by_pickup ?? false
  );
  const [checkByDelivery, setCheckByDelivery] = useState21(
    radiusValue.serviceable_radius_by_delivery ?? false
  );
  const [customerServiceRadius, setCustomerServiceRadius] = useState21(
    radiusValue.customer_serviceable_radius_miles ?? 0
  );
  useEffect20(() => {
    const radiusValue2 = value || {};
    setServiceRadius(radiusValue2.serviceable_radius_miles ?? 0);
    setCheckByPickup(radiusValue2.serviceable_radius_by_pickup ?? false);
    setCheckByDelivery(radiusValue2.serviceable_radius_by_delivery ?? false);
    setCustomerServiceRadius(radiusValue2.customer_serviceable_radius_miles ?? 0);
  }, [value]);
  const isCustomerLevel = entity.level === "customer";
  const entityIdNum = isCustomerLevel && typeof entity.entityId === "number" ? entity.entityId : null;
  const { location } = dataSources.entityLocation(entityIdNum ?? -1);
  const entityLocation = entityIdNum ? location : null;
  const updateRadius = useCallback19(
    (updates) => {
      onChange({
        serviceable_radius_miles: serviceRadius,
        serviceable_radius_by_pickup: checkByPickup,
        serviceable_radius_by_delivery: checkByDelivery,
        customer_serviceable_radius_miles: customerServiceRadius,
        ...updates
      });
    },
    [serviceRadius, checkByPickup, checkByDelivery, customerServiceRadius, onChange]
  );
  const handleServiceRadiusChange = (e) => {
    const newValue = Number(e.target.value) || 0;
    setServiceRadius(newValue);
  };
  const handleServiceRadiusBlur = () => {
    if (serviceRadius !== (radiusValue.serviceable_radius_miles ?? 0)) {
      updateRadius({ serviceable_radius_miles: serviceRadius });
    }
  };
  const handleCheckByPickupChange = (e) => {
    const newValue = e.target.checked;
    setCheckByPickup(newValue);
    updateRadius({ serviceable_radius_by_pickup: newValue });
  };
  const handleCheckByDeliveryChange = (e) => {
    const newValue = e.target.checked;
    setCheckByDelivery(newValue);
    updateRadius({ serviceable_radius_by_delivery: newValue });
  };
  const handleCustomerServiceRadiusChange = (e) => {
    const newValue = Number(e.target.value) || 0;
    setCustomerServiceRadius(newValue);
  };
  const handleCustomerServiceRadiusBlur = () => {
    if (customerServiceRadius !== (radiusValue.customer_serviceable_radius_miles ?? 0)) {
      updateRadius({ customer_serviceable_radius_miles: customerServiceRadius });
    }
  };
  const circles = [];
  if (serviceRadius) {
    circles.push({
      radiusMiles: serviceRadius,
      label: `HopDrive Radius: ${serviceRadius}mi`,
      color: theme.palette.primary.main
    });
  }
  if (customerServiceRadius) {
    circles.push({
      radiusMiles: customerServiceRadius,
      label: `Customer Radius: ${customerServiceRadius}mi`,
      color: theme.palette.secondary.main
    });
  }
  return /* @__PURE__ */ jsxs30("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    Map2 && entityLocation && /* @__PURE__ */ jsx34(Map2, { center: entityLocation, circles, height: 350 }),
    /* @__PURE__ */ jsx34(
      SubOption18,
      {
        inline: true,
        title: "HopDrive Service Radius",
        description: /* @__PURE__ */ jsxs30(Fragment6, { children: [
          "The \u201CHopDrive Service Radius\u201D is used to validate move eligibility within the radius for clients who pre-verify using our API. For more information, see our",
          " ",
          /* @__PURE__ */ jsx34(
            Link,
            {
              href: "https://api.hopdrive.com/docs/moves-confirm-move-eligibility",
              target: "_blank",
              rel: "noopener noreferrer",
              children: "API documentation"
            }
          ),
          "."
        ] }),
        children: /* @__PURE__ */ jsx34(
          TextField21,
          {
            type: "number",
            label: "HopDrive Radius (mi)",
            value: serviceRadius,
            onChange: handleServiceRadiusChange,
            onBlur: handleServiceRadiusBlur,
            disabled,
            size: "small",
            variant: "outlined",
            sx: { width: 300 },
            inputProps: { min: 0, step: 1 }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx34(
      SubOption18,
      {
        inline: true,
        title: "Pickup Verification for HopDrive Service Radius",
        description: "Toggle on to verify that the pickup address is within the HopDrive Service Radius.",
        children: /* @__PURE__ */ jsx34(
          StyledSwitch14,
          {
            checked: checkByPickup,
            onChange: handleCheckByPickupChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx34(
      SubOption18,
      {
        inline: true,
        title: "Delivery Verification for HopDrive Service Radius",
        description: "Toggle on to verify that the delivery address is within the HopDrive Service Radius.",
        children: /* @__PURE__ */ jsx34(
          StyledSwitch14,
          {
            checked: checkByDelivery,
            onChange: handleCheckByDeliveryChange,
            disabled
          }
        )
      }
    ),
    /* @__PURE__ */ jsx34(
      SubOption18,
      {
        inline: true,
        title: "Customer Service Radius",
        description: 'The "Customer Service Radius" is used to validate move eligibility within the radius for clients who use our HopDrive Lite tier. Regions do not apply to these customers as they do not utilize our driver network.',
        children: /* @__PURE__ */ jsx34(
          TextField21,
          {
            type: "number",
            label: "Customer Radius (mi)",
            value: customerServiceRadius,
            onChange: handleCustomerServiceRadiusChange,
            onBlur: handleCustomerServiceRadiusBlur,
            disabled,
            size: "small",
            variant: "outlined",
            sx: { width: 300 },
            inputProps: { min: 0, step: 1 }
          }
        )
      }
    )
  ] });
}

// src/definitions/product-history.renderer.tsx
import { TextField as TextField22, InputAdornment as InputAdornment11 } from "@mui/material";
import { SubOption as SubOption19 } from "@hopdrive/sdk-ui";
import { jsx as jsx35 } from "react/jsx-runtime";
function ProductHistoryRenderer({ value, onChange, disabled }) {
  const current = value || {};
  const retentionDays = current.retention_days;
  const handleChange = (e) => {
    const raw = e.target.value.trim();
    const next = { ...current };
    if (raw === "") {
      delete next.retention_days;
    } else {
      next.retention_days = Math.max(0, Math.trunc(Number(raw)));
    }
    onChange(next);
  };
  return /* @__PURE__ */ jsx35(
    SubOption19,
    {
      inline: true,
      title: "History Retention Window",
      description: "Days after a product's last order (or its enablement ending) during which portals still show the product as history-only. Past the window the product is advertised, or hidden when marketing is suppressed. Blank uses the default of 90.",
      children: /* @__PURE__ */ jsx35(
        TextField22,
        {
          type: "number",
          size: "small",
          value: retentionDays ?? "",
          onChange: handleChange,
          disabled,
          InputProps: { endAdornment: /* @__PURE__ */ jsx35(InputAdornment11, { position: "end", children: "days" }) },
          sx: { width: 160 }
        }
      )
    }
  );
}

// src/definitions/api-config.renderer.tsx
import { useState as useState22, useEffect as useEffect21, useCallback as useCallback20 } from "react";
import { TextField as TextField23, Typography as Typography27, InputAdornment as InputAdornment12, Divider as Divider8 } from "@mui/material";
import { Key as KeyIcon2 } from "@mui/icons-material";
import { SubOption as SubOption20, SecretField } from "@hopdrive/sdk-ui";
import { jsx as jsx36, jsxs as jsxs31 } from "react/jsx-runtime";
function EncryptedField({ label, description, value, onChange, disabled }) {
  const secrets = useHostServices().secrets;
  const isEncrypted = secrets.isEncrypted(value);
  return /* @__PURE__ */ jsx36(SubOption20, { title: label, description, inline: true, children: /* @__PURE__ */ jsx36(
    SecretField,
    {
      value,
      isEncrypted,
      maskValue: secrets.mask,
      onReveal: isEncrypted && value ? () => secrets.decrypt(value) : void 0,
      onCommit: async (plaintext) => {
        if (!plaintext.trim()) {
          onChange("");
          return;
        }
        const encrypted = await secrets.encrypt(plaintext);
        onChange(encrypted);
      },
      disabled
    }
  ) });
}
function ApiConfigRenderer({ value, onChange, disabled }) {
  const apiConfig = value || {};
  const [oauthClientId, setOauthClientId] = useState22(apiConfig.oauth_client_id ?? "");
  useEffect21(() => {
    const apiConfig2 = value || {};
    setOauthClientId(apiConfig2.oauth_client_id ?? "");
  }, [value]);
  const updateField = useCallback20(
    (field, newValue) => {
      const currentValue = value || {};
      onChange({
        ...currentValue,
        [field]: newValue || void 0
      });
    },
    [onChange, value]
  );
  const handleOauthClientIdChange = (e) => {
    setOauthClientId(e.target.value);
  };
  const handleOauthClientIdBlur = () => {
    if (oauthClientId !== (apiConfig.oauth_client_id ?? "")) {
      updateField("oauth_client_id", oauthClientId);
    }
  };
  return /* @__PURE__ */ jsxs31("div", { style: { display: "flex", flexDirection: "column", gap: "16px" }, children: [
    /* @__PURE__ */ jsx36(Typography27, { fontSize: 14, fontWeight: 500, children: "HMAC Authentication" }),
    /* @__PURE__ */ jsx36(Typography27, { fontSize: 13, color: "text.secondary", sx: { mt: -1 }, children: "HMAC secrets are used to generate hash values for anonymous JWT handshake authentication. Two secrets are provided to support key rotation without service interruption." }),
    /* @__PURE__ */ jsx36(
      EncryptedField,
      {
        label: "HMAC Shared Secret 1 (Primary)",
        description: "Primary HMAC shared secret for anonymous JWT handshake authentication.",
        value: apiConfig.hmac_shared_secret_1 ?? "",
        onChange: (newValue) => updateField("hmac_shared_secret_1", newValue),
        disabled
      }
    ),
    /* @__PURE__ */ jsx36(
      EncryptedField,
      {
        label: "HMAC Shared Secret 2 (Rotation)",
        description: "Secondary HMAC shared secret for key rotation support.",
        value: apiConfig.hmac_shared_secret_2 ?? "",
        onChange: (newValue) => updateField("hmac_shared_secret_2", newValue),
        disabled
      }
    ),
    /* @__PURE__ */ jsx36(Divider8, {}),
    /* @__PURE__ */ jsx36(Typography27, { fontSize: 14, fontWeight: 500, children: "OAuth Credentials" }),
    /* @__PURE__ */ jsx36(Typography27, { fontSize: 13, color: "text.secondary", sx: { mt: -1 }, children: "OAuth credentials for HopDrive REST API access." }),
    /* @__PURE__ */ jsx36(
      SubOption20,
      {
        title: "OAuth Client ID",
        description: "Client ID for OAuth authentication. This value is not encrypted.",
        inline: true,
        children: /* @__PURE__ */ jsx36(
          TextField23,
          {
            value: oauthClientId,
            onChange: handleOauthClientIdChange,
            onBlur: handleOauthClientIdBlur,
            disabled,
            fullWidth: true,
            placeholder: "Enter OAuth Client ID...",
            size: "small",
            sx: { maxWidth: 500 },
            InputProps: {
              startAdornment: /* @__PURE__ */ jsx36(InputAdornment12, { position: "start", children: /* @__PURE__ */ jsx36(KeyIcon2, { fontSize: "small", color: "disabled" }) })
            }
          }
        )
      }
    ),
    /* @__PURE__ */ jsx36(
      EncryptedField,
      {
        label: "OAuth Client Secret",
        description: "Client secret for OAuth authentication.",
        value: apiConfig.oauth_client_secret ?? "",
        onChange: (newValue) => updateField("oauth_client_secret", newValue),
        disabled
      }
    )
  ] });
}

// src/definitions/arrival-policy.renderer.tsx
import { useEffect as useEffect22, useState as useState23 } from "react";
import { Box as Box21, Checkbox as Checkbox3, FormControlLabel as FormControlLabel5, ListItemText as ListItemText2, MenuItem as MenuItem11, TextField as TextField24, Typography as Typography28 } from "@mui/material";
import { jsx as jsx37, jsxs as jsxs32 } from "react/jsx-runtime";
var MODE_OPTIONS = {
  automaticWithManual: {
    label: "Automatic, with manual",
    description: "GPS can advance the move, and the driver can always advance it too. The default."
  },
  manualOnly: {
    label: "Manual only",
    description: "GPS crossings are logged but never move the workflow, and optimistic GPS writes are off."
  },
  automaticOnly: {
    label: "Automatic only",
    description: "GPS enforcement, no manual control. Driver app 3 runs this as Automatic, with manual."
  },
  automaticWithDelayedManual: {
    label: "Automatic, manual after a delay",
    description: "The manual control opens after a delay. Driver app 3 runs this as Automatic, with manual."
  }
};
var MODE_ORDER = ["automaticWithManual", "manualOnly", "automaticOnly", "automaticWithDelayedManual"];
var currentMode = (value) => {
  const mode = value?.mode;
  return typeof mode === "string" && TRANSITION_MODES.includes(mode) ? mode : "";
};
var delayMinutesOf = (value) => {
  const ms = value?.manualDelayMs;
  return typeof ms === "number" && ms > 0 ? String(ms / 6e4) : "";
};
var buildArrivalPolicy = (mode, { acknowledged, delayMinutes }) => {
  const candidate = mode === "automaticOnly" ? { mode, acceptsStrandingWhenSensorFails: acknowledged ? true : void 0 } : mode === "automaticWithDelayedManual" ? { mode, manualDelayMs: Math.round(Number(delayMinutes) * 6e4) } : { mode };
  const parsed = transitionPolicySchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
};
function ArrivalPolicyRenderer({ value, onChange, disabled }) {
  const [mode, setMode] = useState23(currentMode(value));
  const [acknowledged, setAcknowledged] = useState23(currentMode(value) === "automaticOnly");
  const [delayMinutes, setDelayMinutes] = useState23(delayMinutesOf(value));
  useEffect22(() => {
    setMode(currentMode(value));
    setAcknowledged(currentMode(value) === "automaticOnly");
    setDelayMinutes(delayMinutesOf(value));
  }, [value]);
  const commit = (next) => {
    if (!next.mode) return;
    const block = buildArrivalPolicy(next.mode, next);
    if (block) onChange(block);
  };
  const pendingInvalid = mode !== "" && mode !== currentMode(value) && !buildArrivalPolicy(mode, { acknowledged, delayMinutes });
  return /* @__PURE__ */ jsxs32(Box21, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: [
    /* @__PURE__ */ jsx37(
      TextField24,
      {
        select: true,
        label: "Mode",
        value: mode,
        onChange: (e) => {
          const next = e.target.value;
          setMode(next);
          commit({ mode: next, acknowledged, delayMinutes });
        },
        variant: "outlined",
        size: "small",
        disabled,
        sx: { width: 360 },
        children: MODE_ORDER.map((m) => /* @__PURE__ */ jsx37(MenuItem11, { value: m, children: /* @__PURE__ */ jsx37(
          ListItemText2,
          {
            primary: MODE_OPTIONS[m].label,
            secondary: MODE_OPTIONS[m].description,
            secondaryTypographyProps: { variant: "caption", sx: { whiteSpace: "normal" } }
          }
        ) }, m))
      }
    ),
    mode === "automaticOnly" && /* @__PURE__ */ jsx37(
      FormControlLabel5,
      {
        control: /* @__PURE__ */ jsx37(
          Checkbox3,
          {
            checked: acknowledged,
            disabled,
            onChange: (e) => {
              setAcknowledged(e.target.checked);
              commit({ mode, acknowledged: e.target.checked, delayMinutes });
            }
          }
        ),
        label: "I accept that with a dead fence or no location permission the driver has no manual control"
      }
    ),
    mode === "automaticWithDelayedManual" && /* @__PURE__ */ jsx37(
      TextField24,
      {
        label: "Manual control opens after (minutes)",
        type: "number",
        value: delayMinutes,
        disabled,
        size: "small",
        inputProps: { min: 0.5, step: 0.5 },
        onChange: (e) => {
          setDelayMinutes(e.target.value);
          commit({ mode, acknowledged, delayMinutes: e.target.value });
        },
        sx: { width: 260 }
      }
    ),
    pendingInvalid && /* @__PURE__ */ jsxs32(Typography28, { variant: "caption", color: "warning.main", children: [
      "Not saved yet: ",
      mode === "automaticOnly" ? "tick the acknowledgement" : "enter a delay above zero",
      " to switch to",
      " ",
      MODE_OPTIONS[mode].label,
      "."
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/index.tsx
import { useCallback as useCallback33, useMemo as useMemo4, useState as useState34 } from "react";
import {
  Box as Box35,
  Typography as Typography41,
  Collapse as Collapse5,
  IconButton as IconButton11,
  Paper as Paper9,
  Divider as Divider11,
  Tooltip as Tooltip9,
  Badge as Badge2
} from "@mui/material";
import { StyledSwitch as StyledSwitch16 } from "@hopdrive/sdk-ui";
import {
  ExpandMore as ExpandMoreIcon6,
  ExpandLess as ExpandLessIcon3,
  HelpOutline as HelpIcon3
} from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/constants.ts
var AUTH_TYPES = {
  OAUTH2: "oauth2_client_credentials",
  API_KEY: "api_key",
  BASIC: "basic"
};
var AUTH_TYPE_LABELS = {
  [AUTH_TYPES.OAUTH2]: "OAuth 2.0 Client Credentials",
  [AUTH_TYPES.API_KEY]: "API Key (Header)",
  [AUTH_TYPES.BASIC]: "Basic Auth"
};
var TRANSFORM_TYPES = [
  { value: "uppercase", label: "UPPERCASE", icon: "ABC", description: "Convert to uppercase", hasConfig: false },
  { value: "lowercase", label: "lowercase", icon: "abc", description: "Convert to lowercase", hasConfig: false },
  { value: "trim", label: "Trim", icon: "\u232B", description: "Remove whitespace", hasConfig: false },
  { value: "date_iso", label: "Date ISO", icon: "\u{1F4C5}", description: "Convert to ISO format", hasConfig: false },
  { value: "regex", label: "Regex", icon: "/.*/s", description: "Replace using regex", hasConfig: true },
  { value: "template", label: "Template", icon: "{}", description: "Format string with {value}", hasConfig: true },
  { value: "split", label: "Split", icon: "a,b", description: "Split and take index", hasConfig: true },
  { value: "concat", label: "Concat", icon: "+", description: "Add prefix/suffix", hasConfig: true },
  { value: "default", label: "Default", icon: "\u2205", description: "Default for null", hasConfig: true },
  { value: "date_format", label: "Date Format", icon: "\u{1F5D3}", description: "Format date", hasConfig: true }
];
var AUTOCOMPLETE_TRIGGER_TYPES = [
  { value: "consumer-name", label: "Consumer Name", description: "Show matching customer names as the user types in the customer name field." },
  { value: "consumer-phone", label: "Consumer Phone", description: "Show matching customers by phone number as the user types digits." },
  { value: "reference-number", label: "Reference Number", description: "Show matching reference numbers (contracts, ROs, etc.) as the user types." },
  { value: "consumer-vin", label: "Consumer VIN", description: "Show matching vehicles by VIN as the user types in the consumer vehicle VIN field." },
  { value: "loaner-vin", label: "Loaner VIN", description: "Show matching loaner vehicles by VIN as the user types." },
  { value: "stock-number", label: "Stock Number", description: "Show matching vehicles by dealer stock number." }
];
var FETCH_TRIGGER_TYPES = [
  { value: "contract-detail", label: "Contract Detail", description: "Fetch full contract details including customer info, vehicle info, and addresses when a reference number is entered or selected." },
  { value: "consumer-detail", label: "Consumer Detail", description: "Fetch customer details (name, phone, email, address) when a customer is selected from autocomplete." },
  { value: "vehicle-detail", label: "Vehicle Detail", description: "Fetch vehicle details (VIN, year, make, model, color) when a VIN is entered or selected." }
];
var AUTOCOMPLETE_MAPPING_FIELDS = [
  { key: "id", label: "ID", required: true, description: "Unique identifier for the result" },
  { key: "displayValue", label: "Display Value", required: true, description: "Primary text shown in dropdown" },
  { key: "subtext", label: "Subtext", required: false, description: "Secondary text shown below display value" }
];
var FETCH_MAPPING_SCHEMAS = {
  "contract-detail": [
    { key: "id", label: "ID" },
    { key: "displayLabel", label: "Display Label" },
    { key: "customerName", label: "Customer Name" },
    { key: "customerPhone", label: "Customer Phone" },
    { key: "customerEmail", label: "Customer Email" },
    { key: "vin", label: "VIN" },
    { key: "year", label: "Year" },
    { key: "make", label: "Make" },
    { key: "model", label: "Model" },
    { key: "color", label: "Color" },
    { key: "customerAddressLine1", label: "Address Line 1" },
    { key: "customerAddressCity", label: "City" },
    { key: "customerAddressState", label: "State" },
    { key: "customerAddressZip", label: "ZIP" }
  ],
  "consumer-detail": [
    { key: "customerName", label: "Customer Name" },
    { key: "customerPhone", label: "Phone" },
    { key: "customerEmail", label: "Email" },
    { key: "customerAddressLine1", label: "Address Line 1" },
    { key: "customerAddressCity", label: "City" },
    { key: "customerAddressState", label: "State" },
    { key: "customerAddressZip", label: "ZIP" }
  ],
  "vehicle-detail": [
    { key: "vin", label: "VIN" },
    { key: "year", label: "Year" },
    { key: "make", label: "Make" },
    { key: "model", label: "Model" },
    { key: "color", label: "Color" },
    { key: "stockNumber", label: "Stock Number" }
  ]
};
var HTTP_METHODS = ["GET", "POST", "PUT", "PATCH"];
var AUTOCOMPLETE_HTTP_METHODS = ["GET", "POST"];
var ENRICHMENT_HTTP_METHODS = ["GET", "POST", "PUT", "PATCH"];
var DEFAULT_CONFIG = {
  enabled: false,
  secrets: {},
  authProviders: {},
  autocompleteTriggers: {},
  fetchTriggers: {}
};
var DEFAULT_AUTOCOMPLETE_TRIGGER = {
  enabled: false,
  httpMethod: "GET",
  minChars: 3,
  debounceMs: 300,
  maxResults: 10,
  mappings: {
    id: { source: "" },
    displayValue: { source: "" }
  }
};
var DEFAULT_FETCH_TRIGGER = {
  enabled: false,
  httpMethod: "GET",
  overwriteExisting: false,
  mappings: {},
  enrichments: []
};
var DEFAULT_ENRICHMENT = {
  httpMethod: "GET",
  endpoint: "",
  mappings: {}
};
var ENRICHMENT_VARIABLE_HELP = {
  lookup_value: {
    syntax: "{lookup_value}",
    description: "Original lookup value that triggered the fetch"
  },
  primary_field: {
    syntax: "{primary.fieldName}",
    description: "Mapped field from primary response (e.g., {primary.customerName})"
  },
  primary_raw: {
    syntax: "{primary_raw.$.path}",
    description: "JSONPath from raw primary response (e.g., {primary_raw.$.data.id})"
  }
};
var ENRICHMENT_MAPPING_FIELDS = [
  { key: "id", label: "ID" },
  { key: "displayLabel", label: "Display Label" },
  { key: "customerName", label: "Customer Name" },
  { key: "customerPhone", label: "Customer Phone" },
  { key: "customerEmail", label: "Customer Email" },
  { key: "vin", label: "VIN" },
  { key: "year", label: "Year" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "color", label: "Color" },
  { key: "stockNumber", label: "Stock Number" },
  { key: "customerAddressLine1", label: "Address Line 1" },
  { key: "customerAddressCity", label: "City" },
  { key: "customerAddressState", label: "State" },
  { key: "customerAddressZip", label: "ZIP" },
  { key: "loanerVin", label: "Loaner VIN" },
  { key: "loanerYear", label: "Loaner Year" },
  { key: "loanerMake", label: "Loaner Make" },
  { key: "loanerModel", label: "Loaner Model" },
  { key: "loanerColor", label: "Loaner Color" }
];

// src/definitions/connector-triggers.renderer/utils.ts
function mergeWithDefaults(config) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    secrets: config?.secrets || {},
    authProviders: config?.authProviders || {},
    autocompleteTriggers: config?.autocompleteTriggers || {},
    fetchTriggers: config?.fetchTriggers || {}
  };
}
function formatSecretRef(name) {
  return `{${name.toLowerCase()}}`;
}
function validateSecretName(name) {
  if (!name) return "Name is required";
  if (!/^[a-z][a-z0-9_]*$/.test(name)) {
    return "Use lowercase_with_underscores (e.g., client_id)";
  }
  return null;
}
function validateProviderName(name) {
  if (!name) return "Name is required";
  if (!/^[a-z][a-z0-9-]*$/.test(name)) {
    return "Use lowercase-with-dashes";
  }
  return null;
}
function getAuthTypeLabel(provider) {
  const labels = {
    oauth2_client_credentials: "OAuth 2.0 Client Credentials",
    api_key: "API Key (Header)",
    basic: "Basic Auth"
  };
  return labels[provider.type] || provider.type;
}
function countItems(obj) {
  return Object.keys(obj || {}).length;
}
function getAuthProviderReferences(providerName, config) {
  const refs = [];
  Object.entries(config.autocompleteTriggers || {}).forEach(([name, trigger]) => {
    if (trigger.authProvider === providerName && trigger.enabled) {
      refs.push(name);
    }
  });
  Object.entries(config.fetchTriggers || {}).forEach(([name, trigger]) => {
    if (trigger.authProvider === providerName && trigger.enabled) {
      refs.push(name);
    }
  });
  return refs;
}
function isAuthProviderInUse(providerName, config) {
  return getAuthProviderReferences(providerName, config).length > 0;
}

// src/definitions/connector-triggers.renderer/components/SecretsSection.tsx
import { useState as useState25, useCallback as useCallback22 } from "react";
import {
  Box as Box23,
  Typography as Typography29,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton as IconButton4,
  Button as Button6,
  Chip as Chip4,
  CircularProgress as CircularProgress10,
  Tooltip as Tooltip4,
  Alert as Alert13
} from "@mui/material";
import {
  Add as AddIcon2,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Delete as DeleteIcon2,
  Lock as LockIcon,
  Warning as WarningIcon2
} from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/SecretEditModal.tsx
import { useState as useState24, useEffect as useEffect23, useCallback as useCallback21 } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as Button5,
  TextField as TextField25,
  Alert as Alert12,
  CircularProgress as CircularProgress9,
  Box as Box22
} from "@mui/material";
import { jsx as jsx38, jsxs as jsxs33 } from "react/jsx-runtime";
function SecretEditModal({
  open,
  onClose,
  onSave,
  existingSecret,
  existingNames
}) {
  const secrets = useHostServices().secrets;
  const [name, setName] = useState24("");
  const [value, setValue] = useState24("");
  const [errors, setErrors] = useState24({});
  const [isEncrypting, setIsEncrypting] = useState24(false);
  const [encryptionError, setEncryptionError] = useState24(null);
  const isEditing = !!existingSecret;
  useEffect23(() => {
    if (open) {
      if (existingSecret) {
        setName(existingSecret.name);
        setValue("");
      } else {
        setName("");
        setValue("");
      }
      setErrors({});
      setEncryptionError(null);
    }
  }, [open, existingSecret]);
  const validate = useCallback21(() => {
    const newErrors = {};
    const nameError = validateSecretName(name);
    if (nameError) {
      newErrors.name = nameError;
    } else if (!isEditing && existingNames.includes(name)) {
      newErrors.name = "Secret name already exists";
    } else if (isEditing && name !== existingSecret?.name && existingNames.includes(name)) {
      newErrors.name = "Secret name already exists";
    }
    if (!value) {
      newErrors.value = "Value is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, value, isEditing, existingSecret, existingNames]);
  const handleSave = useCallback21(async () => {
    if (!validate()) return;
    setIsEncrypting(true);
    setEncryptionError(null);
    try {
      const encryptedValue = await secrets.encrypt(value);
      onSave(name, encryptedValue);
    } catch (error) {
      console.error("Failed to encrypt secret:", error);
      setEncryptionError(
        error instanceof Error ? error.message : "Failed to encrypt secret. Please try again."
      );
    } finally {
      setIsEncrypting(false);
    }
  }, [name, value, validate, onSave, secrets]);
  const handleNameChange = useCallback21((e) => {
    setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"));
  }, []);
  return /* @__PURE__ */ jsxs33(Dialog, { open, onClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx38(DialogTitle, { children: isEditing ? "Update Secret" : "Add Secret" }),
    /* @__PURE__ */ jsxs33(DialogContent, { children: [
      /* @__PURE__ */ jsxs33(Alert12, { severity: "info", sx: { mb: 2 }, children: [
        "Secrets are encrypted server-side using AES-256-GCM before being stored. Reference them in auth providers using",
        " ",
        /* @__PURE__ */ jsx38("code", { style: { background: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }, children: "{secret_name}" }),
        " ",
        "syntax."
      ] }),
      encryptionError && /* @__PURE__ */ jsx38(Alert12, { severity: "error", sx: { mb: 2 }, children: encryptionError }),
      /* @__PURE__ */ jsx38(
        TextField25,
        {
          label: "Secret Key",
          value: name,
          onChange: handleNameChange,
          fullWidth: true,
          margin: "normal",
          placeholder: "client_secret",
          error: !!errors.name,
          helperText: errors.name || "Use lowercase_with_underscores (e.g., client_id)",
          disabled: isEditing || isEncrypting,
          inputProps: { style: { fontFamily: "monospace" } },
          autoFocus: true
        }
      ),
      /* @__PURE__ */ jsx38(
        TextField25,
        {
          label: "Secret Value",
          value,
          onChange: (e) => setValue(e.target.value),
          fullWidth: true,
          margin: "normal",
          type: "password",
          placeholder: isEditing ? "Enter new value to update" : "Enter secret value",
          error: !!errors.value,
          helperText: errors.value || (isEditing ? "Enter a new value to replace the existing secret" : "This value will be encrypted before being stored"),
          disabled: isEncrypting
        }
      )
    ] }),
    /* @__PURE__ */ jsxs33(DialogActions, { children: [
      /* @__PURE__ */ jsx38(Button5, { onClick: onClose, disabled: isEncrypting, children: "Cancel" }),
      /* @__PURE__ */ jsx38(
        Button5,
        {
          onClick: handleSave,
          variant: "contained",
          color: "primary",
          disabled: isEncrypting,
          children: isEncrypting ? /* @__PURE__ */ jsxs33(Box22, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
            /* @__PURE__ */ jsx38(CircularProgress9, { size: 16, color: "inherit" }),
            "Encrypting..."
          ] }) : isEditing ? "Update" : "Add"
        }
      )
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/components/SecretsSection.tsx
import { jsx as jsx39, jsxs as jsxs34 } from "react/jsx-runtime";
function SecretsSection({
  config,
  onChange,
  disabled = false
}) {
  const secretsApi = useHostServices().secrets;
  const secrets = config.secrets || {};
  const secretEntries = Object.entries(secrets);
  const [modalOpen, setModalOpen] = useState25(false);
  const [editingSecret, setEditingSecret] = useState25(null);
  const [decryptedSecrets, setDecryptedSecrets] = useState25({});
  const [revealingSecret, setRevealingSecret] = useState25(null);
  const [decryptError, setDecryptError] = useState25(null);
  const toggleVisibility = useCallback22(async (name, encryptedValue) => {
    if (decryptedSecrets[name] !== void 0) {
      setDecryptedSecrets((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    } else {
      setRevealingSecret(name);
      setDecryptError(null);
      try {
        const decrypted = await secretsApi.decrypt(encryptedValue);
        setDecryptedSecrets((prev) => ({ ...prev, [name]: decrypted }));
      } catch (error) {
        console.error("Failed to decrypt secret:", error);
        setDecryptError(
          `Failed to decrypt "${name}": ${error instanceof Error ? error.message : "Unknown error"}`
        );
      } finally {
        setRevealingSecret(null);
      }
    }
  }, [decryptedSecrets, secretsApi]);
  const handleAdd = useCallback22(() => {
    setEditingSecret(null);
    setModalOpen(true);
  }, []);
  const handleDelete = useCallback22((name) => {
    if (window.confirm(`Delete secret "${name}"? This may break auth providers that reference it.`)) {
      const newSecrets = { ...secrets };
      delete newSecrets[name];
      onChange({ secrets: newSecrets });
      setDecryptedSecrets((prev) => {
        const { [name]: _removed, ...rest } = prev;
        return rest;
      });
    }
  }, [secrets, onChange]);
  const handleSave = useCallback22((name, encryptedValue) => {
    const newSecrets = { ...secrets };
    newSecrets[name] = encryptedValue;
    onChange({ secrets: newSecrets });
    setModalOpen(false);
  }, [secrets, onChange]);
  const isProperlyEncrypted = (value) => {
    return secretsApi.isEncrypted(value);
  };
  if (secretEntries.length === 0) {
    return /* @__PURE__ */ jsxs34(Box23, { children: [
      /* @__PURE__ */ jsx39(
        Box23,
        {
          sx: {
            p: 3,
            textAlign: "center",
            bgcolor: "grey.50",
            borderRadius: 1,
            mb: 2
          },
          children: /* @__PURE__ */ jsx39(Typography29, { color: "text.secondary", children: "No secrets configured. Add secrets to store API credentials securely." })
        }
      ),
      /* @__PURE__ */ jsx39(
        Button6,
        {
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx39(AddIcon2, {}),
          onClick: handleAdd,
          disabled,
          children: "Add Secret"
        }
      ),
      /* @__PURE__ */ jsx39(
        SecretEditModal,
        {
          open: modalOpen,
          onClose: () => setModalOpen(false),
          onSave: handleSave,
          existingSecret: editingSecret,
          existingNames: Object.keys(secrets)
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs34(Box23, { children: [
    decryptError && /* @__PURE__ */ jsx39(Alert13, { severity: "error", sx: { mb: 2 }, onClose: () => setDecryptError(null), children: decryptError }),
    /* @__PURE__ */ jsxs34(Table, { size: "small", sx: { mb: 2 }, children: [
      /* @__PURE__ */ jsx39(TableHead, { children: /* @__PURE__ */ jsxs34(TableRow, { children: [
        /* @__PURE__ */ jsx39(TableCell, { children: "Key" }),
        /* @__PURE__ */ jsx39(TableCell, { children: "Value" }),
        /* @__PURE__ */ jsx39(TableCell, { align: "right", width: 160, children: "Actions" })
      ] }) }),
      /* @__PURE__ */ jsx39(TableBody, { children: secretEntries.map(([name, encryptedValue]) => {
        const isRevealed = decryptedSecrets[name] !== void 0;
        const isEncrypted = isProperlyEncrypted(encryptedValue);
        const isDecrypting = revealingSecret === name;
        return /* @__PURE__ */ jsxs34(TableRow, { children: [
          /* @__PURE__ */ jsx39(TableCell, { children: /* @__PURE__ */ jsxs34(Box23, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
            /* @__PURE__ */ jsx39(
              Chip4,
              {
                label: name,
                size: "small",
                sx: { fontFamily: "monospace", fontSize: "0.8rem" }
              }
            ),
            isEncrypted ? /* @__PURE__ */ jsx39(Tooltip4, { title: "Encrypted with AES-256-GCM", children: /* @__PURE__ */ jsx39(LockIcon, { fontSize: "small", color: "success" }) }) : /* @__PURE__ */ jsx39(Tooltip4, { title: "Warning: Value is not encrypted! Re-save to encrypt.", children: /* @__PURE__ */ jsx39(WarningIcon2, { fontSize: "small", color: "warning" }) })
          ] }) }),
          /* @__PURE__ */ jsx39(TableCell, { children: /* @__PURE__ */ jsx39(
            Typography29,
            {
              variant: "body2",
              sx: { fontFamily: "monospace" },
              children: isRevealed ? decryptedSecrets[name] : secretsApi.mask(encryptedValue)
            }
          ) }),
          /* @__PURE__ */ jsxs34(TableCell, { align: "right", children: [
            /* @__PURE__ */ jsx39(
              Button6,
              {
                size: "small",
                onClick: () => toggleVisibility(name, encryptedValue),
                disabled: isDecrypting || !isEncrypted,
                startIcon: isDecrypting ? /* @__PURE__ */ jsx39(CircularProgress10, { size: 14 }) : isRevealed ? /* @__PURE__ */ jsx39(VisibilityOffIcon, { fontSize: "small" }) : /* @__PURE__ */ jsx39(VisibilityIcon, { fontSize: "small" }),
                sx: { mr: 1, minWidth: 80 },
                children: isDecrypting ? "Decrypting" : isRevealed ? "Hide" : "Show"
              }
            ),
            /* @__PURE__ */ jsx39(
              IconButton4,
              {
                size: "small",
                onClick: () => handleDelete(name),
                disabled,
                color: "error",
                title: "Delete",
                children: /* @__PURE__ */ jsx39(DeleteIcon2, { fontSize: "small" })
              }
            )
          ] })
        ] }, name);
      }) })
    ] }),
    /* @__PURE__ */ jsx39(
      Button6,
      {
        variant: "outlined",
        startIcon: /* @__PURE__ */ jsx39(AddIcon2, {}),
        onClick: handleAdd,
        disabled,
        children: "Add Secret"
      }
    ),
    /* @__PURE__ */ jsx39(
      SecretEditModal,
      {
        open: modalOpen,
        onClose: () => setModalOpen(false),
        onSave: handleSave,
        existingSecret: editingSecret,
        existingNames: Object.keys(secrets)
      }
    )
  ] });
}

// src/definitions/connector-triggers.renderer/components/AuthProvidersSection.tsx
import { useState as useState27, useCallback as useCallback24 } from "react";
import { Box as Box26, Typography as Typography32, Button as Button8, Grid } from "@mui/material";
import { Add as AddIcon3 } from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/AuthProviderCard.tsx
import {
  Box as Box24,
  Typography as Typography30,
  IconButton as IconButton5,
  Paper as Paper4,
  Chip as Chip5,
  Tooltip as Tooltip5
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon3
} from "@mui/icons-material";
import { jsx as jsx40, jsxs as jsxs35 } from "react/jsx-runtime";
function AuthProviderCard({
  name,
  config,
  allConfig,
  onEdit,
  onDelete,
  disabled = false
}) {
  const typeLabel = getAuthTypeLabel(config);
  const isInUse = isAuthProviderInUse(name, allConfig);
  const references = getAuthProviderReferences(name, allConfig);
  const getDomain = () => {
    if (config.type === "oauth2_client_credentials" && config.tokenEndpoint) {
      try {
        const url = new URL(config.tokenEndpoint);
        return url.hostname;
      } catch {
        return config.tokenEndpoint;
      }
    }
    return null;
  };
  const domain = getDomain();
  const handleDelete = () => {
    if (isInUse) {
      window.alert(
        `Cannot delete "${name}" because it is used by: ${references.join(", ")}.

Disable or reconfigure those triggers first.`
      );
      return;
    }
    if (window.confirm(`Delete auth provider "${name}"?`)) {
      onDelete();
    }
  };
  return /* @__PURE__ */ jsxs35(
    Paper4,
    {
      variant: "outlined",
      sx: {
        p: 2,
        bgcolor: "grey.50",
        display: "flex",
        flexDirection: "column",
        gap: 1
      },
      children: [
        /* @__PURE__ */ jsxs35(Box24, { sx: { display: "flex", alignItems: "flex-start", justifyContent: "space-between" }, children: [
          /* @__PURE__ */ jsxs35(Box24, { children: [
            /* @__PURE__ */ jsx40(Typography30, { variant: "subtitle1", fontWeight: 600, children: name }),
            /* @__PURE__ */ jsx40(Typography30, { variant: "body2", color: "text.secondary", children: typeLabel })
          ] }),
          /* @__PURE__ */ jsxs35(Box24, { children: [
            /* @__PURE__ */ jsx40(IconButton5, { size: "small", onClick: onEdit, disabled, title: "Edit", children: /* @__PURE__ */ jsx40(EditIcon, { fontSize: "small" }) }),
            /* @__PURE__ */ jsx40(
              Tooltip5,
              {
                title: isInUse ? `Used by: ${references.join(", ")}` : "Delete",
                children: /* @__PURE__ */ jsx40("span", { children: /* @__PURE__ */ jsx40(
                  IconButton5,
                  {
                    size: "small",
                    onClick: handleDelete,
                    disabled,
                    color: isInUse ? "default" : "error",
                    children: /* @__PURE__ */ jsx40(DeleteIcon3, { fontSize: "small" })
                  }
                ) })
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs35(Box24, { sx: { display: "flex", flexDirection: "column", gap: 0.5 }, children: [
          domain && /* @__PURE__ */ jsx40(Typography30, { variant: "body2", color: "text.secondary", children: domain }),
          config.type === "oauth2_client_credentials" && config.cacheTtlSeconds && /* @__PURE__ */ jsxs35(Typography30, { variant: "body2", color: "text.secondary", children: [
            "Token cached for ",
            config.cacheTtlSeconds,
            "s"
          ] }),
          config.type === "api_key" && /* @__PURE__ */ jsxs35(Typography30, { variant: "body2", color: "text.secondary", children: [
            "Header: ",
            config.headerName
          ] })
        ] }),
        references.length > 0 && /* @__PURE__ */ jsxs35(
          Box24,
          {
            sx: {
              mt: 1,
              p: 1,
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider"
            },
            children: [
              /* @__PURE__ */ jsx40(Typography30, { variant: "caption", color: "text.secondary", sx: { mr: 1 }, children: "Used by:" }),
              references.map((ref) => /* @__PURE__ */ jsx40(
                Chip5,
                {
                  label: ref,
                  size: "small",
                  sx: { mr: 0.5, mb: 0.5 }
                },
                ref
              ))
            ]
          }
        )
      ]
    }
  );
}

// src/definitions/connector-triggers.renderer/components/AuthProviderModal.tsx
import { useState as useState26, useEffect as useEffect24, useCallback as useCallback23 } from "react";
import {
  Dialog as Dialog2,
  DialogTitle as DialogTitle2,
  DialogContent as DialogContent2,
  DialogActions as DialogActions2,
  Button as Button7,
  TextField as TextField26,
  FormControl,
  InputLabel,
  Select as Select2,
  MenuItem as MenuItem12,
  Box as Box25,
  Typography as Typography31,
  Alert as Alert14,
  Chip as Chip6,
  Autocomplete as Autocomplete2
} from "@mui/material";
import { jsx as jsx41, jsxs as jsxs36 } from "react/jsx-runtime";
function AuthProviderModal({
  open,
  onClose,
  onSave,
  existingProvider,
  existingNames,
  secrets
}) {
  const [name, setName] = useState26("");
  const [authType, setAuthType] = useState26(AUTH_TYPES.OAUTH2);
  const [errors, setErrors] = useState26({});
  const [tokenEndpoint, setTokenEndpoint] = useState26("");
  const [clientId, setClientId] = useState26("");
  const [clientSecret, setClientSecret] = useState26("");
  const [audience, setAudience] = useState26("");
  const [scope, setScope] = useState26("");
  const [cacheTtlSeconds, setCacheTtlSeconds] = useState26("3600");
  const [headerName, setHeaderName] = useState26("");
  const [apiKey, setApiKey] = useState26("");
  const [username, setUsername] = useState26("");
  const [password, setPassword] = useState26("");
  const isEditing = !!existingProvider;
  const secretNames = Object.keys(secrets);
  useEffect24(() => {
    if (open) {
      if (existingProvider) {
        setName(existingProvider.name);
        setAuthType(existingProvider.config.type);
        if (existingProvider.config.type === "oauth2_client_credentials") {
          const cfg = existingProvider.config;
          setTokenEndpoint(cfg.tokenEndpoint || "");
          setClientId(cfg.clientId || "");
          setClientSecret(cfg.clientSecret || "");
          setAudience(cfg.audience || "");
          setScope(cfg.scope || "");
          setCacheTtlSeconds(String(cfg.cacheTtlSeconds || 3600));
        } else if (existingProvider.config.type === "api_key") {
          const cfg = existingProvider.config;
          setHeaderName(cfg.headerName || "");
          setApiKey(cfg.apiKey || "");
        } else if (existingProvider.config.type === "basic") {
          const cfg = existingProvider.config;
          setUsername(cfg.username || "");
          setPassword(cfg.password || "");
        }
      } else {
        setName("");
        setAuthType(AUTH_TYPES.OAUTH2);
        setTokenEndpoint("");
        setClientId("");
        setClientSecret("");
        setAudience("");
        setScope("");
        setCacheTtlSeconds("3600");
        setHeaderName("");
        setApiKey("");
        setUsername("");
        setPassword("");
      }
      setErrors({});
    }
  }, [open, existingProvider]);
  const validate = useCallback23(() => {
    const newErrors = {};
    const nameError = validateProviderName(name);
    if (nameError) {
      newErrors.name = nameError;
    } else if (!isEditing && existingNames.includes(name)) {
      newErrors.name = "Provider name already exists";
    } else if (isEditing && name !== existingProvider?.name && existingNames.includes(name)) {
      newErrors.name = "Provider name already exists";
    }
    if (authType === AUTH_TYPES.OAUTH2) {
      if (!tokenEndpoint) newErrors.tokenEndpoint = "Token endpoint is required";
      if (!clientId) newErrors.clientId = "Client ID is required";
      if (!clientSecret) newErrors.clientSecret = "Client secret is required";
    } else if (authType === AUTH_TYPES.API_KEY) {
      if (!headerName) newErrors.headerName = "Header name is required";
      if (!apiKey) newErrors.apiKey = "API key is required";
    } else if (authType === AUTH_TYPES.BASIC) {
      if (!username) newErrors.username = "Username is required";
      if (!password) newErrors.password = "Password is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, authType, tokenEndpoint, clientId, clientSecret, headerName, apiKey, username, password, isEditing, existingProvider, existingNames]);
  const handleSave = useCallback23(() => {
    if (!validate()) return;
    let config;
    if (authType === AUTH_TYPES.OAUTH2) {
      config = {
        type: "oauth2_client_credentials",
        tokenEndpoint,
        clientId,
        clientSecret,
        ...audience && { audience },
        ...scope && { scope },
        cacheTtlSeconds: parseInt(cacheTtlSeconds, 10) || 3600
      };
    } else if (authType === AUTH_TYPES.API_KEY) {
      config = {
        type: "api_key",
        headerName,
        apiKey
      };
    } else {
      config = {
        type: "basic",
        username,
        password
      };
    }
    onSave(name, config);
  }, [name, authType, tokenEndpoint, clientId, clientSecret, audience, scope, cacheTtlSeconds, headerName, apiKey, username, password, validate, onSave]);
  const handleNameChange = useCallback23((e) => {
    setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"));
  }, []);
  const renderSecretField = (label, value, setValue, error) => /* @__PURE__ */ jsx41(
    Autocomplete2,
    {
      freeSolo: true,
      options: secretNames.map((s) => formatSecretRef(s)),
      value,
      onInputChange: (_, newValue) => setValue(newValue),
      renderInput: (params) => /* @__PURE__ */ jsx41(
        TextField26,
        {
          ...params,
          label,
          fullWidth: true,
          margin: "normal",
          error: !!error,
          helperText: error || "Use {secret_name} to reference a secret",
          inputProps: {
            ...params.inputProps,
            style: { fontFamily: "monospace" }
          }
        }
      ),
      renderOption: (props, option) => /* @__PURE__ */ jsx41("li", { ...props, children: /* @__PURE__ */ jsx41(
        Chip6,
        {
          label: option,
          size: "small",
          color: "primary",
          variant: "outlined",
          sx: { fontFamily: "monospace" }
        }
      ) })
    }
  );
  return /* @__PURE__ */ jsxs36(Dialog2, { open, onClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsx41(DialogTitle2, { children: isEditing ? "Edit Auth Provider" : "Add Auth Provider" }),
    /* @__PURE__ */ jsxs36(DialogContent2, { children: [
      /* @__PURE__ */ jsx41(
        TextField26,
        {
          label: "Provider Name",
          value: name,
          onChange: handleNameChange,
          fullWidth: true,
          margin: "normal",
          placeholder: "dealerware-oauth",
          error: !!errors.name,
          helperText: errors.name || "Use lowercase-with-dashes",
          disabled: isEditing,
          inputProps: { style: { fontFamily: "monospace" } },
          autoFocus: true
        }
      ),
      /* @__PURE__ */ jsxs36(FormControl, { fullWidth: true, margin: "normal", children: [
        /* @__PURE__ */ jsx41(InputLabel, { children: "Auth Type" }),
        /* @__PURE__ */ jsx41(
          Select2,
          {
            value: authType,
            label: "Auth Type",
            onChange: (e) => setAuthType(e.target.value),
            disabled: isEditing,
            children: Object.entries(AUTH_TYPE_LABELS).map(([value, label]) => /* @__PURE__ */ jsx41(MenuItem12, { value, children: label }, value))
          }
        )
      ] }),
      authType === AUTH_TYPES.OAUTH2 && /* @__PURE__ */ jsxs36(Box25, { sx: { mt: 2 }, children: [
        /* @__PURE__ */ jsx41(Typography31, { variant: "subtitle2", color: "text.secondary", gutterBottom: true, children: "OAuth2 Client Credentials" }),
        /* @__PURE__ */ jsx41(
          TextField26,
          {
            label: "Token Endpoint",
            value: tokenEndpoint,
            onChange: (e) => setTokenEndpoint(e.target.value),
            fullWidth: true,
            margin: "normal",
            placeholder: "https://api.example.com/oauth/token",
            error: !!errors.tokenEndpoint,
            helperText: errors.tokenEndpoint
          }
        ),
        renderSecretField("Client ID", clientId, setClientId, errors.clientId),
        renderSecretField("Client Secret", clientSecret, setClientSecret, errors.clientSecret),
        /* @__PURE__ */ jsx41(
          TextField26,
          {
            label: "Audience (optional)",
            value: audience,
            onChange: (e) => setAudience(e.target.value),
            fullWidth: true,
            margin: "normal",
            placeholder: "https://api.example.com"
          }
        ),
        /* @__PURE__ */ jsx41(
          TextField26,
          {
            label: "Scope (optional)",
            value: scope,
            onChange: (e) => setScope(e.target.value),
            fullWidth: true,
            margin: "normal",
            placeholder: "read write"
          }
        ),
        /* @__PURE__ */ jsx41(
          TextField26,
          {
            label: "Cache TTL (seconds)",
            value: cacheTtlSeconds,
            onChange: (e) => setCacheTtlSeconds(e.target.value),
            fullWidth: true,
            margin: "normal",
            type: "number",
            inputProps: { min: 0, max: 86400 },
            helperText: "How long to cache the access token (default: 3600)"
          }
        )
      ] }),
      authType === AUTH_TYPES.API_KEY && /* @__PURE__ */ jsxs36(Box25, { sx: { mt: 2 }, children: [
        /* @__PURE__ */ jsx41(Typography31, { variant: "subtitle2", color: "text.secondary", gutterBottom: true, children: "API Key Authentication" }),
        /* @__PURE__ */ jsx41(
          TextField26,
          {
            label: "Header Name",
            value: headerName,
            onChange: (e) => setHeaderName(e.target.value),
            fullWidth: true,
            margin: "normal",
            placeholder: "X-API-Key",
            error: !!errors.headerName,
            helperText: errors.headerName || "The HTTP header to send the API key in"
          }
        ),
        renderSecretField("API Key", apiKey, setApiKey, errors.apiKey)
      ] }),
      authType === AUTH_TYPES.BASIC && /* @__PURE__ */ jsxs36(Box25, { sx: { mt: 2 }, children: [
        /* @__PURE__ */ jsx41(Typography31, { variant: "subtitle2", color: "text.secondary", gutterBottom: true, children: "Basic Authentication" }),
        /* @__PURE__ */ jsx41(Alert14, { severity: "info", sx: { mb: 2 }, children: "Credentials will be sent as Base64-encoded Authorization header." }),
        renderSecretField("Username", username, setUsername, errors.username),
        renderSecretField("Password", password, setPassword, errors.password)
      ] })
    ] }),
    /* @__PURE__ */ jsxs36(DialogActions2, { children: [
      /* @__PURE__ */ jsx41(Button7, { onClick: onClose, children: "Cancel" }),
      /* @__PURE__ */ jsx41(Button7, { onClick: handleSave, variant: "contained", color: "primary", children: isEditing ? "Save" : "Add" })
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/components/AuthProvidersSection.tsx
import { jsx as jsx42, jsxs as jsxs37 } from "react/jsx-runtime";
function AuthProvidersSection({
  config,
  onChange,
  disabled = false
}) {
  const authProviders = config.authProviders || {};
  const providerEntries = Object.entries(authProviders);
  const [modalOpen, setModalOpen] = useState27(false);
  const [editingProvider, setEditingProvider] = useState27(null);
  const handleAdd = useCallback24(() => {
    setEditingProvider(null);
    setModalOpen(true);
  }, []);
  const handleEdit = useCallback24((name, providerConfig) => {
    setEditingProvider({ name, config: providerConfig });
    setModalOpen(true);
  }, []);
  const handleDelete = useCallback24(
    (name) => {
      const newProviders = { ...authProviders };
      delete newProviders[name];
      onChange({ authProviders: newProviders });
    },
    [authProviders, onChange]
  );
  const handleSave = useCallback24(
    (name, providerConfig) => {
      const newProviders = { ...authProviders };
      if (editingProvider && editingProvider.name !== name) {
        delete newProviders[editingProvider.name];
      }
      newProviders[name] = providerConfig;
      onChange({ authProviders: newProviders });
      setModalOpen(false);
    },
    [authProviders, editingProvider, onChange]
  );
  if (providerEntries.length === 0) {
    return /* @__PURE__ */ jsxs37(Box26, { children: [
      /* @__PURE__ */ jsx42(
        Box26,
        {
          sx: {
            p: 3,
            textAlign: "center",
            bgcolor: "grey.50",
            borderRadius: 1,
            mb: 2
          },
          children: /* @__PURE__ */ jsx42(Typography32, { color: "text.secondary", children: "No auth providers configured. Add an auth provider to enable API authentication." })
        }
      ),
      /* @__PURE__ */ jsx42(
        Button8,
        {
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx42(AddIcon3, {}),
          onClick: handleAdd,
          disabled,
          children: "Add Auth Provider"
        }
      ),
      /* @__PURE__ */ jsx42(
        AuthProviderModal,
        {
          open: modalOpen,
          onClose: () => setModalOpen(false),
          onSave: handleSave,
          existingProvider: editingProvider,
          existingNames: Object.keys(authProviders),
          secrets: config.secrets || {}
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs37(Box26, { children: [
    /* @__PURE__ */ jsx42(Grid, { container: true, spacing: 2, sx: { mb: 2 }, children: providerEntries.map(([name, providerConfig]) => /* @__PURE__ */ jsx42(Grid, { size: 12, children: /* @__PURE__ */ jsx42(
      AuthProviderCard,
      {
        name,
        config: providerConfig,
        allConfig: config,
        onEdit: () => handleEdit(name, providerConfig),
        onDelete: () => handleDelete(name),
        disabled
      }
    ) }, name)) }),
    /* @__PURE__ */ jsx42(
      Button8,
      {
        variant: "outlined",
        startIcon: /* @__PURE__ */ jsx42(AddIcon3, {}),
        onClick: handleAdd,
        disabled,
        children: "Add Auth Provider"
      }
    ),
    /* @__PURE__ */ jsx42(
      AuthProviderModal,
      {
        open: modalOpen,
        onClose: () => setModalOpen(false),
        onSave: handleSave,
        existingProvider: editingProvider,
        existingNames: Object.keys(authProviders),
        secrets: config.secrets || {}
      }
    )
  ] });
}

// src/definitions/connector-triggers.renderer/components/TriggersSection.tsx
import { useState as useState33, useCallback as useCallback32 } from "react";
import {
  Box as Box34,
  Typography as Typography40,
  Button as Button14,
  Menu as Menu2,
  MenuItem as MenuItem16,
  ListItemText as ListItemText4
} from "@mui/material";
import { Add as AddIcon8 } from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/TriggerCard.tsx
import { useCallback as useCallback31 } from "react";
import {
  Box as Box33,
  Typography as Typography39,
  FormControlLabel as FormControlLabel6,
  Paper as Paper8,
  Collapse as Collapse4,
  TextField as TextField32,
  FormControl as FormControl3,
  InputLabel as InputLabel3,
  Select as Select4,
  MenuItem as MenuItem15,
  IconButton as IconButton10,
  Alert as Alert15,
  Divider as Divider10,
  Checkbox as Checkbox4,
  Tooltip as Tooltip8
} from "@mui/material";
import { StyledSwitch as StyledSwitch15 } from "@hopdrive/sdk-ui";
import { Delete as DeleteIcon8, Info as InfoIcon } from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/FieldMappingEditor.tsx
import { useState as useState29, useCallback as useCallback26 } from "react";
import {
  Box as Box28,
  Table as Table2,
  TableBody as TableBody2,
  TableCell as TableCell2,
  TableHead as TableHead2,
  TableRow as TableRow2,
  TextField as TextField28,
  Button as Button10,
  Chip as Chip8,
  Typography as Typography34
} from "@mui/material";
import { Add as AddIcon4 } from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/TransformChainModal.tsx
import { useState as useState28, useEffect as useEffect25, useCallback as useCallback25 } from "react";
import {
  Dialog as Dialog3,
  DialogTitle as DialogTitle3,
  DialogContent as DialogContent3,
  DialogActions as DialogActions3,
  Button as Button9,
  Box as Box27,
  Typography as Typography33,
  Paper as Paper5,
  IconButton as IconButton6,
  TextField as TextField27,
  Chip as Chip7,
  Grid as Grid2,
  Divider as Divider9
} from "@mui/material";
import {
  Delete as DeleteIcon4,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from "@mui/icons-material";
import { jsx as jsx43, jsxs as jsxs38 } from "react/jsx-runtime";
function TransformChainModal({
  open,
  onClose,
  fieldKey,
  transforms,
  onSave
}) {
  const [chain, setChain] = useState28([]);
  const [editingIndex, setEditingIndex] = useState28(null);
  useEffect25(() => {
    if (open) {
      setChain([...transforms]);
      setEditingIndex(null);
    }
  }, [open, transforms]);
  const handleAddTransform = useCallback25((type) => {
    const transformDef = TRANSFORM_TYPES.find((t) => t.value === type);
    if (!transformDef) return;
    let newTransform;
    switch (type) {
      case "regex":
        newTransform = { type: "regex", config: { pattern: "", replacement: "" } };
        break;
      case "template":
        newTransform = { type: "template", config: { template: "{value}" } };
        break;
      case "split":
        newTransform = { type: "split", config: { delimiter: ",", index: 0 } };
        break;
      case "concat":
        newTransform = { type: "concat", config: { prefix: "", suffix: "" } };
        break;
      case "default":
        newTransform = { type: "default", config: { value: "" } };
        break;
      case "date_format":
        newTransform = { type: "date_format", config: { outputFormat: "YYYY-MM-DD" } };
        break;
      default:
        newTransform = { type };
    }
    setChain((prev) => [...prev, newTransform]);
    if (transformDef.hasConfig) {
      setEditingIndex(chain.length);
    }
  }, [chain.length]);
  const handleRemove = useCallback25((index) => {
    setChain((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  }, [editingIndex]);
  const handleMoveUp = useCallback25((index) => {
    if (index === 0) return;
    setChain((prev) => {
      const newChain = [...prev];
      [newChain[index - 1], newChain[index]] = [newChain[index], newChain[index - 1]];
      return newChain;
    });
  }, []);
  const handleMoveDown = useCallback25((index) => {
    if (index === chain.length - 1) return;
    setChain((prev) => {
      const newChain = [...prev];
      [newChain[index], newChain[index + 1]] = [newChain[index + 1], newChain[index]];
      return newChain;
    });
  }, [chain.length]);
  const handleConfigChange = useCallback25((index, config) => {
    setChain((prev) => {
      const newChain = [...prev];
      newChain[index] = { ...newChain[index], config };
      return newChain;
    });
  }, []);
  const getTransformLabel = (transform) => {
    const def = TRANSFORM_TYPES.find((t) => t.value === transform.type);
    return def?.label || transform.type;
  };
  const handleSave = useCallback25(() => {
    onSave(chain);
  }, [chain, onSave]);
  const renderConfigEditor = (transform, index) => {
    if (!("config" in transform)) return null;
    switch (transform.type) {
      case "regex":
        return /* @__PURE__ */ jsxs38(Box27, { sx: { display: "flex", gap: 1, mt: 1 }, children: [
          /* @__PURE__ */ jsx43(
            TextField27,
            {
              label: "Pattern",
              value: transform.config.pattern,
              onChange: (e) => handleConfigChange(index, { ...transform.config, pattern: e.target.value }),
              size: "small",
              placeholder: "[0-9]+",
              sx: { flex: 1 }
            }
          ),
          /* @__PURE__ */ jsx43(
            TextField27,
            {
              label: "Replacement",
              value: transform.config.replacement || "",
              onChange: (e) => handleConfigChange(index, { ...transform.config, replacement: e.target.value }),
              size: "small",
              placeholder: "",
              sx: { flex: 1 }
            }
          )
        ] });
      case "template":
        return /* @__PURE__ */ jsx43(
          TextField27,
          {
            label: "Template",
            value: transform.config.template,
            onChange: (e) => handleConfigChange(index, { template: e.target.value }),
            size: "small",
            fullWidth: true,
            placeholder: "Hello, {value}!",
            helperText: "Use {value} as placeholder",
            sx: { mt: 1 }
          }
        );
      case "split":
        return /* @__PURE__ */ jsxs38(Box27, { sx: { display: "flex", gap: 1, mt: 1 }, children: [
          /* @__PURE__ */ jsx43(
            TextField27,
            {
              label: "Delimiter",
              value: transform.config.delimiter,
              onChange: (e) => handleConfigChange(index, { ...transform.config, delimiter: e.target.value }),
              size: "small",
              placeholder: ",",
              sx: { width: 100 }
            }
          ),
          /* @__PURE__ */ jsx43(
            TextField27,
            {
              label: "Index",
              type: "number",
              value: transform.config.index,
              onChange: (e) => handleConfigChange(index, { ...transform.config, index: parseInt(e.target.value, 10) }),
              size: "small",
              inputProps: { min: 0 },
              sx: { width: 100 }
            }
          )
        ] });
      case "concat":
        return /* @__PURE__ */ jsxs38(Box27, { sx: { display: "flex", gap: 1, mt: 1 }, children: [
          /* @__PURE__ */ jsx43(
            TextField27,
            {
              label: "Prefix",
              value: transform.config.prefix || "",
              onChange: (e) => handleConfigChange(index, { ...transform.config, prefix: e.target.value }),
              size: "small",
              sx: { flex: 1 }
            }
          ),
          /* @__PURE__ */ jsx43(
            TextField27,
            {
              label: "Suffix",
              value: transform.config.suffix || "",
              onChange: (e) => handleConfigChange(index, { ...transform.config, suffix: e.target.value }),
              size: "small",
              sx: { flex: 1 }
            }
          )
        ] });
      case "default":
        return /* @__PURE__ */ jsx43(
          TextField27,
          {
            label: "Default Value",
            value: transform.config.value,
            onChange: (e) => handleConfigChange(index, { value: e.target.value }),
            size: "small",
            fullWidth: true,
            placeholder: "N/A",
            helperText: "Value to use when source is null/undefined",
            sx: { mt: 1 }
          }
        );
      case "date_format":
        return /* @__PURE__ */ jsx43(
          TextField27,
          {
            label: "Output Format",
            value: transform.config.outputFormat,
            onChange: (e) => handleConfigChange(index, { outputFormat: e.target.value }),
            size: "small",
            fullWidth: true,
            placeholder: "YYYY-MM-DD",
            helperText: "Date format string (e.g., MM/DD/YYYY)",
            sx: { mt: 1 }
          }
        );
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsxs38(Dialog3, { open, onClose, maxWidth: "sm", fullWidth: true, children: [
    /* @__PURE__ */ jsxs38(DialogTitle3, { children: [
      'Edit Transforms for "',
      fieldKey,
      '"'
    ] }),
    /* @__PURE__ */ jsxs38(DialogContent3, { children: [
      chain.length > 0 && /* @__PURE__ */ jsxs38(Box27, { sx: { mb: 3 }, children: [
        /* @__PURE__ */ jsx43(Typography33, { variant: "subtitle2", gutterBottom: true, children: "Transform Chain" }),
        /* @__PURE__ */ jsx43(Box27, { sx: { display: "flex", flexDirection: "column", gap: 1 }, children: chain.map((transform, index) => /* @__PURE__ */ jsxs38(
          Paper5,
          {
            variant: "outlined",
            sx: {
              p: 1.5,
              bgcolor: editingIndex === index ? "primary.50" : "grey.50"
            },
            children: [
              /* @__PURE__ */ jsxs38(Box27, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
                /* @__PURE__ */ jsx43(
                  Chip7,
                  {
                    label: `${index + 1}. ${getTransformLabel(transform)}`,
                    color: "primary",
                    size: "small",
                    onClick: () => setEditingIndex(editingIndex === index ? null : index)
                  }
                ),
                /* @__PURE__ */ jsx43(Box27, { sx: { flex: 1 } }),
                /* @__PURE__ */ jsx43(
                  IconButton6,
                  {
                    size: "small",
                    onClick: () => handleMoveUp(index),
                    disabled: index === 0,
                    children: /* @__PURE__ */ jsx43(ArrowUpIcon, { fontSize: "small" })
                  }
                ),
                /* @__PURE__ */ jsx43(
                  IconButton6,
                  {
                    size: "small",
                    onClick: () => handleMoveDown(index),
                    disabled: index === chain.length - 1,
                    children: /* @__PURE__ */ jsx43(ArrowDownIcon, { fontSize: "small" })
                  }
                ),
                /* @__PURE__ */ jsx43(
                  IconButton6,
                  {
                    size: "small",
                    onClick: () => handleRemove(index),
                    color: "error",
                    children: /* @__PURE__ */ jsx43(DeleteIcon4, { fontSize: "small" })
                  }
                )
              ] }),
              editingIndex === index && renderConfigEditor(transform, index)
            ]
          },
          index
        )) })
      ] }),
      chain.length > 0 && /* @__PURE__ */ jsx43(Divider9, { sx: { my: 2 } }),
      /* @__PURE__ */ jsx43(Typography33, { variant: "subtitle2", gutterBottom: true, children: "Add Transform" }),
      /* @__PURE__ */ jsx43(Grid2, { container: true, spacing: 1, children: TRANSFORM_TYPES.map((transform) => /* @__PURE__ */ jsx43(Grid2, { size: 4, children: /* @__PURE__ */ jsxs38(
        Paper5,
        {
          variant: "outlined",
          onClick: () => handleAddTransform(transform.value),
          sx: {
            p: 1.5,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": { bgcolor: "primary.50", borderColor: "primary.main" }
          },
          children: [
            /* @__PURE__ */ jsx43(Typography33, { variant: "h6", sx: { fontSize: "1.2rem", mb: 0.5 }, children: transform.icon }),
            /* @__PURE__ */ jsx43(Typography33, { variant: "body2", fontWeight: 500, children: transform.label }),
            /* @__PURE__ */ jsx43(Typography33, { variant: "caption", color: "text.secondary", children: transform.description })
          ]
        }
      ) }, transform.value)) })
    ] }),
    /* @__PURE__ */ jsxs38(DialogActions3, { children: [
      /* @__PURE__ */ jsx43(Button9, { onClick: onClose, children: "Cancel" }),
      /* @__PURE__ */ jsx43(Button9, { onClick: handleSave, variant: "contained", color: "primary", children: "Save" })
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/components/FieldMappingEditor.tsx
import { jsx as jsx44, jsxs as jsxs39 } from "react/jsx-runtime";
function FieldMappingEditor({
  triggerName,
  triggerType,
  mappings,
  onChange,
  disabled = false
}) {
  const [transformModalOpen, setTransformModalOpen] = useState29(false);
  const [editingField, setEditingField] = useState29(null);
  const getFields = () => {
    if (triggerType === "autocomplete") {
      return AUTOCOMPLETE_MAPPING_FIELDS.map((f) => ({
        key: f.key,
        label: f.label,
        required: f.required
      }));
    }
    const schema2 = FETCH_MAPPING_SCHEMAS[triggerName] || [];
    return schema2.map((f) => ({ key: f.key, label: f.label }));
  };
  const fields = getFields();
  const getMapping = (fieldKey) => {
    if (triggerType === "autocomplete") {
      const autoMappings = mappings;
      return autoMappings[fieldKey];
    }
    const fetchMappings = mappings;
    return fetchMappings[fieldKey];
  };
  const handleSourceChange = useCallback26(
    (fieldKey, source) => {
      const currentMapping = getMapping(fieldKey) || { source: "" };
      const updatedMapping = { ...currentMapping, source };
      if (triggerType === "autocomplete") {
        const autoMappings = mappings;
        onChange({
          ...autoMappings,
          [fieldKey]: updatedMapping
        });
      } else {
        const fetchMappings = mappings;
        onChange({
          ...fetchMappings,
          [fieldKey]: updatedMapping
        });
      }
    },
    [mappings, triggerType, onChange]
  );
  const handleTransformsChange = useCallback26(
    (fieldKey, transforms) => {
      const currentMapping = getMapping(fieldKey) || { source: "" };
      const updatedMapping = {
        ...currentMapping,
        transforms: transforms.length > 0 ? transforms : void 0
      };
      if (triggerType === "autocomplete") {
        const autoMappings = mappings;
        onChange({
          ...autoMappings,
          [fieldKey]: updatedMapping
        });
      } else {
        const fetchMappings = mappings;
        onChange({
          ...fetchMappings,
          [fieldKey]: updatedMapping
        });
      }
      setTransformModalOpen(false);
    },
    [mappings, triggerType, onChange]
  );
  const openTransformModal = (fieldKey) => {
    setEditingField(fieldKey);
    setTransformModalOpen(true);
  };
  const getTransformLabel = (transform) => {
    switch (transform.type) {
      case "uppercase":
        return "UPPER";
      case "lowercase":
        return "lower";
      case "trim":
        return "trim";
      case "date_iso":
        return "ISO";
      case "regex":
        return "regex";
      case "template":
        return "template";
      case "split":
        return "split";
      case "concat":
        return "concat";
      case "default":
        return "default";
      case "date_format":
        return "date";
      default:
        return transform.type;
    }
  };
  if (fields.length === 0) {
    return /* @__PURE__ */ jsx44(Box28, { sx: { p: 2, bgcolor: "grey.50", borderRadius: 1 }, children: /* @__PURE__ */ jsx44(Typography34, { color: "text.secondary", variant: "body2", children: "No field mappings defined for this trigger type." }) });
  }
  return /* @__PURE__ */ jsxs39(Box28, { children: [
    /* @__PURE__ */ jsxs39(Table2, { size: "small", children: [
      /* @__PURE__ */ jsx44(TableHead2, { children: /* @__PURE__ */ jsxs39(TableRow2, { children: [
        /* @__PURE__ */ jsx44(TableCell2, { width: "25%", children: "Field" }),
        /* @__PURE__ */ jsx44(TableCell2, { width: "45%", children: "JSONPath Source" }),
        /* @__PURE__ */ jsx44(TableCell2, { width: "30%", children: "Transforms" })
      ] }) }),
      /* @__PURE__ */ jsx44(TableBody2, { children: fields.map((field) => {
        const mapping = getMapping(field.key);
        const transforms = mapping?.transforms || [];
        return /* @__PURE__ */ jsxs39(TableRow2, { children: [
          /* @__PURE__ */ jsx44(TableCell2, { children: /* @__PURE__ */ jsxs39(Typography34, { variant: "body2", children: [
            field.label,
            field.required && /* @__PURE__ */ jsx44(
              Typography34,
              {
                component: "span",
                color: "error",
                sx: { ml: 0.5 },
                children: "*"
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx44(TableCell2, { children: /* @__PURE__ */ jsx44(
            TextField28,
            {
              value: mapping?.source || "",
              onChange: (e) => handleSourceChange(field.key, e.target.value),
              size: "small",
              fullWidth: true,
              placeholder: "$.fieldName",
              disabled,
              inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } }
            }
          ) }),
          /* @__PURE__ */ jsx44(TableCell2, { children: /* @__PURE__ */ jsxs39(Box28, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }, children: [
            transforms.map((transform, idx) => /* @__PURE__ */ jsx44(
              Chip8,
              {
                label: getTransformLabel(transform),
                size: "small",
                color: "primary",
                variant: "outlined",
                onClick: () => !disabled && openTransformModal(field.key),
                sx: { cursor: disabled ? "default" : "pointer" }
              },
              idx
            )),
            /* @__PURE__ */ jsx44(
              Button10,
              {
                size: "small",
                startIcon: /* @__PURE__ */ jsx44(AddIcon4, {}),
                onClick: () => openTransformModal(field.key),
                disabled,
                sx: { minWidth: "auto", px: 1 },
                children: transforms.length === 0 ? "Transform" : ""
              }
            )
          ] }) })
        ] }, field.key);
      }) })
    ] }),
    editingField && /* @__PURE__ */ jsx44(
      TransformChainModal,
      {
        open: transformModalOpen,
        onClose: () => setTransformModalOpen(false),
        fieldKey: editingField,
        transforms: getMapping(editingField)?.transforms || [],
        onSave: (transforms) => handleTransformsChange(editingField, transforms)
      }
    )
  ] });
}

// src/definitions/connector-triggers.renderer/components/EnrichmentsSection.tsx
import { useCallback as useCallback30, useState as useState32 } from "react";
import {
  Box as Box32,
  Typography as Typography38,
  Button as Button13,
  Badge,
  Dialog as Dialog4,
  DialogTitle as DialogTitle4,
  DialogContent as DialogContent4,
  DialogContentText,
  DialogActions as DialogActions4
} from "@mui/material";
import { Add as AddIcon7 } from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/EnrichmentCard.tsx
import { useCallback as useCallback29 } from "react";
import {
  Box as Box31,
  Typography as Typography37,
  Paper as Paper7,
  TextField as TextField31,
  FormControl as FormControl2,
  InputLabel as InputLabel2,
  Select as Select3,
  MenuItem as MenuItem14,
  IconButton as IconButton9,
  Chip as Chip10,
  Tooltip as Tooltip7,
  Collapse as Collapse3
} from "@mui/material";
import {
  Delete as DeleteIcon7,
  ArrowUpward as ArrowUpIcon2,
  ArrowDownward as ArrowDownIcon2,
  Help as HelpIcon2
} from "@mui/icons-material";

// src/definitions/connector-triggers.renderer/components/EnrichmentMappingEditor.tsx
import { useState as useState30, useCallback as useCallback27, useMemo as useMemo3 } from "react";
import {
  Box as Box29,
  Table as Table3,
  TableBody as TableBody3,
  TableCell as TableCell3,
  TableHead as TableHead3,
  TableRow as TableRow3,
  TextField as TextField29,
  Button as Button11,
  Chip as Chip9,
  Typography as Typography35,
  IconButton as IconButton7,
  Menu,
  MenuItem as MenuItem13,
  ListItemText as ListItemText3
} from "@mui/material";
import { Add as AddIcon5, Delete as DeleteIcon5 } from "@mui/icons-material";
import { jsx as jsx45, jsxs as jsxs40 } from "react/jsx-runtime";
function EnrichmentMappingEditor({
  mappings,
  onChange,
  disabled = false
}) {
  const [transformModalOpen, setTransformModalOpen] = useState30(false);
  const [editingField, setEditingField] = useState30(null);
  const [anchorEl, setAnchorEl] = useState30(null);
  const menuOpen = Boolean(anchorEl);
  const configuredFieldKeys = Object.keys(mappings);
  const availableFields = useMemo3(() => {
    return ENRICHMENT_MAPPING_FIELDS.filter(
      (field) => !configuredFieldKeys.includes(field.key)
    );
  }, [configuredFieldKeys]);
  const getFieldLabel = (key) => {
    const field = ENRICHMENT_MAPPING_FIELDS.find((f) => f.key === key);
    return field?.label || key;
  };
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleAddField = useCallback27(
    (fieldKey) => {
      handleMenuClose();
      onChange({
        ...mappings,
        [fieldKey]: { source: "" }
      });
    },
    [mappings, onChange]
  );
  const handleRemoveField = useCallback27(
    (fieldKey) => {
      const newMappings = { ...mappings };
      delete newMappings[fieldKey];
      onChange(newMappings);
    },
    [mappings, onChange]
  );
  const handleSourceChange = useCallback27(
    (fieldKey, source) => {
      const currentMapping = mappings[fieldKey] || { source: "" };
      onChange({
        ...mappings,
        [fieldKey]: { ...currentMapping, source }
      });
    },
    [mappings, onChange]
  );
  const handleTransformsChange = useCallback27(
    (fieldKey, transforms) => {
      const currentMapping = mappings[fieldKey] || { source: "" };
      onChange({
        ...mappings,
        [fieldKey]: {
          ...currentMapping,
          transforms: transforms.length > 0 ? transforms : void 0
        }
      });
      setTransformModalOpen(false);
    },
    [mappings, onChange]
  );
  const openTransformModal = (fieldKey) => {
    setEditingField(fieldKey);
    setTransformModalOpen(true);
  };
  const getTransformLabel = (transform) => {
    switch (transform.type) {
      case "uppercase":
        return "UPPER";
      case "lowercase":
        return "lower";
      case "trim":
        return "trim";
      case "date_iso":
        return "ISO";
      case "regex":
        return "regex";
      case "template":
        return "template";
      case "split":
        return "split";
      case "concat":
        return "concat";
      case "default":
        return "default";
      case "date_format":
        return "date";
    }
  };
  if (configuredFieldKeys.length === 0) {
    return /* @__PURE__ */ jsxs40(Box29, { children: [
      /* @__PURE__ */ jsx45(
        Box29,
        {
          sx: {
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            textAlign: "center",
            mb: 1
          },
          children: /* @__PURE__ */ jsx45(Typography35, { color: "text.secondary", variant: "body2", children: "No field mappings configured. Add fields to map from the enrichment response." })
        }
      ),
      /* @__PURE__ */ jsx45(
        Button11,
        {
          size: "small",
          startIcon: /* @__PURE__ */ jsx45(AddIcon5, {}),
          onClick: handleMenuOpen,
          disabled: disabled || availableFields.length === 0,
          children: "Add Field"
        }
      ),
      /* @__PURE__ */ jsx45(Menu, { anchorEl, open: menuOpen, onClose: handleMenuClose, children: availableFields.map((field) => /* @__PURE__ */ jsx45(MenuItem13, { onClick: () => handleAddField(field.key), children: /* @__PURE__ */ jsx45(ListItemText3, { primary: field.label }) }, field.key)) })
    ] });
  }
  return /* @__PURE__ */ jsxs40(Box29, { children: [
    /* @__PURE__ */ jsxs40(Table3, { size: "small", children: [
      /* @__PURE__ */ jsx45(TableHead3, { children: /* @__PURE__ */ jsxs40(TableRow3, { children: [
        /* @__PURE__ */ jsx45(TableCell3, { width: "25%", children: "Field" }),
        /* @__PURE__ */ jsx45(TableCell3, { width: "40%", children: "JSONPath Source" }),
        /* @__PURE__ */ jsx45(TableCell3, { width: "25%", children: "Transforms" }),
        /* @__PURE__ */ jsx45(TableCell3, { width: "10%", align: "right" })
      ] }) }),
      /* @__PURE__ */ jsx45(TableBody3, { children: configuredFieldKeys.map((fieldKey) => {
        const mapping = mappings[fieldKey];
        const transforms = mapping?.transforms || [];
        return /* @__PURE__ */ jsxs40(TableRow3, { children: [
          /* @__PURE__ */ jsx45(TableCell3, { children: /* @__PURE__ */ jsx45(Typography35, { variant: "body2", children: getFieldLabel(fieldKey) }) }),
          /* @__PURE__ */ jsx45(TableCell3, { children: /* @__PURE__ */ jsx45(
            TextField29,
            {
              value: mapping?.source || "",
              onChange: (e) => handleSourceChange(fieldKey, e.target.value),
              size: "small",
              fullWidth: true,
              placeholder: "$.fieldName",
              disabled,
              inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } }
            }
          ) }),
          /* @__PURE__ */ jsx45(TableCell3, { children: /* @__PURE__ */ jsxs40(Box29, { sx: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }, children: [
            transforms.map((transform, idx) => /* @__PURE__ */ jsx45(
              Chip9,
              {
                label: getTransformLabel(transform),
                size: "small",
                color: "primary",
                variant: "outlined",
                onClick: () => !disabled && openTransformModal(fieldKey),
                sx: { cursor: disabled ? "default" : "pointer" }
              },
              idx
            )),
            /* @__PURE__ */ jsx45(
              Button11,
              {
                size: "small",
                startIcon: /* @__PURE__ */ jsx45(AddIcon5, {}),
                onClick: () => openTransformModal(fieldKey),
                disabled,
                sx: { minWidth: "auto", px: 1 },
                children: transforms.length === 0 ? "Transform" : ""
              }
            )
          ] }) }),
          /* @__PURE__ */ jsx45(TableCell3, { align: "right", children: /* @__PURE__ */ jsx45(
            IconButton7,
            {
              size: "small",
              onClick: () => handleRemoveField(fieldKey),
              disabled,
              color: "error",
              title: "Remove field",
              children: /* @__PURE__ */ jsx45(DeleteIcon5, { fontSize: "small" })
            }
          ) })
        ] }, fieldKey);
      }) })
    ] }),
    availableFields.length > 0 && /* @__PURE__ */ jsxs40(Box29, { sx: { mt: 1 }, children: [
      /* @__PURE__ */ jsx45(
        Button11,
        {
          size: "small",
          startIcon: /* @__PURE__ */ jsx45(AddIcon5, {}),
          onClick: handleMenuOpen,
          disabled,
          children: "Add Field"
        }
      ),
      /* @__PURE__ */ jsx45(Menu, { anchorEl, open: menuOpen, onClose: handleMenuClose, children: availableFields.map((field) => /* @__PURE__ */ jsx45(MenuItem13, { onClick: () => handleAddField(field.key), children: /* @__PURE__ */ jsx45(ListItemText3, { primary: field.label }) }, field.key)) })
    ] }),
    editingField && /* @__PURE__ */ jsx45(
      TransformChainModal,
      {
        open: transformModalOpen,
        onClose: () => setTransformModalOpen(false),
        fieldKey: editingField,
        transforms: mappings[editingField]?.transforms || [],
        onSave: (transforms) => handleTransformsChange(editingField, transforms)
      }
    )
  ] });
}

// src/definitions/connector-triggers.renderer/components/HeadersEditor.tsx
import { useCallback as useCallback28, useState as useState31 } from "react";
import {
  Box as Box30,
  Typography as Typography36,
  TextField as TextField30,
  IconButton as IconButton8,
  Button as Button12,
  Paper as Paper6,
  Tooltip as Tooltip6,
  Collapse as Collapse2
} from "@mui/material";
import {
  Delete as DeleteIcon6,
  Add as AddIcon6,
  ExpandMore as ExpandMoreIcon5,
  ExpandLess as ExpandLessIcon2,
  Help as HelpIcon
} from "@mui/icons-material";
import { jsx as jsx46, jsxs as jsxs41 } from "react/jsx-runtime";
function HeadersEditor({
  headers = {},
  onChange,
  disabled = false,
  variableHelpText = "Use {cdi.variable_name} for custom data input variables"
}) {
  const [expanded, setExpanded] = useState31(Object.keys(headers).length > 0);
  const headerEntries = Object.entries(headers);
  const handleNameChange = useCallback28(
    (oldName, newName) => {
      const newHeaders = { ...headers };
      const value = newHeaders[oldName];
      delete newHeaders[oldName];
      if (newName) {
        newHeaders[newName] = value;
      }
      onChange(newHeaders);
    },
    [headers, onChange]
  );
  const handleValueChange = useCallback28(
    (name, value) => {
      onChange({ ...headers, [name]: value });
    },
    [headers, onChange]
  );
  const handleAddHeader = useCallback28(() => {
    let index = 1;
    let newName = "X-Custom-Header";
    while (headers[newName]) {
      newName = `X-Custom-Header-${index}`;
      index++;
    }
    onChange({ ...headers, [newName]: "" });
    setExpanded(true);
  }, [headers, onChange]);
  const handleDeleteHeader = useCallback28(
    (name) => {
      const newHeaders = { ...headers };
      delete newHeaders[name];
      onChange(newHeaders);
    },
    [headers, onChange]
  );
  const helpTooltip = /* @__PURE__ */ jsxs41(Box30, { sx: { p: 1, maxWidth: 300 }, children: [
    /* @__PURE__ */ jsx46(Typography36, { variant: "subtitle2", gutterBottom: true, children: "Custom Headers" }),
    /* @__PURE__ */ jsx46(Typography36, { variant: "caption", display: "block", sx: { mb: 1 }, children: "Add custom HTTP headers to include with API requests." }),
    /* @__PURE__ */ jsx46(Typography36, { variant: "caption", display: "block", color: "text.secondary", children: variableHelpText }),
    /* @__PURE__ */ jsx46(Box30, { sx: { mt: 1 }, children: /* @__PURE__ */ jsx46(Typography36, { variant: "caption", display: "block", sx: { fontFamily: "monospace" }, children: "Example: api-version: 4" }) })
  ] });
  return /* @__PURE__ */ jsxs41(Box30, { sx: { mb: 2 }, children: [
    /* @__PURE__ */ jsxs41(
      Box30,
      {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1
        },
        children: [
          /* @__PURE__ */ jsxs41(Box30, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [
            /* @__PURE__ */ jsx46(
              IconButton8,
              {
                size: "small",
                onClick: () => setExpanded(!expanded),
                disabled,
                children: expanded ? /* @__PURE__ */ jsx46(ExpandLessIcon2, { fontSize: "small" }) : /* @__PURE__ */ jsx46(ExpandMoreIcon5, { fontSize: "small" })
              }
            ),
            /* @__PURE__ */ jsxs41(Typography36, { variant: "subtitle2", color: "text.secondary", children: [
              "Custom Headers",
              headerEntries.length > 0 && /* @__PURE__ */ jsxs41(Typography36, { component: "span", variant: "caption", sx: { ml: 1 }, children: [
                "(",
                headerEntries.length,
                ")"
              ] })
            ] }),
            /* @__PURE__ */ jsx46(Tooltip6, { title: helpTooltip, arrow: true, placement: "right", children: /* @__PURE__ */ jsx46(HelpIcon, { fontSize: "small", color: "action", sx: { fontSize: 16 } }) })
          ] }),
          /* @__PURE__ */ jsx46(
            Button12,
            {
              size: "small",
              startIcon: /* @__PURE__ */ jsx46(AddIcon6, {}),
              onClick: handleAddHeader,
              disabled,
              children: "Add Header"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx46(Collapse2, { in: expanded, children: headerEntries.length === 0 ? /* @__PURE__ */ jsx46(Typography36, { variant: "caption", color: "text.secondary", sx: { pl: 4 }, children: "No custom headers configured" }) : /* @__PURE__ */ jsx46(Box30, { sx: { display: "flex", flexDirection: "column", gap: 1, pl: 4 }, children: headerEntries.map(([name, value], index) => /* @__PURE__ */ jsxs41(
      Paper6,
      {
        variant: "outlined",
        sx: { p: 1, display: "flex", alignItems: "center", gap: 1 },
        children: [
          /* @__PURE__ */ jsx46(
            TextField30,
            {
              label: "Header Name",
              value: name,
              onChange: (e) => handleNameChange(name, e.target.value),
              size: "small",
              disabled,
              sx: { flex: 1 },
              inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } },
              placeholder: "api-version"
            }
          ),
          /* @__PURE__ */ jsx46(Typography36, { color: "text.secondary", children: ":" }),
          /* @__PURE__ */ jsx46(
            TextField30,
            {
              label: "Header Value",
              value,
              onChange: (e) => handleValueChange(name, e.target.value),
              size: "small",
              disabled,
              sx: { flex: 1 },
              inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } },
              placeholder: "4 or {cdi.api_version}"
            }
          ),
          /* @__PURE__ */ jsx46(
            IconButton8,
            {
              size: "small",
              onClick: () => handleDeleteHeader(name),
              disabled,
              color: "error",
              title: "Delete header",
              children: /* @__PURE__ */ jsx46(DeleteIcon6, { fontSize: "small" })
            }
          )
        ]
      },
      index
    )) }) })
  ] });
}

// src/definitions/connector-triggers.renderer/components/EnrichmentCard.tsx
import { jsx as jsx47, jsxs as jsxs42 } from "react/jsx-runtime";
function EnrichmentCard({
  index,
  enrichment,
  authProviders,
  parentAuthProvider,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  disabled = false
}) {
  const showRequestBody = ["POST", "PUT", "PATCH"].includes(enrichment.httpMethod);
  const handleFieldChange = useCallback29(
    (field, value) => {
      onUpdate({ [field]: value });
    },
    [onUpdate]
  );
  const handleMappingsChange = useCallback29(
    (mappings) => {
      onUpdate({ mappings });
    },
    [onUpdate]
  );
  const handleHeadersChange = useCallback29(
    (headers) => {
      onUpdate({ headers });
    },
    [onUpdate]
  );
  const variableHelpContent = /* @__PURE__ */ jsxs42(Box31, { sx: { p: 1 }, children: [
    /* @__PURE__ */ jsx47(Typography37, { variant: "subtitle2", gutterBottom: true, children: "Variable Interpolation" }),
    Object.values(ENRICHMENT_VARIABLE_HELP).map((help, idx) => /* @__PURE__ */ jsxs42(Box31, { sx: { mb: 1 }, children: [
      /* @__PURE__ */ jsx47(
        Typography37,
        {
          variant: "body2",
          component: "code",
          sx: { fontFamily: "monospace", bgcolor: "grey.100", px: 0.5 },
          children: help.syntax
        }
      ),
      /* @__PURE__ */ jsx47(Typography37, { variant: "caption", display: "block", color: "text.secondary", children: help.description })
    ] }, idx))
  ] });
  return /* @__PURE__ */ jsxs42(Paper7, { variant: "outlined", sx: { overflow: "hidden" }, children: [
    /* @__PURE__ */ jsxs42(
      Box31,
      {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "divider"
        },
        children: [
          /* @__PURE__ */ jsxs42(Box31, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
            /* @__PURE__ */ jsx47(
              Chip10,
              {
                label: `Enrichment ${index + 1}`,
                size: "small",
                color: "secondary",
                variant: "outlined"
              }
            ),
            enrichment.condition && /* @__PURE__ */ jsx47(
              Chip10,
              {
                label: "Conditional",
                size: "small",
                variant: "outlined",
                sx: { fontSize: "0.7rem" }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs42(Box31, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [
            /* @__PURE__ */ jsx47(
              IconButton9,
              {
                size: "small",
                onClick: onMoveUp,
                disabled: disabled || isFirst,
                title: "Move up",
                children: /* @__PURE__ */ jsx47(ArrowUpIcon2, { fontSize: "small" })
              }
            ),
            /* @__PURE__ */ jsx47(
              IconButton9,
              {
                size: "small",
                onClick: onMoveDown,
                disabled: disabled || isLast,
                title: "Move down",
                children: /* @__PURE__ */ jsx47(ArrowDownIcon2, { fontSize: "small" })
              }
            ),
            /* @__PURE__ */ jsx47(
              IconButton9,
              {
                size: "small",
                onClick: onDelete,
                disabled,
                color: "error",
                title: "Delete enrichment",
                children: /* @__PURE__ */ jsx47(DeleteIcon7, { fontSize: "small" })
              }
            )
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs42(Box31, { sx: { p: 2 }, children: [
      /* @__PURE__ */ jsx47(
        TextField31,
        {
          label: "Condition (optional)",
          value: enrichment.condition || "",
          onChange: (e) => handleFieldChange("condition", e.target.value),
          fullWidth: true,
          size: "small",
          placeholder: "$.hasVehicleInfo",
          helperText: "JSONPath condition - enrichment runs only if condition is truthy",
          disabled,
          sx: { mb: 2 },
          inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } }
        }
      ),
      /* @__PURE__ */ jsxs42(Box31, { sx: { display: "flex", gap: 2, mb: 2 }, children: [
        /* @__PURE__ */ jsxs42(FormControl2, { size: "small", sx: { minWidth: 200 }, children: [
          /* @__PURE__ */ jsx47(InputLabel2, { children: "Auth Provider" }),
          /* @__PURE__ */ jsxs42(
            Select3,
            {
              value: enrichment.authProvider || "",
              label: "Auth Provider",
              onChange: (e) => handleFieldChange("authProvider", e.target.value),
              disabled,
              displayEmpty: true,
              children: [
                /* @__PURE__ */ jsx47(MenuItem14, { value: "", children: /* @__PURE__ */ jsxs42("em", { children: [
                  "(Use trigger default: ",
                  parentAuthProvider || "none",
                  ")"
                ] }) }),
                authProviders.map((provider) => /* @__PURE__ */ jsx47(MenuItem14, { value: provider, children: provider }, provider))
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs42(FormControl2, { size: "small", sx: { minWidth: 120 }, children: [
          /* @__PURE__ */ jsx47(InputLabel2, { children: "Method" }),
          /* @__PURE__ */ jsx47(
            Select3,
            {
              value: enrichment.httpMethod || "GET",
              label: "Method",
              onChange: (e) => handleFieldChange("httpMethod", e.target.value),
              disabled,
              children: ENRICHMENT_HTTP_METHODS.map((method) => /* @__PURE__ */ jsx47(MenuItem14, { value: method, children: method }, method))
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsxs42(Box31, { sx: { display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }, children: [
        /* @__PURE__ */ jsx47(
          TextField31,
          {
            label: "Endpoint URL",
            value: enrichment.endpoint || "",
            onChange: (e) => handleFieldChange("endpoint", e.target.value),
            fullWidth: true,
            size: "small",
            placeholder: "https://api.example.com/vehicle/{primary.vin}",
            helperText: "Use variable interpolation to include values from primary response",
            disabled,
            inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } }
          }
        ),
        /* @__PURE__ */ jsx47(Tooltip7, { title: variableHelpContent, arrow: true, placement: "left", children: /* @__PURE__ */ jsx47(IconButton9, { size: "small", sx: { mt: 0.5 }, children: /* @__PURE__ */ jsx47(HelpIcon2, { fontSize: "small", color: "action" }) }) })
      ] }),
      /* @__PURE__ */ jsx47(
        HeadersEditor,
        {
          headers: enrichment.headers,
          onChange: handleHeadersChange,
          disabled,
          variableHelpText: "Use {primary.field}, {primary_raw.$.path}, or {lookup_value} for variable interpolation"
        }
      ),
      /* @__PURE__ */ jsx47(Collapse3, { in: showRequestBody, children: /* @__PURE__ */ jsxs42(Box31, { sx: { display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }, children: [
        /* @__PURE__ */ jsx47(
          TextField31,
          {
            label: "Request Body",
            value: enrichment.requestBody || "",
            onChange: (e) => handleFieldChange("requestBody", e.target.value),
            fullWidth: true,
            size: "small",
            multiline: true,
            rows: 3,
            placeholder: '{\n  "id": "{primary.customerId}"\n}',
            helperText: "JSON body template with variable interpolation",
            disabled,
            inputProps: { style: { fontFamily: "monospace", fontSize: "0.85rem" } }
          }
        ),
        /* @__PURE__ */ jsx47(Tooltip7, { title: variableHelpContent, arrow: true, placement: "left", children: /* @__PURE__ */ jsx47(IconButton9, { size: "small", sx: { mt: 0.5 }, children: /* @__PURE__ */ jsx47(HelpIcon2, { fontSize: "small", color: "action" }) }) })
      ] }) }),
      /* @__PURE__ */ jsx47(Typography37, { variant: "subtitle2", gutterBottom: true, sx: { mt: 1 }, children: "Response Field Mappings" }),
      /* @__PURE__ */ jsx47(Typography37, { variant: "caption", color: "text.secondary", display: "block", sx: { mb: 1 }, children: "Map fields from the enrichment response to populate or override result fields" }),
      /* @__PURE__ */ jsx47(
        EnrichmentMappingEditor,
        {
          mappings: enrichment.mappings,
          onChange: handleMappingsChange,
          disabled
        }
      )
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/components/EnrichmentsSection.tsx
import { jsx as jsx48, jsxs as jsxs43 } from "react/jsx-runtime";
function EnrichmentsSection({
  triggerName,
  enrichments,
  authProviders,
  parentAuthProvider,
  onChange,
  disabled = false
}) {
  const [deleteIndex, setDeleteIndex] = useState32(null);
  const handleAddEnrichment = useCallback30(() => {
    const newEnrichment = {
      ...DEFAULT_ENRICHMENT,
      httpMethod: "GET",
      endpoint: "",
      mappings: {}
    };
    onChange([...enrichments, newEnrichment]);
  }, [enrichments, onChange]);
  const handleUpdateEnrichment = useCallback30(
    (index, updates) => {
      const updated = enrichments.map(
        (e, i) => i === index ? { ...e, ...updates } : e
      );
      onChange(updated);
    },
    [enrichments, onChange]
  );
  const handleDeleteClick = useCallback30((index) => {
    setDeleteIndex(index);
  }, []);
  const handleDeleteConfirm = useCallback30(() => {
    if (deleteIndex !== null) {
      const updated = enrichments.filter((_, i) => i !== deleteIndex);
      onChange(updated);
      setDeleteIndex(null);
    }
  }, [deleteIndex, enrichments, onChange]);
  const handleDeleteCancel = useCallback30(() => {
    setDeleteIndex(null);
  }, []);
  const handleMoveUp = useCallback30(
    (index) => {
      if (index === 0) return;
      const updated = [...enrichments];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      onChange(updated);
    },
    [enrichments, onChange]
  );
  const handleMoveDown = useCallback30(
    (index) => {
      if (index === enrichments.length - 1) return;
      const updated = [...enrichments];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      onChange(updated);
    },
    [enrichments, onChange]
  );
  return /* @__PURE__ */ jsxs43(Box32, { children: [
    /* @__PURE__ */ jsxs43(Box32, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 2 }, children: [
      /* @__PURE__ */ jsx48(Typography38, { variant: "subtitle2", children: "Enrichments" }),
      enrichments.length > 0 && /* @__PURE__ */ jsx48(
        Badge,
        {
          badgeContent: enrichments.length,
          color: "secondary",
          sx: { ml: 1 }
        }
      )
    ] }),
    enrichments.length === 0 && /* @__PURE__ */ jsx48(
      Box32,
      {
        sx: {
          p: 2,
          textAlign: "center",
          bgcolor: "grey.50",
          borderRadius: 1,
          mb: 2
        },
        children: /* @__PURE__ */ jsx48(Typography38, { color: "text.secondary", variant: "body2", children: "No enrichments configured. Enrichments allow chaining secondary API calls using data from the primary fetch response." })
      }
    ),
    enrichments.length > 0 && /* @__PURE__ */ jsx48(Box32, { sx: { display: "flex", flexDirection: "column", gap: 2, mb: 2 }, children: enrichments.map((enrichment, index) => /* @__PURE__ */ jsx48(
      EnrichmentCard,
      {
        index,
        enrichment,
        authProviders,
        parentAuthProvider,
        onUpdate: (updates) => handleUpdateEnrichment(index, updates),
        onDelete: () => handleDeleteClick(index),
        onMoveUp: () => handleMoveUp(index),
        onMoveDown: () => handleMoveDown(index),
        isFirst: index === 0,
        isLast: index === enrichments.length - 1,
        disabled
      },
      index
    )) }),
    /* @__PURE__ */ jsx48(
      Button13,
      {
        variant: "outlined",
        size: "small",
        startIcon: /* @__PURE__ */ jsx48(AddIcon7, {}),
        onClick: handleAddEnrichment,
        disabled,
        children: "Add Enrichment"
      }
    ),
    /* @__PURE__ */ jsxs43(Dialog4, { open: deleteIndex !== null, onClose: handleDeleteCancel, children: [
      /* @__PURE__ */ jsx48(DialogTitle4, { children: "Delete Enrichment" }),
      /* @__PURE__ */ jsx48(DialogContent4, { children: /* @__PURE__ */ jsxs43(DialogContentText, { children: [
        "Are you sure you want to delete Enrichment ",
        deleteIndex !== null ? deleteIndex + 1 : "",
        "? This action cannot be undone."
      ] }) }),
      /* @__PURE__ */ jsxs43(DialogActions4, { children: [
        /* @__PURE__ */ jsx48(Button13, { onClick: handleDeleteCancel, children: "Cancel" }),
        /* @__PURE__ */ jsx48(Button13, { onClick: handleDeleteConfirm, color: "error", variant: "contained", children: "Delete" })
      ] })
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/components/TriggerCard.tsx
import { Fragment as Fragment7, jsx as jsx49, jsxs as jsxs44 } from "react/jsx-runtime";
function TriggerCard({
  name,
  label,
  description,
  config,
  triggerType,
  authProviders,
  onUpdate,
  onDelete,
  disabled = false
}) {
  const isAutocomplete = triggerType === "autocomplete";
  const httpMethods = isAutocomplete ? AUTOCOMPLETE_HTTP_METHODS : HTTP_METHODS;
  const getEndpointPlaceholder = () => {
    if (isAutocomplete) {
      return "https://api.example.com/search?q={search_query}";
    }
    return "https://api.example.com/details/{lookup_value}";
  };
  const handleEnableChange = useCallback31(
    (event) => {
      onUpdate({ enabled: event.target.checked });
    },
    [onUpdate]
  );
  const handleFieldChange = useCallback31(
    (field, value) => {
      onUpdate({ [field]: value });
    },
    [onUpdate]
  );
  const handleMappingsChange = useCallback31(
    (mappings) => {
      onUpdate({ mappings });
    },
    [onUpdate]
  );
  const handleEnrichmentsChange = useCallback31(
    (enrichments) => {
      onUpdate({ enrichments });
    },
    [onUpdate]
  );
  const handleHeadersChange = useCallback31(
    (headers) => {
      onUpdate({ headers });
    },
    [onUpdate]
  );
  const hasValidAuthProvider = authProviders.length > 0 && config.authProvider;
  return /* @__PURE__ */ jsxs44(Paper8, { variant: "outlined", sx: { overflow: "hidden" }, children: [
    /* @__PURE__ */ jsxs44(
      Box33,
      {
        sx: {
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          p: 2,
          bgcolor: config.enabled ? "primary.50" : "grey.50"
        },
        children: [
          /* @__PURE__ */ jsxs44(Box33, { sx: { flex: 1 }, children: [
            /* @__PURE__ */ jsx49(Typography39, { variant: "body1", fontWeight: 600, children: label }),
            /* @__PURE__ */ jsx49(Typography39, { variant: "body2", color: "text.secondary", children: description })
          ] }),
          /* @__PURE__ */ jsx49(
            StyledSwitch15,
            {
              checked: config.enabled,
              onChange: handleEnableChange,
              disabled
            }
          ),
          /* @__PURE__ */ jsx49(
            IconButton10,
            {
              size: "small",
              onClick: onDelete,
              disabled,
              color: "error",
              title: "Delete trigger",
              sx: { ml: 1 },
              children: /* @__PURE__ */ jsx49(DeleteIcon8, { fontSize: "small" })
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs44(Collapse4, { in: config.enabled, children: [
      /* @__PURE__ */ jsx49(Divider10, {}),
      /* @__PURE__ */ jsxs44(Box33, { sx: { p: 2 }, children: [
        authProviders.length === 0 && /* @__PURE__ */ jsx49(Alert15, { severity: "warning", sx: { mb: 2 }, children: "No auth providers configured. Add an auth provider in the Auth Providers section first." }),
        /* @__PURE__ */ jsxs44(Box33, { sx: { display: "flex", gap: 2, mb: 2 }, children: [
          /* @__PURE__ */ jsxs44(FormControl3, { size: "small", sx: { minWidth: 200 }, children: [
            /* @__PURE__ */ jsx49(InputLabel3, { children: "Auth Provider" }),
            /* @__PURE__ */ jsx49(
              Select4,
              {
                value: config.authProvider || "",
                label: "Auth Provider",
                onChange: (e) => handleFieldChange("authProvider", e.target.value),
                disabled: disabled || authProviders.length === 0,
                children: authProviders.map((provider) => /* @__PURE__ */ jsx49(MenuItem15, { value: provider, children: provider }, provider))
              }
            )
          ] }),
          /* @__PURE__ */ jsxs44(FormControl3, { size: "small", sx: { minWidth: 120 }, children: [
            /* @__PURE__ */ jsx49(InputLabel3, { children: "Method" }),
            /* @__PURE__ */ jsx49(
              Select4,
              {
                value: config.httpMethod || "GET",
                label: "Method",
                onChange: (e) => handleFieldChange("httpMethod", e.target.value),
                disabled,
                children: httpMethods.map((method) => /* @__PURE__ */ jsx49(MenuItem15, { value: method, children: method }, method))
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx49(
          TextField32,
          {
            label: "Endpoint URL",
            value: config.endpoint || "",
            onChange: (e) => handleFieldChange("endpoint", e.target.value),
            fullWidth: true,
            size: "small",
            placeholder: getEndpointPlaceholder(),
            helperText: isAutocomplete ? "Use {search_query} as placeholder for the search term" : "Use {lookup_value} as placeholder for the selected value",
            disabled,
            sx: { mb: 2 }
          }
        ),
        /* @__PURE__ */ jsx49(
          HeadersEditor,
          {
            headers: config.headers,
            onChange: handleHeadersChange,
            disabled,
            variableHelpText: isAutocomplete ? "Use {search_query} or {cdi.variable_name} for variable interpolation" : "Use {lookup_value} or {cdi.variable_name} for variable interpolation"
          }
        ),
        !isAutocomplete && /* @__PURE__ */ jsxs44(Box33, { sx: { display: "flex", alignItems: "center", mb: 2 }, children: [
          /* @__PURE__ */ jsx49(
            FormControlLabel6,
            {
              control: /* @__PURE__ */ jsx49(
                Checkbox4,
                {
                  checked: config.overwriteExisting || false,
                  onChange: (e) => handleFieldChange("overwriteExisting", e.target.checked),
                  disabled,
                  size: "small"
                }
              ),
              label: "Overwrite existing values"
            }
          ),
          /* @__PURE__ */ jsx49(
            Tooltip8,
            {
              title: "When enabled, fetched data will replace any existing values in the form fields. When disabled, only empty fields will be populated.",
              arrow: true,
              children: /* @__PURE__ */ jsx49(InfoIcon, { fontSize: "small", color: "action", sx: { ml: -0.5 } })
            }
          )
        ] }),
        isAutocomplete && /* @__PURE__ */ jsxs44(Box33, { sx: { display: "flex", gap: 2, mb: 2 }, children: [
          /* @__PURE__ */ jsx49(
            TextField32,
            {
              label: "Min Characters",
              type: "number",
              value: config.minChars || 3,
              onChange: (e) => handleFieldChange("minChars", parseInt(e.target.value, 10)),
              size: "small",
              inputProps: { min: 1, max: 10 },
              disabled,
              sx: { width: 130 }
            }
          ),
          /* @__PURE__ */ jsx49(
            TextField32,
            {
              label: "Debounce (ms)",
              type: "number",
              value: config.debounceMs || 300,
              onChange: (e) => handleFieldChange("debounceMs", parseInt(e.target.value, 10)),
              size: "small",
              inputProps: { min: 0, max: 2e3 },
              disabled,
              sx: { width: 130 }
            }
          ),
          /* @__PURE__ */ jsx49(
            TextField32,
            {
              label: "Max Results",
              type: "number",
              value: config.maxResults || 10,
              onChange: (e) => handleFieldChange("maxResults", parseInt(e.target.value, 10)),
              size: "small",
              inputProps: { min: 1, max: 50 },
              disabled,
              sx: { width: 130 }
            }
          )
        ] }),
        isAutocomplete && /* @__PURE__ */ jsx49(
          TextField32,
          {
            label: "Response Array Path (optional)",
            value: config.responseArrayPath || "",
            onChange: (e) => handleFieldChange("responseArrayPath", e.target.value),
            fullWidth: true,
            size: "small",
            placeholder: "$.results",
            helperText: "JSONPath to the array in the response (e.g., $.data.items)",
            disabled,
            sx: { mb: 2 }
          }
        ),
        /* @__PURE__ */ jsx49(Typography39, { variant: "subtitle2", gutterBottom: true, sx: { mt: 2 }, children: "Response Field Mappings" }),
        /* @__PURE__ */ jsx49(
          FieldMappingEditor,
          {
            triggerName: name,
            triggerType,
            mappings: config.mappings,
            onChange: handleMappingsChange,
            disabled
          }
        ),
        !isAutocomplete && /* @__PURE__ */ jsxs44(Fragment7, { children: [
          /* @__PURE__ */ jsx49(Divider10, { sx: { my: 2 } }),
          /* @__PURE__ */ jsx49(
            EnrichmentsSection,
            {
              triggerName: name,
              enrichments: config.enrichments || [],
              authProviders,
              parentAuthProvider: config.authProvider,
              onChange: handleEnrichmentsChange,
              disabled
            }
          )
        ] })
      ] })
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/components/TriggersSection.tsx
import { Fragment as Fragment8, jsx as jsx50, jsxs as jsxs45 } from "react/jsx-runtime";
function TriggersSection({
  config,
  onChange,
  disabled = false,
  triggerType
}) {
  const isAutocomplete = triggerType === "autocomplete";
  const triggers = isAutocomplete ? config.autocompleteTriggers : config.fetchTriggers;
  const triggerEntries = Object.entries(triggers || {});
  const triggerTypeOptions = isAutocomplete ? AUTOCOMPLETE_TRIGGER_TYPES : FETCH_TRIGGER_TYPES;
  const configuredTriggerNames = Object.keys(triggers || {});
  const availableTriggers = triggerTypeOptions.filter(
    (t) => !configuredTriggerNames.includes(t.value)
  );
  const [anchorEl, setAnchorEl] = useState33(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleAddTrigger = useCallback32(
    (triggerName) => {
      handleMenuClose();
      if (isAutocomplete) {
        const newTrigger = {
          ...DEFAULT_AUTOCOMPLETE_TRIGGER,
          enabled: false,
          authProvider: "",
          httpMethod: "GET",
          endpoint: "",
          mappings: {
            id: { source: "" },
            displayValue: { source: "" }
          }
        };
        onChange({
          autocompleteTriggers: {
            ...config.autocompleteTriggers,
            [triggerName]: newTrigger
          }
        });
      } else {
        const schema2 = FETCH_MAPPING_SCHEMAS[triggerName] || [];
        const mappings = {};
        schema2.forEach((field) => {
          mappings[field.key] = { source: "" };
        });
        const newTrigger = {
          ...DEFAULT_FETCH_TRIGGER,
          enabled: false,
          authProvider: "",
          httpMethod: "GET",
          endpoint: "",
          overwriteExisting: false,
          mappings,
          enrichments: []
        };
        onChange({
          fetchTriggers: {
            ...config.fetchTriggers,
            [triggerName]: newTrigger
          }
        });
      }
    },
    [isAutocomplete, config, onChange]
  );
  const handleUpdateTrigger = useCallback32(
    (triggerName, updates) => {
      if (isAutocomplete) {
        const currentTrigger = config.autocompleteTriggers?.[triggerName];
        if (!currentTrigger) return;
        onChange({
          autocompleteTriggers: {
            ...config.autocompleteTriggers,
            [triggerName]: { ...currentTrigger, ...updates }
          }
        });
      } else {
        const currentTrigger = config.fetchTriggers?.[triggerName];
        if (!currentTrigger) return;
        onChange({
          fetchTriggers: {
            ...config.fetchTriggers,
            [triggerName]: { ...currentTrigger, ...updates }
          }
        });
      }
    },
    [isAutocomplete, config, onChange]
  );
  const handleDeleteTrigger = useCallback32(
    (triggerName) => {
      if (!window.confirm(`Delete trigger "${triggerName}"?`)) return;
      if (isAutocomplete) {
        const newTriggers = { ...config.autocompleteTriggers };
        delete newTriggers[triggerName];
        onChange({ autocompleteTriggers: newTriggers });
      } else {
        const newTriggers = { ...config.fetchTriggers };
        delete newTriggers[triggerName];
        onChange({ fetchTriggers: newTriggers });
      }
    },
    [isAutocomplete, config, onChange]
  );
  const getTriggerMeta = (name) => {
    const found = triggerTypeOptions.find((t) => t.value === name);
    return found || { value: name, label: name, description: "" };
  };
  if (triggerEntries.length === 0) {
    return /* @__PURE__ */ jsxs45(Box34, { children: [
      /* @__PURE__ */ jsx50(
        Box34,
        {
          sx: {
            p: 3,
            textAlign: "center",
            bgcolor: "grey.50",
            borderRadius: 1,
            mb: 2
          },
          children: /* @__PURE__ */ jsxs45(Typography40, { color: "text.secondary", children: [
            "No ",
            isAutocomplete ? "autocomplete" : "fetch",
            " triggers configured.",
            isAutocomplete ? " Add triggers to enable type-ahead suggestions in form fields." : " Add triggers to fetch data when a field value is entered or selected."
          ] })
        }
      ),
      /* @__PURE__ */ jsxs45(
        Button14,
        {
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx50(AddIcon8, {}),
          onClick: handleMenuOpen,
          disabled: disabled || availableTriggers.length === 0,
          children: [
            "Add ",
            isAutocomplete ? "Autocomplete" : "Fetch",
            " Trigger"
          ]
        }
      ),
      /* @__PURE__ */ jsx50(Menu2, { anchorEl, open: menuOpen, onClose: handleMenuClose, children: availableTriggers.map((trigger) => /* @__PURE__ */ jsx50(MenuItem16, { onClick: () => handleAddTrigger(trigger.value), children: /* @__PURE__ */ jsx50(
        ListItemText4,
        {
          primary: trigger.label,
          secondary: trigger.description,
          secondaryTypographyProps: { variant: "caption" }
        }
      ) }, trigger.value)) })
    ] });
  }
  return /* @__PURE__ */ jsxs45(Box34, { children: [
    /* @__PURE__ */ jsx50(Box34, { sx: { display: "flex", flexDirection: "column", gap: 2, mb: 2 }, children: triggerEntries.map(([name, triggerConfig]) => {
      const meta = getTriggerMeta(name);
      return /* @__PURE__ */ jsx50(
        TriggerCard,
        {
          name,
          label: meta.label,
          description: meta.description,
          config: triggerConfig,
          triggerType,
          authProviders: Object.keys(config.authProviders || {}),
          onUpdate: (updates) => handleUpdateTrigger(name, updates),
          onDelete: () => handleDeleteTrigger(name),
          disabled
        },
        name
      );
    }) }),
    availableTriggers.length > 0 && /* @__PURE__ */ jsxs45(Fragment8, { children: [
      /* @__PURE__ */ jsxs45(
        Button14,
        {
          variant: "outlined",
          startIcon: /* @__PURE__ */ jsx50(AddIcon8, {}),
          onClick: handleMenuOpen,
          disabled,
          children: [
            "Add ",
            isAutocomplete ? "Autocomplete" : "Fetch",
            " Trigger"
          ]
        }
      ),
      /* @__PURE__ */ jsx50(Menu2, { anchorEl, open: menuOpen, onClose: handleMenuClose, children: availableTriggers.map((trigger) => /* @__PURE__ */ jsx50(MenuItem16, { onClick: () => handleAddTrigger(trigger.value), children: /* @__PURE__ */ jsx50(
        ListItemText4,
        {
          primary: trigger.label,
          secondary: trigger.description,
          secondaryTypographyProps: { variant: "caption" }
        }
      ) }, trigger.value)) })
    ] })
  ] });
}

// src/definitions/connector-triggers.renderer/index.tsx
import { jsx as jsx51, jsxs as jsxs46 } from "react/jsx-runtime";
function ConnectorTriggersRenderer({
  value,
  onChange,
  disabled = false
}) {
  const config = useMemo4(
    () => mergeWithDefaults(value),
    [value]
  );
  const [expandedSections, setExpandedSections] = useState34({
    secrets: true,
    authProviders: true,
    autocompleteTriggers: false,
    fetchTriggers: false
  });
  const toggleSection = useCallback33((section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);
  const handleChange = useCallback33(
    (updates) => {
      onChange({
        ...config,
        ...updates
      });
    },
    [config, onChange]
  );
  const handleEnableChange = useCallback33(
    (event) => {
      handleChange({ enabled: event.target.checked });
    },
    [handleChange]
  );
  const secretsCount = countItems(config.secrets);
  const providersCount = countItems(config.authProviders);
  const autoTriggersCount = countItems(config.autocompleteTriggers);
  const fetchTriggersCount = countItems(config.fetchTriggers);
  return /* @__PURE__ */ jsxs46("div", { style: { width: "100%" }, children: [
    /* @__PURE__ */ jsx51(Paper9, { variant: "outlined", sx: { p: 2 }, children: /* @__PURE__ */ jsxs46(Box35, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [
      /* @__PURE__ */ jsxs46(Box35, { children: [
        /* @__PURE__ */ jsx51(Typography41, { variant: "body1", fontWeight: 600, children: "Enable Connector Triggers" }),
        /* @__PURE__ */ jsx51(Typography41, { variant: "body2", color: "text.secondary", children: "Enable external API connections for move form autocomplete and data fetching" })
      ] }),
      /* @__PURE__ */ jsx51(
        StyledSwitch16,
        {
          checked: config.enabled,
          onChange: handleEnableChange,
          disabled
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx51(Collapse5, { in: config.enabled, children: /* @__PURE__ */ jsxs46(Box35, { sx: { display: "flex", flexDirection: "column", gap: 2, mt: 2 }, children: [
      /* @__PURE__ */ jsx51(
        SectionWrapper,
        {
          title: "Secrets",
          tooltip: "Encrypted credentials for API authentication. Reference with {secret_name} syntax.",
          count: secretsCount,
          expanded: expandedSections.secrets,
          onToggle: () => toggleSection("secrets"),
          children: /* @__PURE__ */ jsx51(
            SecretsSection,
            {
              config,
              onChange: handleChange,
              disabled
            }
          )
        }
      ),
      /* @__PURE__ */ jsx51(
        SectionWrapper,
        {
          title: "Auth Providers",
          tooltip: "Configure authentication methods for external APIs",
          count: providersCount,
          expanded: expandedSections.authProviders,
          onToggle: () => toggleSection("authProviders"),
          children: /* @__PURE__ */ jsx51(
            AuthProvidersSection,
            {
              config,
              onChange: handleChange,
              disabled
            }
          )
        }
      ),
      /* @__PURE__ */ jsx51(
        SectionWrapper,
        {
          title: "Autocomplete Triggers",
          tooltip: "Configure type-ahead suggestions for form fields",
          count: autoTriggersCount,
          expanded: expandedSections.autocompleteTriggers,
          onToggle: () => toggleSection("autocompleteTriggers"),
          children: /* @__PURE__ */ jsx51(
            TriggersSection,
            {
              config,
              onChange: handleChange,
              disabled,
              triggerType: "autocomplete"
            }
          )
        }
      ),
      /* @__PURE__ */ jsx51(
        SectionWrapper,
        {
          title: "Fetch Triggers",
          tooltip: "Configure data fetching on field blur or selection",
          count: fetchTriggersCount,
          expanded: expandedSections.fetchTriggers,
          onToggle: () => toggleSection("fetchTriggers"),
          children: /* @__PURE__ */ jsx51(
            TriggersSection,
            {
              config,
              onChange: handleChange,
              disabled,
              triggerType: "fetch"
            }
          )
        }
      )
    ] }) })
  ] });
}
function SectionWrapper({
  title,
  tooltip,
  count,
  expanded,
  onToggle,
  children
}) {
  return /* @__PURE__ */ jsxs46(Paper9, { variant: "outlined", sx: { overflow: "hidden" }, children: [
    /* @__PURE__ */ jsxs46(
      Box35,
      {
        onClick: onToggle,
        sx: {
          display: "flex",
          alignItems: "center",
          p: 1.5,
          cursor: "pointer"
        },
        children: [
          /* @__PURE__ */ jsx51(IconButton11, { size: "small", sx: { mr: 1 }, children: expanded ? /* @__PURE__ */ jsx51(ExpandLessIcon3, {}) : /* @__PURE__ */ jsx51(ExpandMoreIcon6, {}) }),
          /* @__PURE__ */ jsx51(Typography41, { variant: "subtitle1", fontWeight: 600, sx: { flex: 1 }, children: title }),
          count > 0 && /* @__PURE__ */ jsx51(
            Badge2,
            {
              badgeContent: count,
              color: "primary",
              sx: { mr: 2 }
            }
          ),
          /* @__PURE__ */ jsx51(Tooltip9, { title: tooltip, children: /* @__PURE__ */ jsx51(HelpIcon3, { fontSize: "small", color: "action" }) })
        ]
      }
    ),
    /* @__PURE__ */ jsxs46(Collapse5, { in: expanded, children: [
      /* @__PURE__ */ jsx51(Divider11, {}),
      /* @__PURE__ */ jsx51(Box35, { sx: { p: 2 }, children })
    ] })
  ] });
}

// src/definitions/sixt-settings.renderer.tsx
import { useState as useState35, useEffect as useEffect26 } from "react";
import { FormControl as FormControl4, FormHelperText, InputLabel as InputLabel4, MenuItem as MenuItem17, Select as Select5, Stack as Stack5, TextField as TextField33 } from "@mui/material";
import { jsx as jsx52, jsxs as jsxs47 } from "react/jsx-runtime";
function effectiveMode(value) {
  if (value.moveCreationMode) return value.moveCreationMode;
  if (value.moveCreationEnabled === true) return "auto";
  if (value.moveCreationEnabled === false) return "off";
  return "approval";
}
var MODE_HELP = {
  approval: "A validated order posts an Approve / Reject card to the Slack channel; moves are created only when a dispatcher approves. The default.",
  auto: "A validated order creates its HopDrive moves immediately, one per vehicle, with no human step.",
  off: "Intake stops at a ready job and dispatch creates the moves by hand."
};
function clean(value) {
  const next = {};
  if (value.slackChannel) next.slackChannel = value.slackChannel;
  if (value.customerMatchMaxMiles != null) next.customerMatchMaxMiles = value.customerMatchMaxMiles;
  if (value.customerMatchTieMiles != null) next.customerMatchTieMiles = value.customerMatchTieMiles;
  if (value.locationMatchMaxMiles != null) next.locationMatchMaxMiles = value.locationMatchMaxMiles;
  if (value.moveCreationMode) next.moveCreationMode = value.moveCreationMode;
  else if (value.moveCreationEnabled) next.moveCreationEnabled = true;
  if (value.moveRetryMaxAttempts != null) next.moveRetryMaxAttempts = value.moveRetryMaxAttempts;
  if (value.moveRetryBaseSeconds != null) next.moveRetryBaseSeconds = value.moveRetryBaseSeconds;
  if (value.portalWritesEnabled === true) next.portalWritesEnabled = true;
  return next;
}
function SixtSettingsRenderer({ value, onChange, disabled }) {
  const current = value && typeof value === "object" ? value : {};
  const [channel, setChannel] = useState35(current.slackChannel ?? "");
  const [maxMiles, setMaxMiles] = useState35(current.customerMatchMaxMiles?.toString() ?? "");
  const [tieMiles, setTieMiles] = useState35(current.customerMatchTieMiles?.toString() ?? "");
  const [stationMiles, setStationMiles] = useState35(current.locationMatchMaxMiles?.toString() ?? "");
  const [maxAttempts, setMaxAttempts] = useState35(current.moveRetryMaxAttempts?.toString() ?? "");
  const [baseSeconds, setBaseSeconds] = useState35(current.moveRetryBaseSeconds?.toString() ?? "");
  useEffect26(() => setChannel(current.slackChannel ?? ""), [current.slackChannel]);
  useEffect26(() => setMaxMiles(current.customerMatchMaxMiles?.toString() ?? ""), [current.customerMatchMaxMiles]);
  useEffect26(() => setTieMiles(current.customerMatchTieMiles?.toString() ?? ""), [current.customerMatchTieMiles]);
  useEffect26(() => setStationMiles(current.locationMatchMaxMiles?.toString() ?? ""), [current.locationMatchMaxMiles]);
  useEffect26(() => setMaxAttempts(current.moveRetryMaxAttempts?.toString() ?? ""), [current.moveRetryMaxAttempts]);
  useEffect26(() => setBaseSeconds(current.moveRetryBaseSeconds?.toString() ?? ""), [current.moveRetryBaseSeconds]);
  const commitChannel = () => {
    const next = channel.trim();
    if (next === (current.slackChannel ?? "")) return;
    onChange(clean({ ...current, slackChannel: next || void 0 }));
  };
  const commitMiles = (key, raw, setRaw, zeroAllowed) => {
    const trimmed = raw.trim();
    const parsed = trimmed === "" ? void 0 : Number(trimmed);
    const valid = parsed === void 0 || Number.isFinite(parsed) && (zeroAllowed ? parsed >= 0 : parsed > 0);
    if (!valid) {
      setRaw(current[key]?.toString() ?? "");
      return;
    }
    if (parsed === current[key]) return;
    onChange(clean({ ...current, [key]: parsed }));
  };
  return /* @__PURE__ */ jsxs47(Stack5, { spacing: 1.5, children: [
    /* @__PURE__ */ jsx52(
      TextField33,
      {
        value: channel,
        onChange: (e) => setChannel(e.target.value),
        onBlur: commitChannel,
        disabled,
        size: "small",
        label: "Slack channel",
        placeholder: "sixt-dispatch",
        helperText: "Channel HopBot posts to, without the leading #. HopBot must be invited. Blank uses the SLACK_CHANNEL default.",
        sx: { width: 280 }
      }
    ),
    /* @__PURE__ */ jsx52(
      TextField33,
      {
        value: maxMiles,
        onChange: (e) => setMaxMiles(e.target.value),
        onBlur: () => commitMiles("customerMatchMaxMiles", maxMiles, setMaxMiles, false),
        disabled,
        size: "small",
        type: "number",
        label: "Customer match max miles",
        placeholder: "100",
        helperText: "Farthest a Sixt customer can be from the order's pickup (straight-line) and still get the moves. Blank uses 100.",
        sx: { width: 280 }
      }
    ),
    /* @__PURE__ */ jsx52(
      TextField33,
      {
        value: tieMiles,
        onChange: (e) => setTieMiles(e.target.value),
        onBlur: () => commitMiles("customerMatchTieMiles", tieMiles, setTieMiles, true),
        disabled,
        size: "small",
        type: "number",
        label: "Customer match tie margin",
        placeholder: "5",
        helperText: "Two nearest customers within this many miles of each other block the job as ambiguous. Blank uses 5.",
        sx: { width: 280 }
      }
    ),
    /* @__PURE__ */ jsx52(
      TextField33,
      {
        value: stationMiles,
        onChange: (e) => setStationMiles(e.target.value),
        onBlur: () => commitMiles("locationMatchMaxMiles", stationMiles, setStationMiles, false),
        disabled,
        size: "small",
        type: "number",
        label: "Station match max miles",
        placeholder: "3",
        helperText: "Farthest a seeded location can be from a geocoded station and still be it. Raise it for large sites (airport rental centres, auction lots) whose address geocodes off the yard. Blank uses 3.",
        sx: { width: 280 }
      }
    ),
    /* @__PURE__ */ jsx52(
      TextField33,
      {
        value: maxAttempts,
        onChange: (e) => setMaxAttempts(e.target.value),
        onBlur: () => commitMiles("moveRetryMaxAttempts", maxAttempts, setMaxAttempts, false),
        disabled,
        size: "small",
        type: "number",
        label: "Move retry max attempts",
        placeholder: "5",
        helperText: "Watchdog retries creating a move this many times before parking the vehicle for a human. Blank uses 5.",
        sx: { width: 280 }
      }
    ),
    /* @__PURE__ */ jsx52(
      TextField33,
      {
        value: baseSeconds,
        onChange: (e) => setBaseSeconds(e.target.value),
        onBlur: () => commitMiles("moveRetryBaseSeconds", baseSeconds, setBaseSeconds, false),
        disabled,
        size: "small",
        type: "number",
        label: "Move retry backoff base (sec)",
        placeholder: "60",
        helperText: "Exponential backoff base: the wait after attempt N is base \xD7 2^(N-1). Blank uses 60.",
        sx: { width: 280 }
      }
    ),
    /* @__PURE__ */ jsxs47(FormControl4, { size: "small", sx: { width: 280 }, disabled, children: [
      /* @__PURE__ */ jsx52(InputLabel4, { id: "sixt-move-creation-mode", children: "Move creation" }),
      /* @__PURE__ */ jsxs47(
        Select5,
        {
          labelId: "sixt-move-creation-mode",
          label: "Move creation",
          value: effectiveMode(current),
          onChange: (e) => onChange(clean({ ...current, moveCreationMode: e.target.value, moveCreationEnabled: void 0 })),
          children: [
            /* @__PURE__ */ jsx52(MenuItem17, { value: "approval", children: "Approve in Slack first (default)" }),
            /* @__PURE__ */ jsx52(MenuItem17, { value: "auto", children: "Create moves automatically" }),
            /* @__PURE__ */ jsx52(MenuItem17, { value: "off", children: "Off \u2014 dispatch creates moves by hand" })
          ]
        }
      ),
      /* @__PURE__ */ jsxs47(FormHelperText, { children: [
        MODE_HELP[effectiveMode(current)],
        !current.moveCreationMode && current.moveCreationEnabled !== void 0 ? " (Shown from the legacy switch; choosing a value here replaces it.)" : ""
      ] })
    ] }),
    /* @__PURE__ */ jsxs47(FormControl4, { size: "small", sx: { width: 280 }, disabled, children: [
      /* @__PURE__ */ jsx52(InputLabel4, { id: "sixt-portal-writes", children: "Sixt portal writes" }),
      /* @__PURE__ */ jsxs47(
        Select5,
        {
          labelId: "sixt-portal-writes",
          label: "Sixt portal writes",
          value: current.portalWritesEnabled === true ? "on" : "off",
          onChange: (e) => onChange(clean({ ...current, portalWritesEnabled: e.target.value === "on" })),
          children: [
            /* @__PURE__ */ jsx52(MenuItem17, { value: "off", children: "Off \u2014 record only (default)" }),
            /* @__PURE__ */ jsx52(MenuItem17, { value: "on", children: "On \u2014 Approve / Reject act in the Sixt portal" })
          ]
        }
      ),
      /* @__PURE__ */ jsx52(FormHelperText, { children: current.portalWritesEnabled === true ? "Approve accepts and Reject declines the contract in the Sixt portal, but only on a site that also has SIXT_PORTAL_WRITES=on. Otherwise the click is recorded and nothing is sent." : "Approve and Reject stay inside HopDrive. The thread records what would have been sent to the Sixt portal; nothing is sent." })
    ] })
  ] });
}

// src/definitions/move-workflow-overrides.renderer.tsx
import { useEffect as useEffect27, useMemo as useMemo5, useState as useState36 } from "react";
import { Alert as Alert16, Autocomplete as Autocomplete3, Box as Box36, CircularProgress as CircularProgress11, TextField as TextField34, Typography as Typography42 } from "@mui/material";
import { Fragment as Fragment9, jsx as jsx53, jsxs as jsxs48 } from "react/jsx-runtime";
var ROLES = [
  {
    key: "operational",
    label: "Operational",
    description: "Moves without a valet customer or loaner vehicle."
  },
  {
    key: "valet_customer",
    label: "Valet customer",
    description: "The customer's vehicle in a valet move."
  },
  {
    key: "valet_loaner",
    label: "Valet loaner",
    description: "The loaner vehicle in a valet move."
  }
];
function normalize2(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value;
}
function workflowType(option) {
  return option.metadata?.type;
}
function MoveWorkflowOverridesRenderer({ value, onChange, disabled }) {
  const { entity, dataSources } = useHostServices();
  const { options, loading, error } = dataSources.workflows(entity);
  const [draft, setDraft] = useState36(() => normalize2(value));
  useEffect27(() => {
    setDraft(normalize2(value));
  }, [value]);
  const workflows = useMemo5(
    () => ({
      pickup: options.filter((option) => workflowType(option) === "pickup"),
      delivery: options.filter((option) => workflowType(option) === "delivery")
    }),
    [options]
  );
  const updateRole = (role, type, selected) => {
    const current = draft[role] ?? {};
    if (!selected) {
      const next2 = { ...draft };
      delete next2[role];
      setDraft(next2);
      onChange(next2);
      return;
    }
    const nextPair = {
      ...current,
      [`${type}_workflow_id`]: Number(selected.id)
    };
    const next = { ...draft, [role]: nextPair };
    setDraft(next);
    if (nextPair.pickup_workflow_id && nextPair.delivery_workflow_id) {
      onChange(next);
    }
  };
  const renderPicker = (role, type) => {
    const selectedId = draft[role]?.[`${type}_workflow_id`];
    const items = workflows[type];
    const selected = items.find((item) => Number(item.id) === selectedId) ?? null;
    return /* @__PURE__ */ jsx53(
      Autocomplete3,
      {
        options: items,
        value: selected,
        onChange: (_event, option) => updateRole(role, type, option),
        getOptionLabel: (option) => `${option.label} (ID: ${option.id})`,
        isOptionEqualToValue: (option, current) => option.id === current.id,
        disabled: disabled || loading,
        renderInput: (params) => /* @__PURE__ */ jsx53(
          TextField34,
          {
            ...params,
            label: `${type === "pickup" ? "Pickup" : "Delivery"} workflow`,
            size: "small",
            InputProps: {
              ...params.InputProps,
              endAdornment: /* @__PURE__ */ jsxs48(Fragment9, { children: [
                loading ? /* @__PURE__ */ jsx53(CircularProgress11, { color: "inherit", size: 18 }) : null,
                params.InputProps.endAdornment
              ] })
            }
          }
        )
      }
    );
  };
  return /* @__PURE__ */ jsxs48(Box36, { sx: { display: "grid", gap: 2 }, children: [
    error ? /* @__PURE__ */ jsx53(Alert16, { severity: "error", children: "Unable to load workflows." }) : null,
    ROLES.map((role) => {
      const pair = draft[role.key];
      const incomplete = Boolean(pair?.pickup_workflow_id) !== Boolean(pair?.delivery_workflow_id);
      return /* @__PURE__ */ jsxs48(
        Box36,
        {
          sx: { display: "grid", gap: 1, p: 2, border: 1, borderColor: "divider", borderRadius: 1 },
          children: [
            /* @__PURE__ */ jsxs48(Box36, { children: [
              /* @__PURE__ */ jsx53(Typography42, { variant: "subtitle2", children: role.label }),
              /* @__PURE__ */ jsx53(Typography42, { variant: "body2", color: "text.secondary", children: role.description })
            ] }),
            /* @__PURE__ */ jsxs48(Box36, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }, children: [
              renderPicker(role.key, "pickup"),
              renderPicker(role.key, "delivery")
            ] }),
            incomplete ? /* @__PURE__ */ jsx53(Alert16, { severity: "info", children: "Select both workflows to save this override, or clear either selection." }) : null
          ]
        },
        role.key
      );
    }),
    /* @__PURE__ */ jsx53(Typography42, { variant: "body2", color: "text.secondary", children: "Roles without a complete override use the workflowset pickup and delivery workflows." })
  ] });
}

// src/renderers/base/BooleanRenderer.tsx
import { StyledSwitch as StyledSwitch17 } from "@hopdrive/sdk-ui";
import { jsx as jsx54 } from "react/jsx-runtime";
function BooleanRenderer({ value, onChange, disabled }) {
  return /* @__PURE__ */ jsx54(
    StyledSwitch17,
    {
      checked: Boolean(value),
      onChange: (e) => onChange(e.target.checked),
      disabled
    }
  );
}

// src/renderers/base/StringRenderer.tsx
import { TextField as TextField35 } from "@mui/material";
import { useState as useState37, useEffect as useEffect28 } from "react";
import { jsx as jsx55 } from "react/jsx-runtime";
function StringRenderer({ value, onChange, disabled }) {
  const [localValue, setLocalValue] = useState37(value || "");
  useEffect28(() => {
    setLocalValue(value || "");
  }, [value]);
  const handleBlur = () => {
    if (localValue !== (value || "")) {
      onChange(localValue);
    }
  };
  return /* @__PURE__ */ jsx55(
    TextField35,
    {
      value: localValue,
      onChange: (e) => setLocalValue(e.target.value),
      onBlur: handleBlur,
      disabled,
      placeholder: "Enter value...",
      size: "small",
      sx: { width: 300 }
    }
  );
}

// src/renderers/base/NumberRenderer.tsx
import { TextField as TextField36, InputAdornment as InputAdornment13 } from "@mui/material";
import { useState as useState38, useEffect as useEffect29 } from "react";
import { jsx as jsx56 } from "react/jsx-runtime";
function NumberRenderer({ value, onChange, disabled, definition: definition2 }) {
  const unit = definition2?.unit || "";
  const [localValue, setLocalValue] = useState38(value ?? "");
  useEffect29(() => {
    setLocalValue(value ?? "");
  }, [value]);
  const handleBlur = () => {
    const parsedValue = localValue === "" ? void 0 : Number(localValue);
    if (parsedValue !== value) {
      onChange(parsedValue);
    }
  };
  return /* @__PURE__ */ jsx56(
    TextField36,
    {
      type: "number",
      value: localValue,
      onChange: (e) => setLocalValue(e.target.value),
      onBlur: handleBlur,
      disabled,
      size: "small",
      sx: { width: 300 },
      InputProps: {
        startAdornment: unit === "$" ? /* @__PURE__ */ jsx56(InputAdornment13, { position: "start", children: "$" }) : void 0,
        endAdornment: unit && unit !== "$" ? /* @__PURE__ */ jsx56(InputAdornment13, { position: "end", children: unit }) : void 0
      }
    }
  );
}

// src/renderers/base/JsonRenderer.tsx
import { TextField as TextField37 } from "@mui/material";
import { useState as useState39, useEffect as useEffect30 } from "react";
import { jsx as jsx57 } from "react/jsx-runtime";
function JsonRenderer({ value, onChange, disabled }) {
  const [jsonString, setJsonString] = useState39(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState39(null);
  useEffect30(() => {
    setJsonString(JSON.stringify(value, null, 2));
    setError(null);
  }, [value]);
  const handleChange = (newValue) => {
    setJsonString(newValue);
    try {
      JSON.parse(newValue);
      setError(null);
    } catch {
      setError("Invalid JSON");
    }
  };
  const handleBlur = () => {
    try {
      const parsed = JSON.parse(jsonString);
      setError(null);
      if (JSON.stringify(parsed) !== JSON.stringify(value)) {
        onChange(parsed);
      }
    } catch {
      setError("Invalid JSON - changes not saved");
    }
  };
  return /* @__PURE__ */ jsx57(
    TextField37,
    {
      multiline: true,
      rows: 6,
      value: jsonString,
      onChange: (e) => handleChange(e.target.value),
      onBlur: handleBlur,
      disabled,
      fullWidth: true,
      error: !!error,
      helperText: error,
      placeholder: "Enter JSON...",
      sx: {
        fontFamily: "monospace",
        fontSize: "12px",
        "& textarea": {
          fontFamily: "monospace",
          fontSize: "12px"
        }
      }
    }
  );
}

// src/renderers/base/EnumRenderer.tsx
import { Select as Select6, MenuItem as MenuItem18, FormControl as FormControl5, CircularProgress as CircularProgress12, ListItemText as ListItemText5, Box as Box37 } from "@mui/material";

// src/renderers/schema.ts
function getSchemaType(definition2) {
  const schema2 = definition2.schema;
  if (!schema2 || !schema2._def) {
    return "unknown";
  }
  const typeName = schema2._def.typeName;
  if (typeName === "ZodBranded") {
    const innerDef = schema2._def.type?._def;
    if (innerDef?.typeName === "ZodEffects" && innerDef.schema?._def?.typeName === "ZodString") {
      const brand = schema2._def.brand;
      const brandString = typeof brand === "symbol" ? brand.toString() : String(brand);
      if (brandString.includes("EncryptedString")) {
        return "encryptedString";
      }
    }
  }
  switch (typeName) {
    case "ZodBoolean":
      return "boolean";
    case "ZodString":
      return "string";
    case "ZodNumber":
      return "number";
    case "ZodEnum":
    case "ZodNativeEnum":
      return "enum";
    case "ZodArray":
      return "array";
    case "ZodObject":
      return "object";
    case "ZodOptional":
    case "ZodNullable":
      if (schema2._def.innerType) {
        return getSchemaType({ ...definition2, schema: schema2._def.innerType });
      }
      return "unknown";
    case "ZodDefault":
      if (schema2._def.innerType) {
        return getSchemaType({ ...definition2, schema: schema2._def.innerType });
      }
      return "unknown";
    default:
      return "unknown";
  }
}
function isSimpleObjectSchema(definition2) {
  const schema2 = definition2.schema;
  if (!schema2 || schema2._def?.typeName !== "ZodObject") {
    return false;
  }
  const shape = schema2._def.shape?.();
  if (!shape) return false;
  const primitiveTypes = ["ZodBoolean", "ZodString", "ZodNumber", "ZodEnum"];
  return Object.values(shape).every((fieldSchema) => {
    const typeName = fieldSchema._def?.typeName;
    if (primitiveTypes.includes(typeName)) {
      return true;
    }
    if (typeName === "ZodOptional" || typeName === "ZodNullable") {
      const innerTypeName = fieldSchema._def?.innerType?._def?.typeName;
      return primitiveTypes.includes(innerTypeName);
    }
    return false;
  });
}
function getEnumValues(definition2) {
  const schema2 = definition2.schema;
  if (!schema2 || !schema2._def) {
    return [];
  }
  const typeName = schema2._def.typeName;
  if (typeName === "ZodEnum") {
    return schema2._def.values || [];
  }
  if (typeName === "ZodOptional" || typeName === "ZodNullable") {
    return getEnumValues({ ...definition2, schema: schema2._def.innerType });
  }
  return [];
}
var STORAGE_TYPES = {
  JSONB: "jsonb",
  LINK_TABLE: "link_table",
  DEDICATED_COLUMN: "dedicated_column"
};
function getStorageType(definition2, level) {
  const levelConfig = definition2.storage?.levels?.[level];
  return levelConfig?.type || STORAGE_TYPES.JSONB;
}
function hasDbEnumSource(definition2) {
  return !!definition2.storage?.enum;
}
function getLevelStorageConfig(definition2, level) {
  return definition2.storage?.levels?.[level];
}
function usesLinkTableStorage(definition2) {
  if (!definition2.storage?.levels) {
    return false;
  }
  return Object.values(definition2.storage.levels).some(
    (config) => config?.type === STORAGE_TYPES.LINK_TABLE
  );
}
function getLinkTableLevels(definition2) {
  if (!definition2.storage?.levels) {
    return [];
  }
  return Object.entries(definition2.storage.levels).filter(([, config]) => config?.type === STORAGE_TYPES.LINK_TABLE).map(([level]) => level);
}

// src/renderers/base/EnumRenderer.tsx
import { jsx as jsx58, jsxs as jsxs49 } from "react/jsx-runtime";
function EnumRenderer({
  value,
  onChange,
  disabled,
  definition: definition2,
  enumOptions,
  enumLoading
}) {
  const hasDbOptions = enumOptions && enumOptions.length > 0;
  const schemaOptions = getEnumValues(definition2);
  if (enumLoading) {
    return /* @__PURE__ */ jsxs49(Box37, { sx: { display: "flex", alignItems: "center", gap: 1, minHeight: 40 }, children: [
      /* @__PURE__ */ jsx58(CircularProgress12, { size: 20 }),
      /* @__PURE__ */ jsx58("span", { children: "Loading options..." })
    ] });
  }
  if (hasDbOptions) {
    return /* @__PURE__ */ jsx58(FormControl5, { size: "small", sx: { width: 300 }, children: /* @__PURE__ */ jsx58(Select6, { value: value ?? "", onChange: (e) => onChange(e.target.value), disabled, children: enumOptions.map((option) => /* @__PURE__ */ jsx58(MenuItem18, { value: option.value, children: option.description ? /* @__PURE__ */ jsx58(
      ListItemText5,
      {
        primary: option.label,
        secondary: option.description,
        secondaryTypographyProps: { variant: "caption" }
      }
    ) : option.label }, String(option.value))) }) });
  }
  return /* @__PURE__ */ jsx58(FormControl5, { size: "small", sx: { width: 300 }, children: /* @__PURE__ */ jsx58(
    Select6,
    {
      value: value || "",
      onChange: (e) => onChange(e.target.value),
      disabled,
      children: schemaOptions.map((option) => /* @__PURE__ */ jsx58(MenuItem18, { value: option, children: option }, option))
    }
  ) });
}

// src/renderers/base/MultiSelectRenderer.tsx
import {
  Autocomplete as Autocomplete4,
  TextField as TextField38,
  Chip as Chip11,
  CircularProgress as CircularProgress13,
  Box as Box38,
  Checkbox as Checkbox5,
  Typography as Typography43
} from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import { jsx as jsx59, jsxs as jsxs50 } from "react/jsx-runtime";
var icon = /* @__PURE__ */ jsx59(CheckBoxOutlineBlank, { fontSize: "small" });
var checkedIcon = /* @__PURE__ */ jsx59(CheckBox, { fontSize: "small" });
function MultiSelectRenderer({
  value,
  onChange,
  disabled,
  enumOptions,
  enumLoading
}) {
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedOptions = enumOptions?.filter((opt) => selectedValues.includes(opt.value)) || [];
  const handleChange = (_event, newValue) => {
    onChange(newValue.map((opt) => opt.value));
  };
  if (enumLoading) {
    return /* @__PURE__ */ jsxs50(Box38, { sx: { display: "flex", alignItems: "center", gap: 1, minHeight: 40 }, children: [
      /* @__PURE__ */ jsx59(CircularProgress13, { size: 20 }),
      /* @__PURE__ */ jsx59("span", { children: "Loading options..." })
    ] });
  }
  if (!enumOptions || enumOptions.length === 0) {
    return /* @__PURE__ */ jsx59(Typography43, { variant: "body2", color: "text.secondary", children: "No options available" });
  }
  return /* @__PURE__ */ jsx59(
    Autocomplete4,
    {
      multiple: true,
      options: enumOptions,
      value: selectedOptions,
      onChange: handleChange,
      disabled,
      disableCloseOnSelect: true,
      getOptionLabel: (option) => option.label,
      isOptionEqualToValue: (option, val) => option.value === val.value,
      renderOption: (props, option, { selected }) => {
        const { key, ...restProps } = props;
        return /* @__PURE__ */ jsxs50("li", { ...restProps, children: [
          /* @__PURE__ */ jsx59(Checkbox5, { icon, checkedIcon, style: { marginRight: 8 }, checked: selected }),
          /* @__PURE__ */ jsxs50(Box38, { children: [
            /* @__PURE__ */ jsx59(Typography43, { variant: "body2", children: option.label }),
            option.description && /* @__PURE__ */ jsx59(Typography43, { variant: "caption", color: "text.secondary", children: option.description })
          ] })
        ] }, key);
      },
      renderTags: (tagValue, getTagProps) => tagValue.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return /* @__PURE__ */ jsx59(Chip11, { label: option.label, size: "small", ...tagProps }, key);
      }),
      renderInput: (params) => /* @__PURE__ */ jsx59(
        TextField38,
        {
          ...params,
          size: "small",
          placeholder: selectedOptions.length === 0 ? "Select options..." : "",
          sx: { minWidth: 300 }
        }
      ),
      sx: { minWidth: 300 }
    }
  );
}

// src/renderers/base/EncryptedStringRenderer.tsx
import { Alert as Alert17, Box as Box39 } from "@mui/material";
import { SecretField as SecretField2 } from "@hopdrive/sdk-ui";
import { jsx as jsx60, jsxs as jsxs51 } from "react/jsx-runtime";
function EncryptedStringRenderer({ value, onChange, disabled }) {
  const secrets = useOptionalHostServices()?.secrets;
  const stringValue = value || "";
  const isEncrypted = secrets ? secrets.isEncrypted(stringValue) : false;
  return /* @__PURE__ */ jsxs51(Box39, { children: [
    !secrets && /* @__PURE__ */ jsx60(Alert17, { severity: "warning", sx: { mb: 1 }, children: "Secret encryption is unavailable; values are stored as plain text." }),
    /* @__PURE__ */ jsx60(
      SecretField2,
      {
        value: stringValue,
        isEncrypted,
        maskValue: secrets?.mask,
        onReveal: secrets && isEncrypted ? () => secrets.decrypt(stringValue) : void 0,
        onCommit: async (plaintext) => {
          if (!plaintext.trim()) {
            onChange("");
            return;
          }
          if (!secrets) {
            onChange(plaintext);
            return;
          }
          const encrypted = await secrets.encrypt(plaintext);
          onChange(encrypted);
        },
        disabled
      }
    )
  ] });
}

// src/renderers/RendererRouter.tsx
import { jsx as jsx61 } from "react/jsx-runtime";
function RendererRouter(props) {
  const { settingKey, definition: definition2 } = props;
  const services = useOptionalHostServices();
  if (hasCustomRenderer(settingKey)) {
    const requires = getRendererRequires(settingKey);
    if (hasRequiredPorts(services, requires)) {
      const CustomRenderer = getCustomRenderer(settingKey);
      return /* @__PURE__ */ jsx61(CustomRenderer, { ...props });
    }
  }
  const schemaType = getSchemaType(definition2);
  const hasDbEnum = hasDbEnumSource(definition2 ?? {});
  switch (schemaType) {
    case "boolean":
      return /* @__PURE__ */ jsx61(BooleanRenderer, { ...props });
    case "string":
      return /* @__PURE__ */ jsx61(StringRenderer, { ...props });
    case "encryptedString":
      return /* @__PURE__ */ jsx61(EncryptedStringRenderer, { ...props });
    case "number":
      return /* @__PURE__ */ jsx61(NumberRenderer, { ...props });
    case "enum":
      return /* @__PURE__ */ jsx61(EnumRenderer, { ...props });
    case "array":
      if (hasDbEnum) {
        return /* @__PURE__ */ jsx61(MultiSelectRenderer, { ...props });
      }
      return /* @__PURE__ */ jsx61(JsonRenderer, { ...props });
    case "object":
    case "unknown":
    default:
      return /* @__PURE__ */ jsx61(JsonRenderer, { ...props });
  }
}

// src/renderers/index.ts
var WORKFLOW_ID_ENTRY = {
  component: WorkflowIdRenderer,
  mode: "input",
  layout: "stacked",
  requires: ["dataSources.workflows", "dataSources.workflowSets"]
};
var MOBILITY_ENTRY = {
  component: MobilityRenderer,
  mode: "input",
  layout: "inline"
};
var configRenderers = {
  // --- Full-mode object renderers ---
  reporting: { component: ReportingRenderer, mode: "full" },
  sixt_settings: { component: SixtSettingsRenderer, mode: "input", layout: "stacked" },
  allowed_move_types: { component: AllowedMoveTypesRenderer, mode: "full" },
  dms: { component: DmsRenderer, mode: "full" },
  customer_values: { component: CustomerValuesRenderer, mode: "full", subFields: ["Customer Values"] },
  private: { component: PrivateRenderer, mode: "full" },
  rc_config: { component: RcConfigRenderer, mode: "full" },
  fuel: {
    component: FuelRenderer,
    mode: "full",
    subFields: ["Minimum Fuel Spend", "Maximum Fuel Spend", "Required Fuel Level"]
  },
  appraisals: {
    component: AppraisalsRenderer,
    mode: "full",
    subFields: ["Appraisals Feature", "Active Timeframes", "Allowed Move Types"]
  },
  driver_pipeline: {
    component: DriverPipelineRenderer,
    mode: "full",
    subFields: [
      "Recruitment Funnel",
      "Required Moves",
      "Required Score",
      "Required Experience",
      "Region Radius",
      "Max Candidates"
    ]
  },
  fraud_audit: {
    component: FraudAuditRenderer,
    mode: "full",
    subFields: ["Fraud Audit Monitoring", "Time Tolerance", "Distance Tolerance"]
  },
  routing: {
    component: RoutingRenderer,
    mode: "full",
    subFields: ["Route Optimization", "Avoid Toll Roads", "Avoid Highways", "Avoid Ferries", "No Toll Charges"]
  },
  // #1411 split the Guest Rides settings in two. `guestrides` is the dealer-facing half.
  guestrides: {
    component: GuestRidesRenderer,
    mode: "full",
    subFields: ["Allow Guest Portal Rideshare", "Max Trip Miles", "Require Manager Approval", "Approver", "Notify Approver By"]
  },
  guestrides_access: {
    component: GuestRidesAccessRenderer,
    mode: "full",
    subFields: ["Guest Rides Enabled", "Marketable", "Credit Limit", "Access Algorithm"]
  },
  superdispatch_subscription: {
    component: SuperdispatchSubscriptionRenderer,
    mode: "full",
    subFields: ["Report This Customer's Moves", "Webhook Subscription"]
  },
  superdispatch_routing: {
    component: SuperdispatchRoutingRenderer,
    mode: "full",
    subFields: [
      "Super Dispatch Enabled",
      "Order Number Prefix",
      "Auto-Accept Offers",
      "Accept Placeholder-Vehicle Offers"
    ]
  },
  carsarrive_connection: {
    component: CarsarriveConnectionRenderer,
    mode: "full",
    // The API-key step is not a control, but it IS the thing someone comes to
    // this card for, so it earns a sidebar entry alongside the panel.
    subFields: ["API Key Access", "Webhook Subscription"]
  },
  carsarrive_routing: {
    component: CarsarriveRoutingRenderer,
    mode: "full",
    subFields: ["CarsArrive Enabled"]
  },
  carsarrive_settings: {
    component: CarsarriveSettingsRenderer,
    mode: "full",
    // Must match the controls the renderer actually draws — these become
    // sidebar entries, so a stale label shows up as a phantom setting.
    subFields: ["Send Inspection Photos", "Cancel Behaviour", "Charge Mapping", "Slack Channel"]
  },
  superdispatch_settings: {
    component: SuperdispatchSettingsRenderer,
    mode: "full",
    // Must match the controls the renderer actually draws — these become
    // sidebar entries, so a stale label shows up as a phantom setting.
    subFields: [
      "Send Inspection Photos",
      "Send Damage Photos",
      "AR Behaviour",
      "Accessorial Charges Synced",
      "Slack Channel"
    ]
  },
  email_scrape_config: { component: EmailScrapeConfigRenderer, mode: "full" },
  product_history: {
    component: ProductHistoryRenderer,
    mode: "full",
    subFields: ["History Retention Window"]
  },
  branding: {
    component: BrandingRenderer,
    mode: "full",
    requires: ["uploadAsset"],
    subFields: ["Activation", "Display Name", "Primary Color", "Secondary Color", "Backdrop Color", "Logo"]
  },
  hopdrive_contacts: {
    component: HopdriveContactsRenderer,
    mode: "full",
    requires: ["dataSources.admins"],
    subFields: ["Account Manager", "Market Manager", "Sales Representative", "AR Representative"]
  },
  service_radius: {
    component: ServiceRadiusRenderer,
    mode: "full",
    requires: ["dataSources.entityLocation"],
    subFields: ["HopDrive Service Radius", "Pickup Verification", "Delivery Verification", "Customer Service Radius"]
  },
  appointment_management: {
    component: AppointmentManagementRenderer,
    mode: "full",
    requires: ["dataSources.apptAlgorithms"],
    subFields: [
      "Appointment Management Enabled",
      "Full Planning Enabled",
      "Appointment Management Strategy Algorithm",
      "Driver Count",
      "Move Buffer",
      "Enforcement Mode",
      "Offset Max",
      "Offset Increment"
    ]
  },
  api_config: { component: ApiConfigRenderer, mode: "full", requires: ["secrets"] },
  connector_triggers: { component: ConnectorTriggersRenderer, mode: "full", requires: ["secrets"] },
  move_workflow_overrides: {
    component: MoveWorkflowOverridesRenderer,
    mode: "full",
    requires: ["dataSources.workflows"],
    subFields: ["Operational", "Valet customer", "Valet loaner"]
  },
  // --- Input-mode renderers ---
  dry_run_behavior: { component: DryRunBehaviorRenderer, mode: "input" },
  // Both arrival policy keys share one schema (arrival-policy-shared.ts), so one renderer.
  arrival_policy_arrive: { component: ArrivalPolicyRenderer, mode: "input", layout: "stacked" },
  arrival_policy_depart: { component: ArrivalPolicyRenderer, mode: "input", layout: "stacked" },
  ride_provider_algorithm_key: { component: RideProviderAlgorithmRenderer, mode: "input", layout: "stacked" },
  ride_type_override: { component: PreferredRidePartnerRenderer, mode: "input", layout: "stacked" },
  default_payer: { component: PayerRenderer, mode: "input", requires: ["dataSources.payers"] },
  scheduler: {
    component: SchedulerRenderer,
    mode: "input",
    layout: "stacked",
    requires: ["dataSources.schedulerValues"]
  },
  // WorkflowIdRenderer — shared across workflow-related keys.
  default_workflow_set_id: WORKFLOW_ID_ENTRY,
  pickup_workflow_id: WORKFLOW_ID_ENTRY,
  delivery_workflow_id: WORKFLOW_ID_ENTRY,
  default_pickup_workflow_id: WORKFLOW_ID_ENTRY,
  default_delivery_workflow_id: { ...WORKFLOW_ID_ENTRY, layout: void 0 },
  // MobilityRenderer — all mobility products share the `{ active?: boolean }` shape.
  mobile_service: MOBILITY_ENTRY,
  mobile_service_claims: MOBILITY_ENTRY,
  parts_delivery: MOBILITY_ENTRY,
  shuttle: MOBILITY_ENTRY
};
function getConfigRenderer(key) {
  return configRenderers[key] ?? null;
}
for (const [key, entry] of Object.entries(configRenderers)) {
  registerCustomRenderer(key, entry.component, entry.mode ?? "full", entry.layout, entry.requires);
  if (entry.subFields?.length) {
    registerSubFields(key, entry.subFields);
  }
}
export {
  BooleanRenderer,
  EncryptedStringRenderer,
  EnumRenderer,
  HostServicesProvider,
  JsonRenderer,
  MultiSelectRenderer,
  NumberRenderer,
  RendererRouter,
  STORAGE_TYPES,
  StringRenderer,
  configRenderers,
  getConfigRenderer,
  getCustomRenderer,
  getEnumValues,
  getLevelStorageConfig,
  getLinkTableLevels,
  getRendererLayout,
  getRendererMode,
  getRendererRequires,
  getSchemaType,
  getStorageType,
  getSubFields,
  hasCustomRenderer,
  hasDbEnumSource,
  hasRequiredPorts,
  hasSubFields,
  isFullRenderer,
  isInputRenderer,
  isSimpleObjectSchema,
  registerCustomRenderer,
  registerSubFields,
  useHostServices,
  useOptionalHostServices,
  usesLinkTableStorage
};
//# sourceMappingURL=index.mjs.map